import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Download } from "lucide-react";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { listReports, getInsightSeries } from "../lib/api";
import { HOME } from "../constants/testIds";
import InquiryForm from "../components/InquiryForm";
import TwoDoorsExperience from "../components/TwoDoorsExperience";
import TwoDoorsCollapsed from "../components/TwoDoorsCollapsed";
import useTravelAssessment from "../hooks/useTravelAssessment";

/* ═══════════════════════════════════════════════════════════════════════════
   THE REACH—homepage expression of "Efficient Access from East Texas."
   Every distance and flight time is calculated live from real coordinates
   (Haversine great-circle formula + a documented planning model), not
   hardcoded per destination—see FLIGHT_PLANNING_MODEL below. Featured
   destinations are a curated shortcut list; the searchable directory
   (public/data/us-airports.json, ~820 continental-US commercial/business
   airports, public-domain OurAirports data) covers the rest of the country.
═══════════════════════════════════════════════════════════════════════════ */

/* East Texas origin—centralized, single source of truth for every
   calculation on this map.
   FLAGGED DECISION: the previous implementation used a bare coordinate
   (32.6626, -95.4894) labeled only "Mineola, TX"—the company's mailing
   address, not a named airport. That's not a real operating origin for
   flight-planning math. Per Steck, the operating origin is Wood County
   Airport (KJDD/JDD) — the real field in the Mineola/Quitman area where
   Steck is based — so all distance/time math is centralized here. (Note: an
   older aircraft-profile PDF names Sulphur Springs Municipal as the base;
   Steck has confirmed Wood County is correct.) The public-facing label stays
   "East Texas" throughout the interface; only this one object needs to change
   if that identification turns out to be incorrect. */
const EAST_TEXAS_ORIGIN = {
  name: "Wood County Airport",
  city: "Mineola, TX",
  iata: "JDD",
  icao: "KJDD",
  lat: 32.9447,
  lon: -95.4983,
};

/* Featured destinations—a curated, quick-access shortcut list layered on
   top of the full searchable directory, not the limit of what's
   selectable. Real airport coordinates matching the searchable dataset
   (not city centroids), so a featured pick and a searched pick behave
   identically in every calculation. */
const FEATURED_DESTINATIONS = [
  { city: "Dallas",      code: "DAL", name: "Dallas Love Field",                        state: "TX", lat: 32.8448, lon: -96.8477 },
  { city: "Houston",     code: "HOU", name: "William P. Hobby Airport",                  state: "TX", lat: 29.6453, lon: -95.2768 },
  { city: "New Orleans", code: "MSY", name: "Louis Armstrong New Orleans International", state: "LA", lat: 29.9934, lon: -90.2647 },
  { city: "Nashville",   code: "BNA", name: "Nashville International Airport",           state: "TN", lat: 36.1245, lon: -86.6782 },
  { city: "Atlanta",     code: "ATL", name: "Hartsfield-Jackson Atlanta International",  state: "GA", lat: 33.6367, lon: -84.4281 },
  { city: "Chicago",     code: "ORD", name: "Chicago O'Hare International Airport",      state: "IL", lat: 41.9786, lon: -87.9048 },
  { city: "Denver",      code: "DEN", name: "Denver International Airport",              state: "CO", lat: 39.8600, lon: -104.6738 },
  { city: "Miami",       code: "MIA", name: "Miami International Airport",                state: "FL", lat: 25.7960, lon: -80.2898 },
  { city: "New York",    code: "JFK", name: "John F. Kennedy International Airport",      state: "NY", lat: 40.6394, lon: -73.7793 },
];

/* Mexico + Caribbean getaway airports. Selectable + searchable exactly like
   the U.S. hubs, but most sit south of the map's own frame (the night-lights
   photo is continental-U.S.), so the marker/route are clamped to the frame
   edge in the true direction (see clampedActivePos) while the panel's
   distance/time are computed from the real coordinates. `iata` mirrors `code`
   so these merge cleanly into the airport search directory. */
const GETAWAY_DESTINATIONS = [
  // Mexico
  { city: "Cancún",             code: "CUN", iata: "CUN", name: "Cancún International Airport",              state: "Mexico", lat: 21.0365, lon: -86.8771, priority: "major" },
  { city: "Los Cabos",          code: "SJD", iata: "SJD", name: "Los Cabos International Airport",           state: "Mexico", lat: 23.1518, lon: -109.7215, priority: "major" },
  { city: "Puerto Vallarta",    code: "PVR", iata: "PVR", name: "Puerto Vallarta International Airport",     state: "Mexico", lat: 20.6801, lon: -105.2544, priority: "major" },
  { city: "Cozumel",            code: "CZM", iata: "CZM", name: "Cozumel International Airport",             state: "Mexico", lat: 20.5224, lon: -86.9256 },
  { city: "Tulum",              code: "TQO", iata: "TQO", name: "Tulum Felipe Carrillo Puerto Airport",     state: "Mexico", lat: 20.4553, lon: -87.5717 },
  { city: "Mazatlán",           code: "MZT", iata: "MZT", name: "Mazatlán International Airport",            state: "Mexico", lat: 23.1614, lon: -106.2660 },
  { city: "Ixtapa-Zihuatanejo", code: "ZIH", iata: "ZIH", name: "Ixtapa-Zihuatanejo International Airport", state: "Mexico", lat: 17.6016, lon: -101.4606 },
  { city: "Loreto",             code: "LTO", iata: "LTO", name: "Loreto International Airport",              state: "Mexico", lat: 25.9892, lon: -111.3484 },
  { city: "La Paz",             code: "LAP", iata: "LAP", name: "La Paz International Airport",              state: "Mexico", lat: 24.0727, lon: -110.3625 },
  { city: "Mérida",             code: "MID", iata: "MID", name: "Mérida International Airport",              state: "Mexico", lat: 20.9370, lon: -89.6577 },
  { city: "Acapulco",           code: "ACA", iata: "ACA", name: "Acapulco International Airport",            state: "Mexico", lat: 16.7571, lon: -99.7539 },
  { city: "Huatulco",           code: "HUX", iata: "HUX", name: "Bahías de Huatulco International Airport",  state: "Mexico", lat: 15.7753, lon: -96.2626 },
  { city: "Manzanillo",         code: "ZLO", iata: "ZLO", name: "Playa de Oro International Airport",        state: "Mexico", lat: 19.1448, lon: -104.5586 },
  { city: "Guadalajara",        code: "GDL", iata: "GDL", name: "Guadalajara International Airport",         state: "Mexico", lat: 20.5218, lon: -103.3111 },
  { city: "Mexico City",        code: "MEX", iata: "MEX", name: "Mexico City International Airport",         state: "Mexico", lat: 19.4363, lon: -99.0721 },
  // Caribbean & Bahamas
  { city: "Nassau",             code: "NAS", iata: "NAS", name: "Lynden Pindling International Airport",     state: "Bahamas", lat: 25.0390, lon: -77.4662, priority: "major" },
  { city: "Freeport",           code: "FPO", iata: "FPO", name: "Grand Bahama International Airport",        state: "Bahamas", lat: 26.5587, lon: -78.6956 },
  { city: "Exuma",              code: "GGT", iata: "GGT", name: "Exuma International Airport",               state: "Bahamas", lat: 23.5626, lon: -75.8779 },
  { city: "North Eleuthera",    code: "ELH", iata: "ELH", name: "North Eleuthera Airport",                  state: "Bahamas", lat: 25.4749, lon: -76.6835 },
  { city: "Marsh Harbour",      code: "MHH", iata: "MHH", name: "Marsh Harbour International Airport",       state: "Bahamas", lat: 26.5114, lon: -77.0835 },
  { city: "Punta Cana",         code: "PUJ", iata: "PUJ", name: "Punta Cana International Airport",          state: "Dominican Republic", lat: 18.5674, lon: -68.3634, priority: "major" },
  { city: "Puerto Plata",       code: "POP", iata: "POP", name: "Gregorio Luperón International Airport",    state: "Dominican Republic", lat: 19.7579, lon: -70.5700 },
  { city: "La Romana",          code: "LRM", iata: "LRM", name: "La Romana International Airport",           state: "Dominican Republic", lat: 18.4507, lon: -68.9117 },
  { city: "Santo Domingo",      code: "SDQ", iata: "SDQ", name: "Las Américas International Airport",        state: "Dominican Republic", lat: 18.4297, lon: -69.6689 },
  { city: "Montego Bay",        code: "MBJ", iata: "MBJ", name: "Sangster International Airport",            state: "Jamaica", lat: 18.5037, lon: -77.9134, priority: "major" },
  { city: "Kingston",           code: "KIN", iata: "KIN", name: "Norman Manley International Airport",       state: "Jamaica", lat: 17.9357, lon: -76.7875 },
  { city: "Grand Cayman",       code: "GCM", iata: "GCM", name: "Owen Roberts International Airport",        state: "Cayman Islands", lat: 19.2928, lon: -81.3577 },
  { city: "San Juan",           code: "SJU", iata: "SJU", name: "Luis Muñoz Marín International Airport",    state: "Puerto Rico", lat: 18.4394, lon: -66.0018, priority: "major" },
  { city: "St. Thomas",         code: "STT", iata: "STT", name: "Cyril E. King Airport",                    state: "U.S. Virgin Islands", lat: 18.3373, lon: -64.9734 },
  { city: "Providenciales",     code: "PLS", iata: "PLS", name: "Providenciales International Airport",      state: "Turks & Caicos", lat: 21.7736, lon: -72.2659 },
  { city: "Aruba",              code: "AUA", iata: "AUA", name: "Queen Beatrix International Airport",       state: "", lat: 12.5014, lon: -70.0152 },
  { city: "Curaçao",            code: "CUR", iata: "CUR", name: "Curaçao International Airport",             state: "", lat: 12.1889, lon: -68.9598 },
  { city: "St. Maarten",        code: "SXM", iata: "SXM", name: "Princess Juliana International Airport",    state: "", lat: 18.0410, lon: -63.1089 },
  { city: "Antigua",            code: "ANU", iata: "ANU", name: "V. C. Bird International Airport",          state: "Antigua & Barbuda", lat: 17.1367, lon: -61.7927 },
  { city: "St. Kitts",          code: "SKB", iata: "SKB", name: "Robert L. Bradshaw International Airport",  state: "St. Kitts & Nevis", lat: 17.3111, lon: -62.7187 },
  { city: "Barbados",           code: "BGI", iata: "BGI", name: "Grantley Adams International Airport",      state: "", lat: 13.0746, lon: -59.4925 },
  { city: "St. Lucia",          code: "UVF", iata: "UVF", name: "Hewanorra International Airport",           state: "", lat: 13.7332, lon: -60.9526 },
  { city: "Grenada",            code: "GND", iata: "GND", name: "Maurice Bishop International Airport",      state: "", lat: 12.0042, lon: -61.7862 },
  { city: "Tortola",            code: "EIS", iata: "EIS", name: "Terrance B. Lettsome International Airport",state: "British Virgin Islands", lat: 18.4448, lon: -64.5430 },
  { city: "Bermuda",            code: "BDA", iata: "BDA", name: "L.F. Wade International Airport",           state: "", lat: 32.3640, lon: -64.6787 },
];

/* Aircraft performance—reusing the Beechjet 400A's published spec-panel
   figures from the Aircraft page (cruise speed, range). Not re-derived or
   invented here. */
const AIRCRAFT_CRUISE_KTAS = 440;
const AIRCRAFT_PUBLISHED_RANGE_NM = 1519;

/* Conservative planning threshold—deliberately NOT the published max
   range. Published range figures assume a specific payload/fuel condition
   rarely matched exactly in real trips; ~80% of published range is a
   common, defensible rule-of-thumb planning margin, not a certified
   performance number. Destinations beyond this still display full
   information—see estimateFlightPlan()—they're simply flagged as
   likely needing a fuel stop rather than hidden or misrepresented. */
const PLANNING_RANGE_NM = Math.round((AIRCRAFT_PUBLISHED_RANGE_NM * 0.8) / 10) * 10;

/* Centralized, documented flight-time planning model—the ONLY place
   distance-to-time math happens, so every destination (featured or
   searched) is estimated identically.

   Calibrated to ForeFlight's Beechjet 400A "standard, max cruise" profile
   (the enroute time ForeFlight shows, wheels-up to wheels-down): the fixed
   allowance below captures the time a short leg spends climbing and
   descending rather than cruising, and the rest is distance ÷ cruise. Anchor
   point: Wood County (KJDD, our origin) → Dallas Love ≈ 68 nm ≈ 18 min
   (DFW ≈ 78 nm ≈ 19 min), matching ForeFlight. No extra block-time padding is
   applied—these are the flight times, not gate-to-gate block times. */
const FLIGHT_PLANNING_MODEL = {
  cruiseSpeedKts: AIRCRAFT_CRUISE_KTAS,
  taxiAllowanceMin: 4,           // brief ramp/maneuvering at each end
  climbDescentAllowanceMin: 4,   // time beyond pure cruise math for the climb/descent profile
  routingBufferFactor: 1.03,     // ~3% for non-direct routing / vectoring
  fuelStopGroundMin: [30, 60],   // typical ground-time range for a fuel stop, when one is needed
};

const NM_TO_MI = 1.15078;

/* Distance in nautical miles on the WGS84 ellipsoid via the Vincenty inverse
   formula — the same geodesic model ForeFlight uses. This matches ForeFlight's
   direct distance to a fraction of a nautical mile, rather than the ~0.2–0.4%
   error a spherical great-circle (Haversine) carries. */
const geodesicNM = (lat1, lon1, lat2, lon2) => {
  const a = 6378137.0;          // WGS84 semi-major axis (m)
  const f = 1 / 298.257223563;  // WGS84 flattening
  const b = (1 - f) * a;        // semi-minor axis (m)
  const toRad = (d) => (d * Math.PI) / 180;
  const L = toRad(lon2 - lon1);
  const U1 = Math.atan((1 - f) * Math.tan(toRad(lat1)));
  const U2 = Math.atan((1 - f) * Math.tan(toRad(lat2)));
  const sinU1 = Math.sin(U1), cosU1 = Math.cos(U1);
  const sinU2 = Math.sin(U2), cosU2 = Math.cos(U2);
  let lambda = L, lambdaP = 0, iter = 0;
  let sinSigma = 0, cosSigma = 0, sigma = 0, cosSqAlpha = 0, cos2SigmaM = 0;
  do {
    const sinLambda = Math.sin(lambda), cosLambda = Math.cos(lambda);
    sinSigma = Math.sqrt(
      (cosU2 * sinLambda) ** 2 +
      (cosU1 * sinU2 - sinU1 * cosU2 * cosLambda) ** 2
    );
    if (sinSigma === 0) return 0; // coincident points
    cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosLambda;
    sigma = Math.atan2(sinSigma, cosSigma);
    const sinAlpha = (cosU1 * cosU2 * sinLambda) / sinSigma;
    cosSqAlpha = 1 - sinAlpha ** 2;
    cos2SigmaM = cosSqAlpha !== 0 ? cosSigma - (2 * sinU1 * sinU2) / cosSqAlpha : 0;
    const C = (f / 16) * cosSqAlpha * (4 + f * (4 - 3 * cosSqAlpha));
    lambdaP = lambda;
    lambda = L + (1 - C) * f * sinAlpha *
      (sigma + C * sinSigma * (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM ** 2)));
  } while (Math.abs(lambda - lambdaP) > 1e-12 && ++iter < 200);
  const uSq = (cosSqAlpha * (a * a - b * b)) / (b * b);
  const A = 1 + (uSq / 16384) * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
  const B = (uSq / 1024) * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));
  const deltaSigma = B * sinSigma * (cos2SigmaM + (B / 4) *
    (cosSigma * (-1 + 2 * cos2SigmaM ** 2) -
      (B / 6) * cos2SigmaM * (-3 + 4 * sinSigma ** 2) * (-3 + 4 * cos2SigmaM ** 2)));
  const s = b * A * (sigma - deltaSigma); // metres
  return s / 1852;                        // → nautical miles (1 NM = 1852 m)
};

/* The single, documented estimate used everywhere on this map—see
   FLIGHT_PLANNING_MODEL above for what each allowance represents. */
const estimateFlightPlan = (distanceNm) => {
  const effectiveNm = distanceNm * FLIGHT_PLANNING_MODEL.routingBufferFactor;
  const cruiseHours = effectiveNm / FLIGHT_PLANNING_MODEL.cruiseSpeedKts;
  const overheadHours = (FLIGHT_PLANNING_MODEL.taxiAllowanceMin + FLIGHT_PLANNING_MODEL.climbDescentAllowanceMin) / 60;
  const totalHours = cruiseHours + overheadHours;
  const withinRange = distanceNm <= PLANNING_RANGE_NM;
  const stopRangeHours = withinRange ? null : [
    totalHours + FLIGHT_PLANNING_MODEL.fuelStopGroundMin[0] / 60,
    totalHours + FLIGHT_PLANNING_MODEL.fuelStopGroundMin[1] / 60,
  ];
  return { distanceNm, totalHours, withinRange, stopRangeHours };
};

const formatHours = (h) => {
  if (h < 0.92) return `${Math.round(h * 60)} min`;
  return `${h.toFixed(1)} hrs`;
};

/* Spherical midpoint (slerp at t=0.5) between origin and destination—used
   to bow the route line into a gentle great-circle-inspired arc instead of
   an arbitrary straight decorative line. */
const greatCircleMidpoint = (lat1, lon1, lat2, lon2) => {
  const toRad = (d) => (d * Math.PI) / 180, toDeg = (r) => (r * 180) / Math.PI;
  const phi1 = toRad(lat1), lam1 = toRad(lon1), phi2 = toRad(lat2), lam2 = toRad(lon2);
  const Bx = Math.cos(phi2) * Math.cos(lam2 - lam1);
  const By = Math.cos(phi2) * Math.sin(lam2 - lam1);
  const phiM = Math.atan2(Math.sin(phi1) + Math.sin(phi2), Math.sqrt((Math.cos(phi1) + Bx) ** 2 + By ** 2));
  const lamM = lam1 + Math.atan2(By, Math.cos(phi1) + Bx);
  return { lat: toDeg(phiM), lon: toDeg(lamM) };
};


/* Contextual population-center lights—atmosphere, not interaction. These
   are NOT destinations: no button, no aria-label, no hover/click handling,
   no flight data. They exist purely to make the landmass read as a living,
   populated country rather than nine isolated interactive dots on an empty
   silhouette. Two (Washington and Los Angeles) also carry a small landmark
   glyph per the "selective landmark" treatment below. */
const CONTEXT_CITIES = [
  { city: "Los Angeles",   lat: 34.0522, lon: -118.2437, landmark: true  },
  { city: "Washington",    lat: 38.9072, lon: -77.0369,  landmark: true  },
  { city: "Seattle",       lat: 47.6062, lon: -122.3321, landmark: false },
  { city: "Phoenix",       lat: 33.4484, lon: -112.0740, landmark: false },
  { city: "San Antonio",   lat: 29.4241, lon: -98.4936,  landmark: false },
  { city: "Kansas City",   lat: 39.0997, lon: -94.5786,  landmark: false },
  { city: "Minneapolis",   lat: 44.9778, lon: -93.2650,  landmark: false },
  { city: "Detroit",       lat: 42.3314, lon: -83.0458,  landmark: false },
  { city: "Charlotte",     lat: 35.2271, lon: -80.8431,  landmark: false },
  { city: "Boston",        lat: 42.3601, lon: -71.0589,  landmark: false },
  { city: "Philadelphia",  lat: 39.9526, lon: -75.1652,  landmark: false },
];

/* Sparse, abstract transient points along common corridors out of East
   Texas—never labeled, never a statistic, just a dot + crosshair that
   resolves briefly then fades, like a sensor noting something in passing. */
const TRANSIENT_BLIPS = [
  { id: "tb1", lat: 35.2, lon: -92.4 },  // toward Nashville/Atlanta corridor
  { id: "tb2", lat: 37.6, lon: -97.0 },  // toward Kansas City/Chicago corridor
  { id: "tb3", lat: 33.8, lon: -101.8 }, // toward Denver corridor
];


/* Landmark glyphs for the two contextual (non-interactive) cities called
   out for "genuine recognition" treatment. Same abstract line-art language
   as CityGlyph—dimensional via gradient fill + soft glow rather than a
   literal icon—kept intentionally small and dim so they read as ambient
   geographic detail, never mistaken for a selectable destination. */
const LandmarkGlyph = ({ city }) => {
  const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.1, strokeLinecap: "round", strokeLinejoin: "round" };
  if (city === "Washington") {
    // Capitol dome—column base + dome arc + spire
    return (
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
        <path d="M6 20 L6 13 M9 20 L9 13 M12 20 L12 13 M15 20 L15 13 M18 20 L18 13" {...stroke} opacity="0.7" />
        <path d="M5 13 Q12 6 19 13 Z" fill="currentColor" opacity="0.16" stroke="currentColor" strokeWidth="1.1" />
        <line x1="12" y1="6" x2="12" y2="3" {...stroke} />
        <line x1="3" y1="20" x2="21" y2="20" {...stroke} />
      </svg>
    );
  }
  if (city === "Los Angeles") {
    // low downtown cluster beneath a broad horizon line—coastal/basin suggestion
    return (
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
        <path d="M2 20 Q12 15 22 20" {...stroke} opacity="0.6" />
        <line x1="7" y1="20" x2="7" y2="15" {...stroke} />
        <line x1="10.5" y1="20" x2="10.5" y2="11" {...stroke} />
        <line x1="14" y1="20" x2="14" y2="16" {...stroke} />
        <line x1="17" y1="20" x2="17" y2="13" {...stroke} />
      </svg>
    );
  }
  return null;
};

/* ── Shared geographic coordinate model ──────────────────────────────────
   One equirectangular fit-to-box projection, used by the map outline, the
   graticule, the radar rings, the routes, and every destination marker.
   Nothing is positioned independently—if the map plane is tilted, every
   layer tilts with it because they all live inside the same transformed
   container and share the same (lon,lat) → (x,y) function. */
const MAP_VB = { w: 1000, h: 600 };
const GEO_BOUNDS = { lonMin: -125, lonMax: -66, latMin: 24, latMax: 49.5 };
const project = (lon, lat) => ({
  x: ((lon - GEO_BOUNDS.lonMin) / (GEO_BOUNDS.lonMax - GEO_BOUNDS.lonMin)) * MAP_VB.w,
  y: ((GEO_BOUNDS.latMax - lat) / (GEO_BOUNDS.latMax - GEO_BOUNDS.latMin)) * MAP_VB.h,
});

/* Real continental-US national boundary (Natural Earth admin-0 country
   polygon, public domain), simplified via Ramer-Douglas-Peucker from ~2,000
   to ~900 points. Replaces an earlier hand-traced silhouette that didn't
   match the real coastline the accurate state/lake data now uses—that
   mismatch was cutting into real land and leaving stray water outside the
   state borders along the coast. */
const US_OUTLINE = [
  [-74.709,45.004],[-71.518,45.008],[-71.419,45.2],[-71.327,45.29],[-71.135,45.263],[-71,45.337],[-70.865,45.271],[-70.799,45.405],[-70.69,45.428],[-70.702,45.551],[-70.421,45.738],[-70.408,45.802],[-70.296,45.906],[-70.304,46.057],[-70.248,46.251],[-70.067,46.441],[-70.008,46.709],[-69.243,47.463],[-69.05,47.427],[-69.049,47.274],[-68.937,47.211],[-68.829,47.203],[-68.358,47.345],[-68.235,47.346],[-67.807,47.083],[-67.767,45.927],[-67.802,45.728],[-67.596,45.621],[-67.433,45.603],[-67.424,45.53],[-67.494,45.474],[-67.428,45.378],[-67.473,45.276],[-67.315,45.154],[-67.25,45.201],[-67.125,45.169],[-67.08,44.989],[-67.107,44.885],[-66.987,44.828],[-67.191,44.676],[-67.364,44.697],[-67.556,44.645],[-67.653,44.562],[-67.839,44.576],[-67.907,44.454],[-67.963,44.464],[-68.057,44.384],[-68.152,44.502],[-68.277,44.507],[-68.374,44.445],[-68.451,44.508],[-68.521,44.38],[-68.533,44.259],[-68.612,44.311],[-68.812,44.339],[-68.71,44.443],[-68.795,44.454],[-68.763,44.571],[-68.961,44.434],[-68.956,44.348],[-69.064,44.172],[-69.068,44.098],[-69.137,44.038],[-69.226,43.986],[-69.345,44.001],[-69.521,43.897],[-69.557,43.983],[-69.59,43.887],[-69.624,43.881],[-69.653,43.994],[-69.73,43.852],[-69.795,43.911],[-69.808,43.772],[-69.873,43.82],[-69.974,43.788],[-69.965,43.855],[-70.062,43.835],[-70.269,43.672],[-70.203,43.626],[-70.521,43.349],[-70.642,43.134],[-70.733,43.07],[-70.829,42.825],[-70.736,42.669],[-70.624,42.672],[-70.613,42.623],[-70.831,42.553],[-71.046,42.331],[-70.738,42.229],[-70.618,42.04],[-70.656,41.987],[-70.549,41.939],[-70.515,41.803],[-70.427,41.757],[-70.295,41.729],[-70.001,41.826],[-70.11,42.03],[-70.237,42.071],[-70.204,42.101],[-70.109,42.078],[-69.978,41.961],[-69.949,41.677],[-70.405,41.627],[-70.657,41.534],[-70.666,41.71],[-70.701,41.715],[-70.974,41.549],[-71.169,41.489],[-71.204,41.641],[-71.149,41.746],[-71.271,41.681],[-71.39,41.795],[-71.364,41.703],[-71.427,41.633],[-71.444,41.454],[-71.523,41.379],[-72.479,41.276],[-72.925,41.285],[-73.024,41.216],[-73.583,41.022],[-73.987,40.751],[-73.872,41.055],[-73.882,41.171],[-73.97,41.25],[-73.918,41.136],[-73.927,40.914],[-74.025,40.756],[-74.187,40.648],[-74.264,40.529],[-74.242,40.456],[-73.998,40.452],[-73.972,40.4],[-73.958,40.328],[-74.08,39.788],[-74.065,39.993],[-74.096,39.976],[-74.176,39.727],[-74.257,39.614],[-74.331,39.536],[-74.407,39.549],[-74.39,39.487],[-74.429,39.387],[-74.603,39.293],[-74.794,39.002],[-74.954,38.95],[-74.897,39.145],[-75.136,39.208],[-75.524,39.49],[-75.524,39.602],[-75.422,39.79],[-75.154,39.871],[-75.074,39.983],[-75.173,39.895],[-75.401,39.832],[-75.588,39.641],[-75.574,39.477],[-75.413,39.281],[-75.392,39.093],[-75.185,38.819],[-75.089,38.778],[-75.128,38.632],[-75.187,38.591],[-75.073,38.579],[-75.039,38.426],[-75.074,38.366],[-75.073,38.41],[-75.117,38.406],[-75.16,38.255],[-75.225,38.242],[-75.354,38.065],[-75.596,37.631],[-75.587,37.559],[-75.812,37.425],[-75.934,37.152],[-75.997,37.264],[-75.975,37.398],[-75.888,37.619],[-75.719,37.821],[-75.659,37.954],[-75.851,37.972],[-75.795,38.087],[-75.928,38.169],[-75.864,38.261],[-75.859,38.362],[-76.051,38.28],[-76.212,38.361],[-76.265,38.436],[-76.295,38.495],[-76.264,38.6],[-76.017,38.625],[-76.057,38.621],[-76.213,38.758],[-76.278,38.772],[-76.341,38.71],[-76.3,38.818],[-76.168,38.853],[-76.241,38.943],[-76.331,38.909],[-76.313,39.009],[-76.186,38.991],[-76.135,39.082],[-76.133,39.123],[-76.217,39.064],[-76.236,39.192],[-76.074,39.369],[-75.876,39.376],[-76.003,39.411],[-75.873,39.511],[-75.97,39.505],[-75.959,39.585],[-76.063,39.561],[-76.097,39.433],[-76.216,39.38],[-76.248,39.439],[-76.276,39.323],[-76.331,39.404],[-76.421,39.225],[-76.57,39.269],[-76.428,39.126],[-76.42,39.074],[-76.473,39.031],[-76.559,39.065],[-76.494,38.945],[-76.537,38.743],[-76.501,38.532],[-76.394,38.369],[-76.572,38.436],[-76.677,38.612],[-76.669,38.538],[-76.642,38.454],[-76.409,38.268],[-76.333,38.141],[-76.341,38.087],[-76.454,38.174],[-76.769,38.263],[-76.868,38.39],[-76.89,38.292],[-77.001,38.445],[-77.233,38.408],[-77.221,38.541],[-77.018,38.778],[-77.03,38.889],[-77.046,38.776],[-77.26,38.6],[-77.314,38.397],[-77.273,38.352],[-77.047,38.357],[-76.906,38.197],[-76.55,38.094],[-76.264,37.894],[-76.344,37.676],[-76.492,37.682],[-76.94,38.095],[-77.111,38.166],[-76.549,37.669],[-76.306,37.571],[-76.368,37.53],[-76.269,37.495],[-76.254,37.431],[-76.263,37.357],[-76.401,37.386],[-76.393,37.3],[-76.454,37.274],[-76.758,37.505],[-76.611,37.323],[-76.327,37.149],[-76.283,37.053],[-76.401,36.991],[-76.602,37.143],[-76.631,37.222],[-77.007,37.318],[-77.251,37.329],[-76.672,37.173],[-76.634,37.047],[-76.505,36.961],[-76.488,36.897],[-76.4,36.89],[-76.244,36.953],[-75.999,36.913],[-75.758,36.229],[-75.534,35.819],[-75.728,36.104],[-75.946,36.659],[-75.993,36.474],[-75.82,36.113],[-75.883,36.176],[-76.148,36.279],[-76.15,36.146],[-76.271,36.19],[-76.227,36.116],[-76.384,36.134],[-76.479,36.028],[-76.559,36.015],[-76.679,36.075],[-76.734,36.229],[-76.726,35.958],[-76.07,35.97],[-76.084,35.691],[-76.001,35.722],[-75.979,35.896],[-75.854,35.96],[-75.812,35.956],[-75.759,35.843],[-75.774,35.647],[-76.174,35.354],[-76.49,35.397],[-76.577,35.532],[-76.611,35.53],[-76.634,35.453],[-76.741,35.431],[-77.04,35.527],[-76.974,35.458],[-76.513,35.27],[-76.628,35.073],[-76.779,34.99],[-76.861,35.005],[-77.07,35.155],[-76.975,35.025],[-76.899,34.97],[-76.745,34.941],[-76.457,34.989],[-76.362,34.937],[-76.44,34.843],[-76.517,34.777],[-76.707,34.752],[-76.733,34.707],[-77.134,34.708],[-77.296,34.603],[-77.358,34.62],[-77.412,34.731],[-77.38,34.527],[-77.751,34.285],[-77.861,34.149],[-77.928,33.94],[-77.953,34.169],[-78.013,33.912],[-78.406,33.918],[-78.578,33.873],[-78.841,33.724],[-79.138,33.406],[-79.194,33.244],[-79.238,33.312],[-79.226,33.405],[-79.281,33.315],[-79.229,33.185],[-79.276,33.135],[-79.42,33.043],[-79.587,33.001],[-79.615,32.909],[-79.805,32.787],[-79.933,32.81],[-79.894,32.729],[-79.941,32.667],[-80.363,32.501],[-80.634,32.512],[-80.474,32.423],[-80.486,32.352],[-80.579,32.287],[-80.647,32.396],[-80.709,32.337],[-80.803,32.448],[-80.798,32.363],[-80.694,32.216],[-80.758,32.142],[-80.849,32.114],[-80.923,31.945],[-81.113,31.879],[-81.066,31.788],[-81.198,31.704],[-81.17,31.61],[-81.259,31.539],[-81.175,31.531],[-81.295,31.371],[-81.381,31.353],[-81.288,31.264],[-81.365,31.172],[-81.442,31.2],[-81.46,31.127],[-81.516,30.802],[-81.25,29.794],[-80.9,29.05],[-80.524,28.486],[-80.568,28.426],[-80.585,28.272],[-80.457,27.901],[-80.61,28.178],[-80.607,28.523],[-80.694,28.345],[-80.732,28.463],[-80.688,28.579],[-80.766,28.633],[-80.771,28.732],[-80.838,28.758],[-80.749,28.381],[-80.226,27.207],[-80.126,27.083],[-80.05,26.808],[-80.041,26.569],[-80.126,25.833],[-80.159,25.878],[-80.301,25.619],[-80.367,25.331],[-80.485,25.23],[-80.737,25.156],[-80.862,25.176],[-81.11,25.138],[-81.167,25.229],[-81.136,25.31],[-81.098,25.319],[-80.965,25.224],[-80.94,25.264],[-81.113,25.367],[-81.345,25.732],[-81.365,25.831],[-81.568,25.892],[-81.715,25.983],[-81.811,26.146],[-81.867,26.435],[-81.959,26.49],[-81.829,26.687],[-81.97,26.552],[-82.04,26.552],[-82.078,26.704],[-82.067,26.892],[-82.013,26.962],[-82.181,26.937],[-82.181,26.84],[-82.29,26.871],[-82.715,27.5],[-82.636,27.525],[-82.431,27.771],[-82.406,27.863],[-82.446,27.903],[-82.521,27.878],[-82.636,27.981],[-82.675,27.964],[-82.597,27.873],[-82.611,27.777],[-82.661,27.718],[-82.743,27.709],[-82.844,27.846],[-82.661,28.486],[-82.651,28.888],[-82.769,29.052],[-83.29,29.452],[-83.694,29.926],[-84.044,30.104],[-84.31,30.065],[-84.356,30.029],[-84.383,29.907],[-84.55,29.898],[-84.801,29.773],[-85.319,29.68],[-85.376,29.695],[-85.414,29.768],[-85.414,29.842],[-85.336,29.74],[-85.315,29.758],[-85.354,29.876],[-85.676,30.122],[-85.623,30.117],[-85.61,30.148],[-85.663,30.189],[-85.604,30.287],[-85.741,30.244],[-85.756,30.167],[-85.791,30.172],[-86.175,30.333],[-86.454,30.399],[-86.24,30.429],[-86.124,30.406],[-86.166,30.464],[-86.257,30.493],[-86.448,30.496],[-86.68,30.403],[-87.201,30.339],[-86.986,30.431],[-86.965,30.502],[-86.998,30.57],[-87.072,30.5],[-87.171,30.539],[-87.185,30.454],[-87.281,30.339],[-87.476,30.294],[-87.501,30.309],[-87.448,30.394],[-87.622,30.265],[-88.006,30.231],[-87.79,30.292],[-87.924,30.45],[-87.923,30.562],[-88.011,30.694],[-88.135,30.367],[-88.692,30.355],[-88.905,30.415],[-89.224,30.332],[-89.321,30.345],[-89.444,30.223],[-89.588,30.166],[-89.954,30.269],[-90.045,30.351],[-90.126,30.369],[-90.225,30.379],[-90.332,30.278],[-90.413,30.14],[-90.175,30.029],[-89.994,30.059],[-89.737,30.172],[-89.665,30.117],[-89.815,30.007],[-89.744,29.93],[-89.632,29.904],[-89.494,30.058],[-89.401,30.046],[-89.354,29.82],[-89.531,29.772],[-89.591,29.725],[-89.559,29.698],[-89.683,29.675],[-89.721,29.619],[-89.675,29.539],[-89.514,29.42],[-89.181,29.336],[-89.117,29.248],[-89.016,29.203],[-89.021,29.143],[-89.11,29.099],[-89.156,29.017],[-89.236,29.081],[-89.376,28.981],[-89.354,29.07],[-89.443,29.194],[-89.62,29.302],[-89.792,29.333],[-89.818,29.416],[-89.877,29.458],[-90.159,29.537],[-90.141,29.48],[-90.052,29.431],[-90.053,29.337],[-90.101,29.182],[-90.213,29.105],[-90.302,29.256],[-90.379,29.295],[-90.586,29.272],[-90.677,29.151],[-90.751,29.131],[-91.29,29.289],[-91.237,29.331],[-91.151,29.318],[-91.244,29.457],[-91.249,29.564],[-91.331,29.514],[-91.514,29.555],[-91.672,29.746],[-91.824,29.751],[-91.893,29.836],[-92.017,29.8],[-92.135,29.699],[-92.059,29.617],[-92.084,29.593],[-92.261,29.557],[-92.671,29.597],[-93.176,29.779],[-93.695,29.77],[-93.826,29.725],[-93.884,29.81],[-93.809,29.851],[-93.769,29.952],[-93.841,29.98],[-93.946,29.815],[-93.89,29.689],[-94.1,29.67],[-94.76,29.384],[-94.526,29.548],[-94.778,29.548],[-94.724,29.655],[-94.742,29.75],[-94.832,29.753],[-94.89,29.677],[-95.023,29.702],[-94.993,29.531],[-94.888,29.371],[-95.139,29.168],[-95.152,29.079],[-95.273,28.964],[-95.853,28.64],[-96.235,28.489],[-96.011,28.632],[-96.373,28.657],[-96.449,28.594],[-96.576,28.716],[-96.64,28.709],[-96.525,28.489],[-96.421,28.457],[-96.676,28.341],[-96.774,28.422],[-96.84,28.194],[-96.892,28.158],[-96.933,28.224],[-97.015,28.163],[-97.156,28.144],[-97.141,28.061],[-97.034,28.094],[-97.073,27.986],[-97.171,27.88],[-97.431,27.837],[-97.289,27.671],[-97.439,27.328],[-97.524,27.314],[-97.768,27.458],[-97.692,27.287],[-97.485,27.237],[-97.476,27.118],[-97.555,26.967],[-97.435,26.486],[-97.214,26.068],[-97.15,26.065],[-97.14,26.03],[-97.146,25.961],[-97.282,25.942],[-97.358,25.871],[-97.801,26.042],[-98.275,26.111],[-98.378,26.182],[-98.691,26.276],[-98.765,26.34],[-99.108,26.447],[-99.23,26.762],[-99.457,27.057],[-99.44,27.17],[-99.51,27.34],[-99.505,27.548],[-99.754,27.73],[-100.112,28.173],[-100.296,28.328],[-100.332,28.503],[-100.755,29.183],[-101.016,29.401],[-101.039,29.46],[-101.304,29.634],[-101.38,29.743],[-101.546,29.808],[-101.991,29.796],[-102.343,29.865],[-102.476,29.769],[-102.615,29.752],[-102.734,29.644],[-102.892,29.216],[-103.09,29.042],[-103.258,29.001],[-103.853,29.291],[-103.99,29.323],[-104.401,29.574],[-104.622,29.854],[-104.681,29.991],[-104.681,30.134],[-104.918,30.583],[-105.514,30.981],[-106.024,31.398],[-106.148,31.451],[-106.445,31.768],[-108.212,31.779],[-108.214,31.329],[-111.042,31.324],[-114.836,32.508],[-114.725,32.715],[-117.128,32.533],[-117.137,32.649],[-117.184,32.688],[-117.243,32.664],[-117.263,32.939],[-117.319,33.1],[-117.467,33.296],[-118.081,33.722],[-118.41,33.744],[-118.393,33.858],[-118.506,34.017],[-118.832,34.024],[-119.144,34.112],[-119.236,34.164],[-119.268,34.257],[-119.606,34.418],[-120.481,34.472],[-120.645,34.58],[-120.625,34.812],[-120.659,35.122],[-120.857,35.21],[-120.885,35.275],[-120.86,35.365],[-120.9,35.425],[-121.284,35.676],[-121.344,35.792],[-121.664,36.154],[-121.877,36.331],[-121.919,36.572],[-121.79,36.732],[-121.807,36.851],[-121.881,36.939],[-122.164,36.991],[-122.395,37.208],[-122.408,37.373],[-122.499,37.543],[-122.514,37.772],[-122.446,37.798],[-122.384,37.789],[-122.37,37.656],[-122.298,37.592],[-122.071,37.478],[-122.385,37.961],[-122.314,38.007],[-122.087,38.05],[-121.525,38.056],[-121.881,38.075],[-122.031,38.124],[-122.154,38.066],[-122.393,38.145],[-122.484,38.109],[-122.467,37.838],[-122.521,37.826],[-122.932,38.055],[-122.999,37.989],[-122.978,38.227],[-122.877,38.123],[-122.908,38.197],[-123.046,38.305],[-123.121,38.449],[-123.29,38.536],[-123.701,38.907],[-123.72,39.111],[-123.82,39.368],[-123.778,39.515],[-123.833,39.775],[-124.108,40.095],[-124.324,40.252],[-124.372,40.491],[-124.284,40.711],[-124.254,40.74],[-124.22,40.696],[-124.19,40.772],[-124.223,40.775],[-124.133,40.97],[-124.14,41.156],[-124.072,41.46],[-124.163,41.719],[-124.245,41.788],[-124.212,41.985],[-124.355,42.123],[-124.421,42.381],[-124.406,42.584],[-124.54,42.813],[-124.347,43.342],[-124.197,43.423],[-124.288,43.41],[-124.149,43.692],[-124.099,44.334],[-124.047,44.425],[-124.059,44.778],[-123.929,45.577],[-123.947,46.141],[-123.989,46.219],[-123.912,46.182],[-123.674,46.183],[-123.522,46.223],[-123.402,46.155],[-123.221,46.154],[-123.465,46.271],[-123.65,46.268],[-123.688,46.3],[-124.073,46.279],[-124.044,46.605],[-123.946,46.433],[-123.889,46.66],[-124.072,46.745],[-124.113,46.863],[-123.843,46.963],[-124.112,47.035],[-124.139,46.955],[-124.376,47.659],[-124.663,47.974],[-124.71,48.38],[-123.976,48.168],[-123.294,48.12],[-123.162,48.155],[-122.974,48.073],[-122.779,48.138],[-122.657,47.881],[-122.778,47.738],[-122.821,47.793],[-123.051,47.552],[-123.139,47.386],[-123.104,47.348],[-122.922,47.408],[-123.067,47.4],[-123.049,47.479],[-122.608,47.835],[-122.586,47.928],[-122.533,47.92],[-122.524,47.769],[-122.63,47.693],[-122.614,47.616],[-122.675,47.612],[-122.557,47.463],[-122.578,47.293],[-122.708,47.316],[-122.768,47.218],[-122.828,47.337],[-122.92,47.29],[-123.028,47.139],[-122.812,47.146],[-122.702,47.111],[-122.604,47.167],[-122.542,47.276],[-122.354,47.372],[-122.402,47.784],[-122.242,48.011],[-122.416,48.184],[-122.395,48.084],[-122.517,48.16],[-122.52,48.229],[-122.409,48.294],[-122.669,48.465],[-122.497,48.506],[-122.513,48.669],[-122.562,48.778],[-122.653,48.764],[-122.686,48.794],[-122.789,48.993],[-95.162,48.992],[-95.155,49.37],[-94.939,49.349],[-94.854,49.305],[-94.803,49.003],[-94.705,48.808],[-94.621,48.743],[-94.055,48.659],[-93.708,48.525],[-93.464,48.561],[-93.378,48.617],[-92.996,48.612],[-92.501,48.435],[-92.415,48.277],[-92.172,48.338],[-92.005,48.302],[-91.858,48.198],[-91.518,48.058],[-91.387,48.059],[-90.916,48.209],[-90.84,48.201],[-90.744,48.105],[-90.092,48.118],[-89.994,48.015],[-89.901,47.995],[-89.456,47.996],[-89.273,48.02],[-88.612,48.264],[-88.378,48.303],[-84.876,46.9],[-84.779,46.637],[-84.562,46.457],[-84.149,46.543],[-84.088,46.227],[-83.978,46.085],[-83.616,46.117],[-83.469,45.995],[-83.593,45.817],[-82.551,45.347],[-82.138,43.571],[-82.408,43.073],[-82.545,42.625],[-82.868,42.385],[-83.073,42.3],[-83.15,42.142],[-83.142,41.976],[-83.03,41.833],[-82.69,41.675],[-82.439,41.675],[-81.278,42.209],[-80.248,42.366],[-79.037,42.802],[-78.915,42.909],[-79.026,43.017],[-79.066,43.106],[-79.059,43.278],[-79.172,43.467],[-78.846,43.583],[-78.72,43.625],[-78.458,43.631],[-76.82,43.629],[-76.465,44.058],[-76.186,44.242],[-76.151,44.304],[-75.876,44.417],[-75.179,44.899],[-74.857,45.004],[-74.709,45.004]
];
const US_OUTLINE_PATH = US_OUTLINE
  .map(([lon, lat], i) => {
    const p = project(lon, lat);
    return `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`;
  })
  .join(" ") + " Z";

/* Real continental-US state boundaries (Census-derived, simplified via
   Ramer-Douglas-Peucker to ~1,800 points total)—accurate shapes rather
   than hand-approximated rectangles, so every state reads correctly, not
   just the handful with naturally straight borders. */
const US_STATE_BOUNDARIES = [
  /* Alabama */ [[-87.359,35.001],[-85.607,34.985],[-85.185,32.86],[-84.96,32.422],[-85.004,32.323],[-84.889,32.263],[-85.059,32.137],[-85.141,31.841],[-85.043,31.54],[-85.114,31.277],[-85.004,31.003],[-87.6,30.998],[-87.633,30.866],[-87.409,30.674],[-87.447,30.51],[-87.37,30.428],[-87.518,30.28],[-87.655,30.247],[-87.907,30.412],[-87.934,30.658],[-88.011,30.685],[-88.137,30.318],[-88.394,30.368],[-88.471,31.896],[-88.099,34.892],[-88.203,34.996],[-87.359,35.001]],
  /* Arizona */ [[-109.043,37],[-109.048,31.332],[-111.074,31.332],[-114.815,32.493],[-114.722,32.717],[-114.525,32.756],[-114.47,32.843],[-114.525,33.029],[-114.662,33.035],[-114.728,33.407],[-114.525,33.55],[-114.536,33.933],[-114.415,34.108],[-114.136,34.306],[-114.333,34.448],[-114.634,34.875],[-114.574,35.138],[-114.739,36.102],[-114.372,36.14],[-114.251,36.02],[-114.152,36.025],[-114.048,36.195],[-114.048,37],[-109.043,37]],
  /* Arkansas */ [[-94.474,36.502],[-90.153,36.496],[-90.065,36.305],[-90.377,35.998],[-89.731,35.998],[-89.764,35.812],[-89.912,35.757],[-89.944,35.604],[-90.131,35.439],[-90.114,35.198],[-90.213,35.023],[-90.311,34.996],[-90.251,34.908],[-90.41,34.831],[-90.481,34.662],[-90.585,34.618],[-90.569,34.421],[-90.75,34.366],[-90.744,34.3],[-90.952,34.136],[-90.892,34.026],[-91.073,33.867],[-91.231,33.561],[-91.056,33.429],[-91.144,33.347],[-91.089,33.139],[-91.166,33.002],[-94.041,33.019],[-94.041,33.55],[-94.184,33.594],[-94.381,33.544],[-94.485,33.637],[-94.43,35.396],[-94.616,36.502],[-94.474,36.502]],
  /* California */ [[-123.233,42.006],[-120.002,41.995],[-120.002,38.999],[-117.499,37.219],[-114.634,35.001],[-114.634,34.875],[-114.333,34.448],[-114.136,34.306],[-114.415,34.108],[-114.536,33.933],[-114.525,33.55],[-114.728,33.407],[-114.662,33.035],[-114.525,33.029],[-114.47,32.843],[-114.525,32.756],[-117.126,32.537],[-117.247,32.668],[-117.329,33.123],[-117.472,33.298],[-118.184,33.763],[-118.26,33.703],[-118.414,33.741],[-118.392,33.84],[-118.567,34.043],[-118.802,33.999],[-119.219,34.147],[-119.279,34.267],[-119.558,34.415],[-120.473,34.448],[-120.648,34.579],[-120.632,35.1],[-120.895,35.248],[-120.906,35.45],[-121.283,35.675],[-121.716,36.195],[-121.897,36.316],[-121.935,36.639],[-121.859,36.611],[-121.787,36.803],[-121.93,36.978],[-122.105,36.956],[-122.417,37.241],[-122.401,37.362],[-122.516,37.521],[-122.516,37.783],[-122.33,37.783],[-122.406,38.15],[-122.488,38.112],[-122.505,37.931],[-122.702,37.893],[-122.938,38.03],[-122.976,38.265],[-123.129,38.452],[-123.737,38.956],[-123.688,39.032],[-123.825,39.366],[-123.765,39.553],[-123.852,39.832],[-124.362,40.259],[-124.411,40.44],[-124.159,40.878],[-124.066,41.442],[-124.148,41.716],[-124.257,41.782],[-124.214,42.001],[-123.233,42.006]],
  /* Colorado */ [[-107.92,41.004],[-102.054,41.004],[-102.043,36.995],[-109.043,37],[-109.048,40.998],[-107.92,41.004]],
  /* Connecticut */ [[-73.054,42.039],[-71.799,42.023],[-71.799,41.415],[-71.86,41.322],[-72.906,41.283],[-73.13,41.146],[-73.656,40.987],[-73.727,41.102],[-73.481,41.212],[-73.552,41.294],[-73.486,42.05],[-73.054,42.039]],
  /* Delaware */ [[-75.414,39.804],[-75.611,39.618],[-75.589,39.459],[-75.441,39.312],[-75.403,39.065],[-75.19,38.808],[-75.091,38.797],[-75.047,38.452],[-75.693,38.463],[-75.787,39.722],[-75.617,39.832],[-75.414,39.804]],
  /* District of Columbia */ [[-77.035,38.994],[-76.909,38.895],[-77.041,38.791],[-77.117,38.934],[-77.035,38.994]],
  /* Florida */ [[-85.497,30.998],[-85.004,31.003],[-84.867,30.713],[-82.216,30.57],[-82.167,30.357],[-82.047,30.362],[-82.041,30.751],[-81.948,30.828],[-81.444,30.707],[-81.258,29.787],[-80.968,29.146],[-80.524,28.462],[-80.59,28.412],[-80.568,28.095],[-80.031,26.797],[-80.146,25.74],[-80.239,25.723],[-80.338,25.466],[-80.305,25.384],[-80.497,25.197],[-80.573,25.241],[-81.077,25.121],[-81.17,25.225],[-81.127,25.378],[-81.351,25.822],[-81.526,25.904],[-81.68,25.844],[-81.833,26.293],[-82.041,26.517],[-82.058,26.879],[-82.173,26.917],[-82.145,26.791],[-82.249,26.758],[-82.693,27.438],[-82.392,27.837],[-82.589,27.815],[-82.72,27.689],[-82.852,27.887],[-82.677,28.434],[-82.644,28.889],[-82.764,28.998],[-82.802,29.146],[-82.994,29.179],[-83.399,29.519],[-83.41,29.667],[-83.536,29.721],[-83.64,29.886],[-84.024,30.105],[-84.358,30.056],[-84.342,29.902],[-84.451,29.93],[-84.867,29.743],[-85.311,29.7],[-85.3,29.809],[-85.404,29.94],[-85.924,30.236],[-86.297,30.362],[-86.631,30.395],[-87.518,30.28],[-87.37,30.428],[-87.447,30.51],[-87.409,30.674],[-87.633,30.866],[-87.6,30.998],[-85.497,30.998]],
  /* Georgia */ [[-83.109,35.001],[-83.323,34.788],[-83.339,34.684],[-83.005,34.47],[-82.901,34.486],[-82.556,33.944],[-81.926,33.462],[-81.937,33.347],[-81.762,33.161],[-81.493,33.008],[-81.417,32.63],[-81.28,32.558],[-81.121,32.29],[-81.116,32.12],[-80.886,32.033],[-81.132,31.693],[-81.291,31.206],[-81.4,31.134],[-81.444,30.707],[-81.948,30.828],[-82.041,30.751],[-82.047,30.362],[-82.167,30.357],[-82.216,30.57],[-84.867,30.713],[-85.114,31.277],[-85.043,31.54],[-85.141,31.841],[-85.059,32.137],[-84.889,32.263],[-85.004,32.323],[-84.96,32.422],[-85.185,32.86],[-85.607,34.985],[-83.109,35.001]],
  /* Idaho */ [[-116.048,49],[-116.048,47.976],[-115.724,47.697],[-115.719,47.423],[-115.325,47.259],[-114.93,46.919],[-114.886,46.809],[-114.624,46.705],[-114.613,46.64],[-114.322,46.645],[-114.492,46.037],[-114.388,45.884],[-114.569,45.774],[-114.498,45.67],[-114.547,45.561],[-114.333,45.457],[-113.988,45.703],[-113.807,45.605],[-113.835,45.522],[-113.736,45.331],[-113.451,45.057],[-113.457,44.865],[-113.342,44.783],[-113.134,44.772],[-113.002,44.449],[-112.887,44.394],[-112.783,44.487],[-112.471,44.482],[-112.241,44.569],[-112.104,44.52],[-111.869,44.564],[-111.819,44.509],[-111.617,44.547],[-111.387,44.756],[-111.047,44.476],[-111.047,42.001],[-117.028,42.001],[-117.028,43.83],[-116.896,44.159],[-116.979,44.241],[-117.17,44.257],[-117.241,44.394],[-117.039,44.75],[-116.831,44.931],[-116.847,45.024],[-116.464,45.615],[-116.546,45.752],[-116.781,45.824],[-117.055,46.344],[-117.033,49],[-116.048,49]],
  /* Illinois */ [[-90.64,42.51],[-87.803,42.494],[-87.836,42.302],[-87.524,41.71],[-87.529,39.35],[-87.639,39.169],[-87.496,38.78],[-87.836,38.293],[-87.951,38.276],[-87.923,38.15],[-88,38.101],[-88.027,37.8],[-88.159,37.657],[-88.066,37.482],[-88.477,37.389],[-88.515,37.285],[-88.422,37.154],[-88.548,37.071],[-89.03,37.214],[-89.183,37.039],[-89.134,36.984],[-89.293,36.995],[-89.517,37.28],[-89.435,37.345],[-89.517,37.69],[-89.84,37.904],[-89.95,37.882],[-90.355,38.216],[-90.35,38.375],[-90.109,38.846],[-90.47,38.961],[-90.585,38.868],[-90.662,38.928],[-90.728,39.257],[-91.368,39.728],[-91.494,40.034],[-91.505,40.237],[-91.401,40.56],[-91.122,40.67],[-91.095,40.823],[-90.963,40.922],[-90.947,41.097],[-91.111,41.239],[-91.045,41.415],[-90.656,41.464],[-90.344,41.59],[-90.311,41.743],[-90.18,41.809],[-90.169,42.127],[-90.394,42.225],[-90.421,42.329],[-90.64,42.51]],
  /* Indiana */ [[-85.99,41.76],[-84.807,41.76],[-84.818,39.103],[-84.895,39.06],[-84.813,38.786],[-85.174,38.687],[-85.431,38.731],[-85.42,38.534],[-85.59,38.452],[-85.656,38.326],[-85.831,38.276],[-85.924,38.024],[-86.039,37.959],[-86.264,38.052],[-86.302,38.167],[-86.521,38.041],[-86.505,37.931],[-86.729,37.893],[-86.795,37.992],[-87.047,37.893],[-87.129,37.789],[-87.6,37.975],[-87.682,37.904],[-87.934,37.893],[-88.027,37.8],[-88,38.101],[-87.923,38.15],[-87.951,38.276],[-87.836,38.293],[-87.496,38.78],[-87.639,39.169],[-87.529,39.35],[-87.524,41.71],[-87.425,41.645],[-87.118,41.645],[-86.823,41.76],[-85.99,41.76]],
  /* Iowa */ [[-91.368,43.501],[-91.215,43.501],[-91.204,43.354],[-91.056,43.255],[-91.177,43.134],[-91.067,42.751],[-90.711,42.636],[-90.394,42.225],[-90.169,42.127],[-90.142,42.001],[-90.18,41.809],[-90.311,41.743],[-90.344,41.59],[-90.656,41.464],[-91.045,41.415],[-91.111,41.239],[-90.947,41.097],[-90.963,40.922],[-91.095,40.823],[-91.122,40.67],[-91.401,40.56],[-91.418,40.38],[-91.73,40.615],[-95.766,40.588],[-95.881,40.719],[-95.827,40.977],[-95.925,41.201],[-95.92,41.453],[-96.095,41.541],[-96.062,41.798],[-96.128,41.973],[-96.265,42.039],[-96.446,42.488],[-96.632,42.707],[-96.435,43.123],[-96.561,43.222],[-96.528,43.397],[-96.582,43.479],[-96.451,43.501],[-91.368,43.501]],
  /* Kansas */ [[-101.906,40.002],[-95.306,40.002],[-95.208,39.909],[-94.885,39.832],[-95.109,39.542],[-94.824,39.207],[-94.611,39.158],[-94.616,37],[-102.043,36.995],[-102.054,40.002],[-101.906,40.002]],
  /* Kentucky */ [[-83.903,38.769],[-83.679,38.632],[-83.52,38.704],[-83.142,38.627],[-82.89,38.758],[-82.846,38.589],[-82.594,38.424],[-82.622,38.123],[-82.501,37.931],[-82.293,37.668],[-81.97,37.537],[-82.353,37.269],[-82.72,37.121],[-82.879,36.891],[-83.071,36.852],[-83.137,36.743],[-83.69,36.584],[-88.071,36.677],[-88.055,36.496],[-89.419,36.496],[-89.364,36.622],[-89.216,36.579],[-89.134,36.984],[-89.183,37.039],[-89.03,37.214],[-88.548,37.071],[-88.422,37.154],[-88.515,37.285],[-88.477,37.389],[-88.066,37.482],[-88.159,37.657],[-87.934,37.893],[-87.682,37.904],[-87.6,37.975],[-87.129,37.789],[-87.047,37.893],[-86.795,37.992],[-86.729,37.893],[-86.505,37.931],[-86.521,38.041],[-86.302,38.167],[-86.264,38.052],[-86.039,37.959],[-85.924,38.024],[-85.831,38.276],[-85.656,38.326],[-85.59,38.452],[-85.42,38.534],[-85.431,38.731],[-85.174,38.687],[-84.813,38.786],[-84.895,39.06],[-84.818,39.103],[-84.435,39.103],[-84.216,38.808],[-83.903,38.769]],
  /* Louisiana */ [[-93.608,33.019],[-91.166,33.002],[-91.073,32.887],[-91.144,32.843],[-91.155,32.641],[-91.007,32.515],[-90.985,32.219],[-91.106,31.989],[-91.341,31.846],[-91.401,31.622],[-91.5,31.644],[-91.516,31.277],[-91.637,31.266],[-91.566,31.069],[-91.637,30.998],[-89.747,30.998],[-89.846,30.669],[-89.523,30.181],[-89.818,30.045],[-89.84,29.946],[-89.599,29.88],[-89.495,30.039],[-89.287,29.88],[-89.304,29.754],[-89.424,29.7],[-89.649,29.749],[-89.621,29.656],[-89.698,29.513],[-89.506,29.387],[-89.2,29.349],[-89.002,29.179],[-89.161,29.009],[-89.336,29.042],[-89.484,29.218],[-89.851,29.311],[-89.851,29.48],[-90.032,29.426],[-90.021,29.283],[-90.103,29.152],[-90.235,29.13],[-90.333,29.278],[-90.563,29.283],[-90.645,29.13],[-90.799,29.086],[-91.095,29.19],[-91.221,29.437],[-91.533,29.53],[-91.62,29.738],[-91.883,29.71],[-91.889,29.836],[-92.146,29.716],[-92.113,29.623],[-92.31,29.535],[-93.225,29.776],[-93.839,29.689],[-93.926,29.787],[-93.691,30.143],[-93.767,30.335],[-93.696,30.439],[-93.729,30.576],[-93.526,30.937],[-93.543,31.151],[-93.817,31.556],[-93.822,31.775],[-94.041,31.994],[-94.041,33.019],[-93.608,33.019]],
  /* Maine */ [[-70.704,43.058],[-70.967,43.343],[-71.082,45.303],[-70.649,45.44],[-70.72,45.511],[-70.386,45.736],[-70.419,45.796],[-70.26,45.889],[-70.31,46.065],[-70.211,46.327],[-70.058,46.415],[-69.997,46.694],[-69.225,47.461],[-69.044,47.428],[-69.033,47.242],[-68.902,47.176],[-68.234,47.357],[-67.954,47.198],[-67.79,47.067],[-67.801,45.676],[-67.456,45.605],[-67.505,45.49],[-67.418,45.38],[-67.489,45.281],[-67.347,45.128],[-67.16,45.161],[-66.98,44.805],[-67.188,44.646],[-67.308,44.706],[-67.407,44.597],[-67.549,44.624],[-67.566,44.531],[-67.752,44.542],[-68.048,44.328],[-68.119,44.476],[-68.223,44.487],[-68.174,44.328],[-68.404,44.252],[-68.458,44.378],[-68.568,44.312],[-68.825,44.312],[-68.831,44.46],[-68.984,44.427],[-68.957,44.323],[-69.099,44.104],[-69.072,44.044],[-69.258,43.923],[-69.444,43.967],[-69.554,43.841],[-69.707,43.825],[-69.833,43.72],[-69.986,43.742],[-70.03,43.852],[-70.255,43.677],[-70.195,43.567],[-70.359,43.529],[-70.37,43.436],[-70.556,43.321],[-70.704,43.058]],
  /* Maryland */ [[-79.478,39.722],[-75.787,39.722],[-75.693,38.463],[-75.047,38.452],[-75.244,38.03],[-75.885,37.909],[-75.88,38.074],[-75.962,38.139],[-75.847,38.211],[-76,38.375],[-76.049,38.304],[-76.258,38.32],[-76.329,38.501],[-76.263,38.501],[-76.192,38.83],[-76.279,39.147],[-76.17,39.333],[-76,39.366],[-75.973,39.558],[-76.099,39.536],[-76.104,39.438],[-76.367,39.312],[-76.444,39.197],[-76.46,38.906],[-76.559,38.769],[-76.515,38.539],[-76.384,38.38],[-76.4,38.26],[-76.318,38.139],[-76.362,38.057],[-76.592,38.216],[-76.92,38.293],[-77.019,38.446],[-77.205,38.359],[-77.276,38.479],[-76.909,38.895],[-77.035,38.994],[-77.117,38.934],[-77.457,39.076],[-77.457,39.224],[-77.72,39.322],[-77.835,39.602],[-78.174,39.695],[-78.268,39.618],[-78.432,39.624],[-78.47,39.514],[-78.766,39.585],[-78.963,39.438],[-79.095,39.47],[-79.489,39.207],[-79.478,39.722]],
  /* Massachusetts */ [[-70.918,42.888],[-70.819,42.872],[-70.781,42.696],[-70.824,42.554],[-70.983,42.422],[-70.989,42.269],[-70.77,42.247],[-70.54,41.814],[-70.26,41.716],[-69.937,41.809],[-70.008,41.672],[-70.485,41.552],[-70.66,41.546],[-70.764,41.639],[-71.12,41.497],[-71.328,41.782],[-71.383,42.017],[-73.508,42.088],[-73.267,42.746],[-71.295,42.696],[-70.918,42.888]],
  /* Michigan */ [[-83.454,41.732],[-84.807,41.694],[-84.807,41.76],[-86.823,41.76],[-86.62,41.891],[-86.357,42.253],[-86.209,42.718],[-86.231,43.014],[-86.527,43.594],[-86.434,43.814],[-86.499,44.076],[-86.269,44.345],[-86.253,44.69],[-86.089,44.739],[-86.067,44.903],[-85.809,44.947],[-85.612,45.128],[-85.629,44.767],[-85.525,44.75],[-85.393,44.931],[-85.388,45.238],[-85.032,45.364],[-85.119,45.577],[-84.938,45.758],[-84.714,45.769],[-84.462,45.654],[-84.216,45.637],[-84.095,45.495],[-83.487,45.358],[-83.317,45.144],[-83.454,45.029],[-83.273,44.712],[-83.334,44.339],[-83.536,44.246],[-83.586,44.055],[-83.827,43.989],[-83.958,43.759],[-83.909,43.671],[-83.668,43.589],[-83.263,43.972],[-82.917,44.071],[-82.748,43.994],[-82.644,43.852],[-82.414,42.976],[-82.518,42.614],[-82.682,42.559],[-82.687,42.691],[-82.797,42.652],[-82.923,42.351],[-83.126,42.236],[-83.186,42.006],[-83.438,41.814],[-83.454,41.732]],
  /* Michigan */ [[-87.589,45.095],[-87.743,45.199],[-87.65,45.342],[-87.885,45.364],[-87.792,45.5],[-87.781,45.676],[-88.104,45.922],[-88.531,46.021],[-88.663,45.988],[-90.12,46.338],[-90.229,46.508],[-90.415,46.568],[-90.027,46.673],[-89.851,46.793],[-89.413,46.842],[-88.997,46.996],[-88.181,47.456],[-87.956,47.385],[-88.444,46.974],[-88.438,46.788],[-88.247,46.93],[-87.902,46.908],[-87.633,46.809],[-87.392,46.536],[-87.261,46.486],[-87.009,46.53],[-86.697,46.437],[-86.16,46.667],[-85.064,46.76],[-85.026,46.481],[-84.632,46.486],[-84.55,46.421],[-84.418,46.503],[-84.128,46.53],[-84.122,46.18],[-83.991,46.032],[-83.794,45.993],[-83.772,46.092],[-83.58,46.092],[-83.476,45.988],[-83.564,45.911],[-84.111,45.977],[-84.374,45.933],[-84.659,46.054],[-84.741,45.944],[-84.703,45.851],[-85.015,46.01],[-85.503,46.097],[-85.661,45.966],[-86.209,45.961],[-86.324,45.906],[-86.352,45.796],[-86.664,45.703],[-86.647,45.835],[-86.784,45.862],[-86.839,45.725],[-87.173,45.659],[-87.589,45.095]],
  /* Minnesota */ [[-92.015,46.705],[-92.091,46.749],[-92.294,46.667],[-92.294,46.076],[-92.639,45.933],[-92.869,45.72],[-92.886,45.577],[-92.771,45.566],[-92.645,45.44],[-92.76,45.287],[-92.809,44.75],[-92.546,44.569],[-92.338,44.553],[-92.234,44.443],[-91.927,44.334],[-91.878,44.202],[-91.434,43.994],[-91.242,43.775],[-91.215,43.501],[-96.451,43.501],[-96.451,45.298],[-96.681,45.413],[-96.856,45.605],[-96.582,45.818],[-96.599,46.333],[-96.719,46.437],[-96.802,46.656],[-96.856,47.609],[-97.13,48.14],[-97.163,48.546],[-97.097,48.683],[-97.229,49],[-95.153,49],[-95.153,49.384],[-94.956,49.373],[-94.824,49.296],[-94.693,48.776],[-94.589,48.715],[-93.839,48.628],[-93.795,48.518],[-93.209,48.644],[-92.984,48.622],[-92.727,48.54],[-92.655,48.436],[-92.508,48.447],[-92.371,48.223],[-92.305,48.316],[-92.053,48.359],[-92.009,48.266],[-91.713,48.201],[-91.713,48.113],[-91.566,48.042],[-90.837,48.239],[-90.75,48.091],[-90.142,48.113],[-89.873,47.987],[-89.616,48.009],[-89.972,47.828],[-90.739,47.626],[-92.091,46.788],[-92.015,46.705]],
  /* Mississippi */ [[-88.471,34.996],[-88.203,34.996],[-88.099,34.892],[-88.471,31.896],[-88.394,30.368],[-88.745,30.346],[-88.844,30.412],[-89.523,30.181],[-89.846,30.669],[-89.747,30.998],[-91.637,30.998],[-91.566,31.069],[-91.637,31.266],[-91.516,31.277],[-91.5,31.644],[-91.401,31.622],[-91.341,31.846],[-91.106,31.989],[-90.985,32.219],[-91.007,32.515],[-91.155,32.641],[-91.144,32.843],[-91.073,32.887],[-91.166,33.002],[-91.089,33.139],[-91.144,33.347],[-91.056,33.429],[-91.231,33.561],[-91.073,33.867],[-90.892,34.026],[-90.952,34.136],[-90.744,34.3],[-90.75,34.366],[-90.569,34.421],[-90.585,34.618],[-90.481,34.662],[-90.41,34.831],[-90.251,34.908],[-90.311,34.996],[-88.471,34.996]],
  /* Missouri */ [[-91.834,40.61],[-91.73,40.615],[-91.418,40.38],[-91.505,40.237],[-91.494,40.034],[-91.368,39.728],[-90.728,39.257],[-90.662,38.928],[-90.585,38.868],[-90.47,38.961],[-90.109,38.846],[-90.35,38.375],[-90.355,38.216],[-89.95,37.882],[-89.84,37.904],[-89.517,37.69],[-89.435,37.345],[-89.517,37.28],[-89.293,36.995],[-89.134,36.984],[-89.216,36.579],[-89.364,36.622],[-89.419,36.496],[-89.539,36.496],[-89.534,36.25],[-89.731,35.998],[-90.377,35.998],[-90.065,36.305],[-90.153,36.496],[-94.616,36.502],[-94.611,39.158],[-94.824,39.207],[-95.109,39.542],[-94.885,39.832],[-95.208,39.909],[-95.766,40.588],[-91.834,40.61]],
  /* Montana */ [[-104.048,49],[-104.042,44.997],[-111.053,45.002],[-111.047,44.476],[-111.387,44.756],[-111.617,44.547],[-111.819,44.509],[-111.869,44.564],[-112.104,44.52],[-112.241,44.569],[-112.471,44.482],[-112.783,44.487],[-112.887,44.394],[-113.002,44.449],[-113.134,44.772],[-113.342,44.783],[-113.457,44.865],[-113.451,45.057],[-113.736,45.331],[-113.835,45.522],[-113.807,45.605],[-113.988,45.703],[-114.333,45.457],[-114.547,45.561],[-114.498,45.67],[-114.569,45.774],[-114.388,45.884],[-114.492,46.037],[-114.322,46.645],[-114.613,46.64],[-114.624,46.705],[-114.886,46.809],[-114.93,46.919],[-115.325,47.259],[-115.719,47.423],[-115.724,47.697],[-116.048,47.976],[-116.048,49],[-104.048,49]],
  /* Nebraska */ [[-103.325,43.003],[-98.499,42.998],[-97.952,42.767],[-97.831,42.866],[-97.218,42.844],[-96.692,42.658],[-96.626,42.516],[-96.446,42.488],[-96.265,42.039],[-96.128,41.973],[-96.062,41.798],[-96.095,41.541],[-95.92,41.453],[-95.925,41.201],[-95.827,40.977],[-95.881,40.719],[-95.306,40.002],[-102.054,40.002],[-102.054,41.004],[-104.053,41.004],[-104.053,43.003],[-103.325,43.003]],
  /* Nevada */ [[-117.028,42.001],[-114.043,41.995],[-114.048,36.195],[-114.152,36.025],[-114.251,36.02],[-114.372,36.14],[-114.739,36.102],[-114.574,35.138],[-114.634,35.001],[-117.499,37.219],[-120.002,38.999],[-120.002,41.995],[-117.028,42.001]],
  /* New Hampshire */ [[-71.082,45.303],[-70.967,43.343],[-70.704,43.058],[-70.819,42.872],[-71.186,42.789],[-71.295,42.696],[-72.457,42.729],[-72.544,42.806],[-72.533,42.954],[-72.446,43.008],[-72.38,43.573],[-72.029,44.076],[-72.035,44.323],[-71.701,44.416],[-71.536,44.586],[-71.63,44.75],[-71.493,44.914],[-71.361,45.27],[-71.131,45.243],[-71.082,45.303]],
  /* New Jersey */ [[-74.237,41.141],[-73.902,40.998],[-74.023,40.708],[-74.187,40.642],[-74.275,40.489],[-74.001,40.412],[-74.1,39.761],[-74.795,38.994],[-74.888,39.158],[-75.179,39.24],[-75.535,39.459],[-75.562,39.629],[-75.414,39.804],[-75.146,39.887],[-75.129,39.963],[-74.773,40.215],[-75.058,40.418],[-75.069,40.544],[-75.195,40.577],[-75.206,40.692],[-75.053,40.867],[-75.135,40.971],[-74.697,41.36],[-74.237,41.141]],
  /* New Mexico */ [[-107.421,37],[-103.001,37],[-103.067,32],[-106.616,32],[-106.644,31.901],[-106.529,31.786],[-108.21,31.786],[-108.21,31.332],[-109.048,31.332],[-109.043,37],[-107.421,37]],
  /* New York */ [[-73.344,45.013],[-73.388,44.619],[-73.295,44.438],[-73.437,44.044],[-73.349,43.77],[-73.404,43.688],[-73.245,43.523],[-73.267,42.746],[-73.508,42.088],[-73.552,41.294],[-73.481,41.212],[-73.727,41.102],[-73.656,40.987],[-73.229,40.905],[-73.141,40.966],[-72.588,40.998],[-72.281,41.157],[-72.259,41.042],[-72.101,40.993],[-73.24,40.626],[-73.935,40.544],[-74.023,40.708],[-73.902,40.998],[-74.74,41.431],[-74.894,41.437],[-75.075,41.606],[-75.053,41.754],[-75.359,42.001],[-79.763,42.001],[-79.763,42.269],[-79.149,42.554],[-79.051,42.691],[-78.854,42.784],[-79.012,42.987],[-79.073,43.26],[-78.487,43.375],[-77.758,43.343],[-77.534,43.233],[-76.696,43.343],[-76.416,43.523],[-76.236,43.529],[-76.23,43.803],[-76.137,43.961],[-76.362,44.071],[-76.312,44.197],[-75.912,44.367],[-75.283,44.849],[-74.828,45.019],[-73.344,45.013]],
  /* North Carolina */ [[-80.979,36.562],[-75.869,36.551],[-75.754,36.151],[-76.033,36.19],[-76.685,36.009],[-76.674,35.938],[-76.06,35.993],[-75.962,35.899],[-75.781,35.938],[-75.715,35.697],[-75.776,35.582],[-75.896,35.571],[-76.148,35.324],[-76.482,35.313],[-76.537,35.144],[-76.394,34.974],[-76.279,34.941],[-76.493,34.662],[-76.674,34.694],[-77.211,34.607],[-77.556,34.415],[-77.829,34.163],[-77.972,33.846],[-78.18,33.917],[-78.541,33.851],[-79.675,34.804],[-80.798,34.82],[-80.781,34.935],[-80.935,35.105],[-81.039,35.045],[-81.044,35.149],[-82.277,35.198],[-83.109,35.001],[-84.32,34.99],[-84.292,35.226],[-84.095,35.248],[-84.018,35.412],[-83.772,35.56],[-83.498,35.565],[-83.252,35.719],[-82.994,35.773],[-82.638,36.064],[-82.611,35.965],[-82.216,36.157],[-82.036,36.118],[-81.91,36.305],[-81.724,36.354],[-81.68,36.589],[-80.979,36.562]],
  /* North Dakota */ [[-97.229,49],[-97.097,48.683],[-97.163,48.546],[-97.13,48.14],[-96.856,47.609],[-96.802,46.656],[-96.719,46.437],[-96.599,46.333],[-96.561,45.933],[-104.048,45.944],[-104.048,49],[-97.229,49]],
  /* Ohio */ [[-80.519,41.979],[-80.519,40.637],[-80.666,40.582],[-80.601,40.319],[-80.831,39.711],[-81.22,39.388],[-81.346,39.344],[-81.455,39.41],[-81.57,39.268],[-81.685,39.273],[-81.811,39.082],[-81.784,38.966],[-81.888,38.873],[-82.036,39.027],[-82.222,38.786],[-82.173,38.632],[-82.293,38.578],[-82.331,38.446],[-82.594,38.424],[-82.846,38.589],[-82.89,38.758],[-83.142,38.627],[-83.52,38.704],[-83.679,38.632],[-83.903,38.769],[-84.216,38.808],[-84.435,39.103],[-84.818,39.103],[-84.807,41.694],[-83.454,41.732],[-82.934,41.513],[-82.835,41.59],[-82.479,41.382],[-82.014,41.513],[-81.74,41.486],[-81.012,41.853],[-80.519,41.979]],
  /* Oklahoma */ [[-100.088,37],[-94.616,37],[-94.616,36.502],[-94.43,35.396],[-94.485,33.637],[-94.868,33.747],[-94.967,33.862],[-95.224,33.961],[-95.29,33.873],[-95.602,33.933],[-95.838,33.835],[-95.936,33.889],[-96.15,33.84],[-96.347,33.687],[-96.632,33.846],[-96.851,33.846],[-96.922,33.961],[-97.174,33.736],[-97.256,33.862],[-97.371,33.824],[-97.694,33.982],[-97.87,33.851],[-97.946,33.988],[-98.089,34.004],[-98.171,34.114],[-98.362,34.158],[-98.488,34.065],[-98.571,34.147],[-99.189,34.213],[-99.261,34.404],[-99.699,34.382],[-99.923,34.574],[-100,34.563],[-100,36.502],[-103.001,36.502],[-103.001,37],[-100.088,37]],
  /* Oregon */ [[-123.211,46.174],[-122.905,46.081],[-122.812,45.961],[-122.762,45.659],[-122.247,45.55],[-121.809,45.709],[-121.535,45.725],[-121.218,45.67],[-121.185,45.605],[-120.637,45.747],[-120.21,45.725],[-118.989,45.999],[-116.918,45.993],[-116.781,45.824],[-116.546,45.752],[-116.464,45.615],[-116.847,45.024],[-116.831,44.931],[-117.039,44.75],[-117.241,44.394],[-117.17,44.257],[-116.979,44.241],[-116.896,44.159],[-117.028,43.83],[-117.028,42.001],[-124.214,42.001],[-124.356,42.116],[-124.416,42.663],[-124.553,42.839],[-124.17,43.808],[-123.978,45.144],[-123.94,45.659],[-123.995,45.944],[-123.945,46.114],[-123.545,46.262],[-123.37,46.147],[-123.211,46.174]],
  /* Pennsylvania */ [[-79.763,42.253],[-79.763,42.001],[-75.359,42.001],[-75.053,41.754],[-75.075,41.606],[-74.697,41.36],[-75.135,40.971],[-75.053,40.867],[-75.206,40.692],[-75.195,40.577],[-75.069,40.544],[-75.058,40.418],[-74.773,40.215],[-75.129,39.963],[-75.146,39.887],[-75.414,39.804],[-75.617,39.832],[-75.787,39.722],[-80.519,39.722],[-80.519,41.979],[-79.763,42.253]],
  /* Rhode Island */ [[-71.531,42.017],[-71.383,42.017],[-71.328,41.782],[-71.224,41.71],[-71.345,41.727],[-71.482,41.371],[-71.86,41.322],[-71.799,41.415],[-71.799,42.006],[-71.531,42.017]],
  /* South Carolina */ [[-82.764,35.067],[-82.277,35.198],[-81.044,35.149],[-81.039,35.045],[-80.935,35.105],[-80.781,34.935],[-80.798,34.82],[-79.675,34.804],[-78.541,33.851],[-78.936,33.637],[-79.149,33.38],[-79.188,33.172],[-79.357,33.008],[-79.582,33.008],[-79.631,32.887],[-80.661,32.246],[-80.886,32.033],[-81.116,32.12],[-81.121,32.29],[-81.28,32.558],[-81.417,32.63],[-81.493,33.008],[-81.762,33.161],[-81.937,33.347],[-81.926,33.462],[-82.556,33.944],[-82.901,34.486],[-83.005,34.47],[-83.339,34.684],[-83.323,34.788],[-83.109,35.001],[-82.764,35.067]],
  /* South Dakota */ [[-104.048,45.944],[-96.561,45.933],[-96.582,45.818],[-96.856,45.605],[-96.681,45.413],[-96.451,45.298],[-96.451,43.501],[-96.582,43.479],[-96.528,43.397],[-96.561,43.222],[-96.435,43.123],[-96.632,42.707],[-96.446,42.488],[-96.626,42.516],[-96.692,42.658],[-97.218,42.844],[-97.831,42.866],[-97.952,42.767],[-98.499,42.998],[-104.053,43.003],[-104.048,45.944]],
  /* Tennessee */ [[-88.055,36.496],[-88.071,36.677],[-81.68,36.589],[-81.724,36.354],[-81.91,36.305],[-82.036,36.118],[-82.216,36.157],[-82.611,35.965],[-82.638,36.064],[-82.994,35.773],[-83.252,35.719],[-83.498,35.565],[-83.772,35.56],[-84.018,35.412],[-84.095,35.248],[-84.292,35.226],[-84.32,34.99],[-90.311,34.996],[-90.213,35.023],[-90.114,35.198],[-90.131,35.439],[-89.944,35.604],[-89.912,35.757],[-89.764,35.812],[-89.731,35.998],[-89.534,36.25],[-89.539,36.496],[-88.055,36.496]],
  /* Texas */ [[-101.813,36.502],[-100,36.502],[-100,34.563],[-99.923,34.574],[-99.699,34.382],[-99.261,34.404],[-99.189,34.213],[-98.571,34.147],[-98.488,34.065],[-98.362,34.158],[-98.171,34.114],[-98.089,34.004],[-97.946,33.988],[-97.87,33.851],[-97.694,33.982],[-97.371,33.824],[-97.256,33.862],[-97.174,33.736],[-96.922,33.961],[-96.851,33.846],[-96.632,33.846],[-96.347,33.687],[-96.15,33.84],[-95.936,33.889],[-95.838,33.835],[-95.602,33.933],[-95.29,33.873],[-95.224,33.961],[-94.381,33.544],[-94.184,33.594],[-94.041,33.55],[-94.041,31.994],[-93.822,31.775],[-93.817,31.556],[-93.543,31.151],[-93.526,30.937],[-93.729,30.576],[-93.696,30.439],[-93.767,30.335],[-93.691,30.143],[-93.926,29.787],[-93.839,29.689],[-94.523,29.546],[-94.709,29.623],[-94.742,29.787],[-94.874,29.672],[-94.967,29.7],[-95.016,29.557],[-94.912,29.497],[-94.896,29.311],[-95.383,28.867],[-95.985,28.604],[-96.478,28.599],[-96.593,28.725],[-96.665,28.697],[-96.402,28.44],[-96.593,28.358],[-96.774,28.407],[-96.802,28.226],[-97.026,28.04],[-97.404,27.333],[-97.514,27.361],[-97.541,27.229],[-97.426,27.262],[-97.563,26.841],[-97.47,26.758],[-97.442,26.457],[-97.333,26.353],[-97.218,25.992],[-97.524,25.888],[-97.65,26.019],[-98.198,26.057],[-98.467,26.222],[-98.669,26.238],[-98.823,26.37],[-99.031,26.413],[-99.173,26.539],[-99.266,26.841],[-99.447,27.021],[-99.48,27.481],[-99.606,27.64],[-99.88,27.799],[-99.934,27.98],[-100.296,28.281],[-100.674,29.103],[-101.063,29.459],[-101.26,29.535],[-101.413,29.754],[-102.114,29.793],[-102.339,29.869],[-102.388,29.765],[-102.629,29.732],[-102.81,29.524],[-102.919,29.19],[-103.116,28.987],[-103.281,28.982],[-104.146,29.382],[-104.508,29.639],[-104.677,29.924],[-104.688,30.181],[-104.858,30.39],[-104.896,30.57],[-105.006,30.685],[-105.395,30.855],[-105.954,31.364],[-106.205,31.469],[-106.381,31.731],[-106.644,31.901],[-106.616,32],[-103.067,32],[-103.04,36.502],[-101.813,36.502]],
  /* Utah */ [[-112.164,41.995],[-111.047,42.001],[-111.047,40.998],[-109.048,40.998],[-109.043,37],[-114.048,37],[-114.043,41.995],[-112.164,41.995]],
  /* Vermont */ [[-71.504,45.013],[-71.493,44.914],[-71.63,44.75],[-71.536,44.586],[-71.701,44.416],[-72.035,44.323],[-72.029,44.076],[-72.38,43.573],[-72.446,43.008],[-72.533,42.954],[-72.544,42.806],[-72.457,42.729],[-73.267,42.746],[-73.245,43.523],[-73.404,43.688],[-73.349,43.77],[-73.437,44.044],[-73.295,44.438],[-73.388,44.619],[-73.344,45.013],[-71.504,45.013]],
  /* Virginia */ [[-78.35,39.465],[-77.829,39.131],[-77.72,39.322],[-77.567,39.306],[-77.457,39.224],[-77.457,39.076],[-77.117,38.934],[-77.041,38.791],[-77.326,38.446],[-77.282,38.342],[-77.013,38.375],[-76.964,38.216],[-76.614,38.15],[-76.515,38.024],[-76.236,37.888],[-76.362,37.608],[-76.247,37.389],[-76.384,37.285],[-76.4,37.159],[-76.274,37.082],[-76.411,36.962],[-76.619,37.121],[-76.668,37.066],[-76.488,36.951],[-75.995,36.924],[-75.869,36.551],[-83.673,36.6],[-83.137,36.743],[-83.071,36.852],[-82.879,36.891],[-82.72,37.121],[-82.353,37.269],[-81.97,37.537],[-81.986,37.455],[-81.849,37.285],[-81.68,37.203],[-81.362,37.34],[-81.225,37.236],[-80.968,37.291],[-80.3,37.51],[-80.294,37.69],[-79.724,38.364],[-79.648,38.594],[-79.314,38.413],[-78.996,38.851],[-78.87,38.764],[-78.404,39.169],[-78.35,39.465]],
  /* Washington */ [[-117.033,49],[-117.055,46.344],[-116.924,46.169],[-116.918,45.993],[-118.989,45.999],[-120.21,45.725],[-120.637,45.747],[-121.185,45.605],[-121.218,45.67],[-121.535,45.725],[-121.809,45.709],[-122.247,45.55],[-122.762,45.659],[-122.812,45.961],[-122.905,46.081],[-123.118,46.185],[-123.37,46.147],[-123.545,46.262],[-123.726,46.3],[-123.874,46.24],[-124.066,46.327],[-124.027,46.464],[-123.896,46.536],[-124.099,46.744],[-124.427,47.741],[-124.624,47.888],[-124.707,48.184],[-124.597,48.381],[-123.984,48.162],[-123.425,48.118],[-123.162,48.168],[-123.036,48.08],[-122.801,48.086],[-122.636,47.867],[-122.516,47.883],[-122.423,47.319],[-122.324,47.346],[-122.423,47.576],[-122.395,47.801],[-122.231,48.031],[-122.362,48.124],[-122.488,48.754],[-122.795,48.891],[-122.757,49],[-117.033,49]],
  /* West Virginia */ [[-80.519,40.637],[-80.519,39.722],[-79.478,39.722],[-79.489,39.207],[-79.095,39.47],[-78.963,39.438],[-78.766,39.585],[-78.47,39.514],[-78.432,39.624],[-78.268,39.618],[-78.174,39.695],[-77.835,39.602],[-77.72,39.322],[-77.829,39.131],[-78.35,39.465],[-78.404,39.169],[-78.87,38.764],[-78.996,38.851],[-79.314,38.413],[-79.648,38.594],[-79.724,38.364],[-80.294,37.69],[-80.3,37.51],[-80.968,37.291],[-81.225,37.236],[-81.362,37.34],[-81.68,37.203],[-81.849,37.285],[-81.986,37.455],[-81.97,37.537],[-82.293,37.668],[-82.501,37.931],[-82.622,38.123],[-82.594,38.424],[-82.331,38.446],[-82.293,38.578],[-82.173,38.632],[-82.222,38.786],[-82.036,39.027],[-81.888,38.873],[-81.784,38.966],[-81.811,39.082],[-81.685,39.273],[-81.57,39.268],[-81.455,39.41],[-81.346,39.344],[-81.22,39.388],[-80.831,39.711],[-80.601,40.319],[-80.666,40.582],[-80.519,40.637]],
  /* Wisconsin */ [[-90.415,46.568],[-90.229,46.508],[-90.12,46.338],[-88.663,45.988],[-88.531,46.021],[-88.104,45.922],[-87.781,45.676],[-87.792,45.5],[-87.885,45.364],[-87.65,45.342],[-87.743,45.199],[-87.589,45.095],[-87.628,44.975],[-87.819,44.953],[-88.044,44.564],[-87.929,44.537],[-87.611,44.838],[-87.403,44.914],[-87.239,45.166],[-87.031,45.221],[-87.047,45.09],[-87.469,44.553],[-87.54,44.159],[-87.644,44.104],[-87.737,43.879],[-87.704,43.688],[-87.912,43.249],[-87.885,43.003],[-87.765,42.784],[-87.803,42.494],[-90.64,42.51],[-90.711,42.636],[-91.067,42.751],[-91.177,43.134],[-91.056,43.255],[-91.204,43.354],[-91.242,43.775],[-91.434,43.994],[-91.878,44.202],[-91.927,44.334],[-92.234,44.443],[-92.338,44.553],[-92.546,44.569],[-92.809,44.75],[-92.76,45.287],[-92.645,45.44],[-92.771,45.566],[-92.886,45.577],[-92.869,45.72],[-92.639,45.933],[-92.294,46.076],[-92.294,46.667],[-92.091,46.749],[-91.79,46.694],[-90.837,46.957],[-90.75,46.886],[-90.886,46.755],[-90.415,46.568]],
  /* Wyoming */ [[-109.081,45.002],[-104.058,44.997],[-104.053,41.004],[-111.047,40.998],[-111.053,45.002],[-109.081,45.002]],
];
const STATE_LINES = US_STATE_BOUNDARIES.map((ring) => ring.map(([lon, lat]) => project(lon, lat)));

/* Great Lakes—real Natural Earth shoreline data (public domain), projected
   through the same lon/lat -> viewBox transform as everything else, so they
   stay registered with the state borders and terrain at every breakpoint. */
const GREAT_LAKES = [
  { name: "Lake Erie", ring: [[-83.105,42.286],[-83.076,42.275],[-83.108,42.071],[-82.834,41.997],[-82.645,42.037],[-82.512,41.932],[-82.462,42.058],[-82.412,42.103],[-81.874,42.315],[-81.887,42.257],[-81.867,42.258],[-81.73,42.447],[-81.404,42.63],[-81.039,42.662],[-80.094,42.546],[-80.062,42.554],[-80.105,42.572],[-80.451,42.613],[-80.22,42.774],[-79.747,42.858],[-79.386,42.881],[-79.057,42.862],[-78.922,42.924],[-79.04,43.021],[-79.066,43.106],[-78.895,43.058],[-78.924,42.998],[-78.878,42.799],[-79.209,42.555],[-79.704,42.3],[-80.334,42.041],[-81.229,41.77],[-81.723,41.511],[-82.121,41.493],[-82.48,41.398],[-82.643,41.467],[-83.027,41.455],[-82.703,41.529],[-82.831,41.58],[-82.963,41.542],[-83.456,41.742],[-83.362,41.88],[-83.177,42.049],[-83.175,42.146],[-83.105,42.286]] },
  { name: "Lake Ontario", ring: [[-75.787,44.484],[-75.776,44.459],[-75.832,44.397],[-76.352,44.106],[-76.306,44.043],[-76.173,44.079],[-76.154,44.045],[-76.192,44.003],[-76.071,43.998],[-76.198,43.874],[-76.236,43.871],[-76.233,43.895],[-76.285,43.869],[-76.218,43.758],[-76.203,43.575],[-76.473,43.507],[-76.722,43.343],[-76.885,43.314],[-76.963,43.264],[-77.271,43.295],[-77.529,43.267],[-77.838,43.357],[-78.175,43.395],[-78.689,43.361],[-79.338,43.198],[-79.728,43.253],[-79.773,43.301],[-79.569,43.578],[-79.303,43.69],[-79.061,43.835],[-77.726,44.009],[-77.682,44.045],[-77.549,44.025],[-77.568,44.008],[-77.503,43.963],[-77.259,43.964],[-77.315,43.947],[-77.267,43.922],[-77.19,43.948],[-77.208,43.876],[-77.054,43.883],[-76.879,43.943],[-76.87,43.965],[-77.044,43.952],[-76.863,44.1],[-77.103,44.046],[-77.117,44.079],[-77.083,44.148],[-77.115,44.173],[-77.271,44.139],[-77.296,44.098],[-77.332,44.104],[-77.333,44.153],[-77.586,44.084],[-77.547,44.124],[-77.059,44.205],[-77.02,44.197],[-77.033,44.156],[-77.01,44.153],[-76.913,44.198],[-77.055,44.106],[-77.041,44.071],[-76.992,44.074],[-76.688,44.218],[-76.408,44.262],[-75.942,44.411],[-75.831,44.49],[-75.787,44.484]] },
  { name: "Lake Michigan", ring: [[-84.763,45.792],[-84.981,45.767],[-84.955,45.743],[-84.977,45.707],[-85.088,45.605],[-85.088,45.507],[-84.93,45.413],[-85.223,45.356],[-85.376,45.277],[-85.389,45.033],[-85.549,44.772],[-85.494,44.994],[-85.534,44.976],[-85.593,44.816],[-85.623,44.804],[-85.647,44.838],[-85.566,45.207],[-85.617,45.193],[-85.858,44.962],[-85.91,44.976],[-86.058,44.916],[-86.126,44.748],[-86.259,44.699],[-86.242,44.551],[-86.296,44.331],[-86.521,44.055],[-86.449,43.924],[-86.438,43.813],[-86.542,43.637],[-86.329,43.224],[-86.222,42.9],[-86.26,42.531],[-86.407,42.224],[-86.632,41.911],[-86.798,41.789],[-87.159,41.646],[-87.356,41.643],[-87.515,41.721],[-87.695,42.084],[-87.797,42.211],[-87.812,42.543],[-87.781,42.753],[-87.888,43.012],[-87.902,43.245],[-87.801,43.538],[-87.7,43.724],[-87.729,43.851],[-87.7,43.977],[-87.603,44.125],[-87.535,44.174],[-87.512,44.23],[-87.527,44.386],[-87.337,44.776],[-87.42,44.866],[-87.604,44.847],[-87.788,44.652],[-87.969,44.552],[-88.019,44.571],[-88.028,44.618],[-87.952,44.764],[-87.83,44.942],[-87.627,45.032],[-87.593,45.178],[-87.114,45.692],[-86.982,45.917],[-86.993,45.79],[-86.938,45.7],[-86.754,45.859],[-86.563,45.883],[-86.559,45.806],[-86.691,45.701],[-86.69,45.657],[-86.622,45.623],[-86.527,45.753],[-86.351,45.831],[-86.272,45.937],[-86.123,45.969],[-85.914,45.941],[-85.615,46.02],[-85.479,46.108],[-85.386,46.109],[-84.972,45.989],[-84.757,45.863],[-84.763,45.792]] },
  { name: "Lake Superior", ring: [[-84.432,46.522],[-84.344,46.531],[-84.358,46.505],[-84.528,46.436],[-84.635,46.482],[-84.851,46.469],[-85.034,46.506],[-85.015,46.686],[-84.968,46.771],[-85.015,46.783],[-85.616,46.691],[-86.132,46.687],[-86.403,46.588],[-86.637,46.453],[-86.733,46.49],[-86.914,46.483],[-87.034,46.543],[-87.171,46.514],[-87.358,46.524],[-87.637,46.827],[-87.778,46.888],[-88.216,46.952],[-88.383,46.874],[-88.449,46.802],[-88.476,46.837],[-88.466,47.013],[-88.543,47.122],[-88.633,47.167],[-88.65,47.232],[-88.83,47.146],[-88.948,47.043],[-89.155,46.989],[-89.355,46.882],[-89.695,46.839],[-90.154,46.654],[-90.469,46.584],[-90.737,46.689],[-90.77,46.645],[-90.944,46.619],[-90.768,46.908],[-90.84,46.962],[-90.946,46.954],[-91.127,46.868],[-91.218,46.886],[-91.499,46.766],[-91.836,46.702],[-91.987,46.699],[-92.107,46.762],[-92.065,46.819],[-91.645,47.05],[-90.847,47.585],[-90.597,47.688],[-89.829,47.901],[-89.345,48.121],[-89.221,48.311],[-89.201,48.451],[-88.775,48.583],[-88.876,48.381],[-88.933,48.335],[-88.734,48.384],[-88.625,48.599],[-88.55,48.658],[-88.503,48.833],[-88.395,48.845],[-88.352,48.821],[-88.323,48.738],[-88.567,48.528],[-88.585,48.492],[-88.559,48.447],[-88.501,48.467],[-88.444,48.56],[-88.233,48.597],[-88.138,48.683],[-88.109,48.776],[-88.224,48.867],[-88.264,48.974],[-88.07,49.001],[-87.224,48.776],[-86.82,48.773],[-86.693,48.808],[-86.581,48.753],[-86.432,48.758],[-86.283,48.598],[-86.104,48.21],[-85.914,48.03],[-85.615,47.941],[-84.855,47.95],[-85.014,47.639],[-84.936,47.54],[-84.716,47.427],[-84.594,47.32],[-84.72,47.142],[-84.775,47.004],[-84.625,46.922],[-84.488,46.944],[-84.385,46.906],[-84.382,46.853],[-84.485,46.848],[-84.553,46.806],[-84.54,46.697],[-84.488,46.744],[-84.438,46.723],[-84.568,46.543],[-84.501,46.483],[-84.432,46.522]] },
  { name: "Lake Huron", ring: [[-84.344,46.531],[-84.165,46.563],[-84.087,46.507],[-84.092,46.361],[-83.459,46.238],[-83.025,46.181],[-82.719,46.207],[-82.638,46.172],[-82.35,46.193],[-82.196,46.134],[-81.769,46.104],[-81.765,46.058],[-81.615,46.119],[-81.542,46.075],[-81.64,46.04],[-81.597,46.012],[-81.627,45.978],[-81.597,45.967],[-81.449,45.994],[-81.237,45.974],[-81.174,46.016],[-81.16,45.955],[-81.097,45.942],[-80.818,45.951],[-80.511,45.59],[-80.444,45.584],[-80.414,45.62],[-80.35,45.399],[-80.177,45.344],[-80.176,45.397],[-80.057,45.375],[-80.05,45.301],[-80.121,45.223],[-80.049,45.204],[-80.006,45.153],[-80.086,45.109],[-79.902,44.957],[-79.828,44.936],[-79.772,44.819],[-79.696,44.875],[-79.709,44.791],[-79.678,44.769],[-79.827,44.768],[-79.925,44.813],[-79.93,44.863],[-80.108,44.807],[-79.997,44.652],[-80.008,44.572],[-80.085,44.489],[-80.46,44.578],[-80.593,44.639],[-80.67,44.727],[-80.79,44.7],[-80.91,44.618],[-80.895,44.781],[-80.963,44.809],[-81.13,44.77],[-81.006,44.876],[-80.973,44.97],[-81.074,44.914],[-81.134,44.918],[-81.138,44.968],[-81.26,45.028],[-81.313,45.156],[-81.285,45.241],[-81.697,45.251],[-81.706,45.223],[-81.59,45.166],[-81.451,45.015],[-81.355,44.978],[-81.358,44.911],[-81.278,44.79],[-81.278,44.644],[-81.402,44.461],[-81.565,44.36],[-81.708,44.131],[-81.732,44.06],[-81.713,43.474],[-81.785,43.327],[-82.181,43.085],[-82.417,43.017],[-82.461,43.081],[-82.663,43.854],[-82.716,43.939],[-82.878,44.048],[-82.995,44.061],[-83.383,43.924],[-83.353,43.879],[-83.469,43.731],[-83.573,43.697],[-83.668,43.621],[-83.848,43.664],[-83.938,43.738],[-83.879,43.963],[-83.614,44.058],[-83.512,44.271],[-83.463,44.265],[-83.331,44.375],[-83.3,44.785],[-83.328,44.869],[-83.452,44.992],[-83.426,45.059],[-83.279,45.047],[-83.331,45.164],[-83.396,45.221],[-83.397,45.274],[-83.479,45.339],[-83.918,45.486],[-84.073,45.509],[-84.242,45.644],[-84.441,45.676],[-84.763,45.792],[-84.757,45.863],[-84.713,45.871],[-84.725,45.958],[-84.632,46.053],[-84.521,45.995],[-83.979,45.962],[-83.901,45.981],[-83.917,46.021],[-84.041,46.088],[-84.055,46.159],[-84.248,46.203],[-84.202,46.283],[-84.255,46.408],[-84.358,46.505],[-84.344,46.531]] },
];
const GREAT_LAKES_PROJECTED = GREAT_LAKES.map((lake) => ({
  ...lake,
  ring: lake.ring.map(([lon, lat]) => project(lon, lat)),
}));

const GRATICULE_LINES = (() => {
  const lines = [];
  for (let lon = -120; lon <= -70; lon += 5) {
    lines.push([project(lon, GEO_BOUNDS.latMin), project(lon, GEO_BOUNDS.latMax)]);
  }
  for (let lat = 25; lat <= 49; lat += 5) {
    lines.push([project(GEO_BOUNDS.lonMin, lat), project(GEO_BOUNDS.lonMax, lat)]);
  }
  return lines;
})();

/* Range rings—nautical miles (the primary aviation measurement), scaled
   using the standard 60 NM-per-degree-of-latitude relationship (1 NM = 1
   arcminute of latitude, by definition) rather than a statute-mile
   approximation. Centered on the real origin coordinate above and never
   resized to fit a preferred composition. */
const NM_PER_DEG_LAT = 60;
const PX_PER_NM = (MAP_VB.h / (GEO_BOUNDS.latMax - GEO_BOUNDS.latMin)) / NM_PER_DEG_LAT;
const ACCESS_RINGS_NM = [500, 1000, PLANNING_RANGE_NM];
const DEFAULT_AIRPORT_CODE = "ATL";

/* Lazily fetches the full continental-US airport directory (public-domain
   OurAirports data, large + medium commercial/business-relevant airports —
   ~820 entries) the first time it's needed, so it never blocks the initial
   page paint. Cached at module scope so re-mounting the section doesn't
   re-fetch it. */
let airportDirectoryCache = null;
let airportDirectoryPromise = null;
const useAirportDirectory = (enabled) => {
  const [airports, setAirports] = useState(airportDirectoryCache || []);
  const [loading, setLoading] = useState(!airportDirectoryCache);
  useEffect(() => {
    if (!enabled || airportDirectoryCache) return;
    if (!airportDirectoryPromise) {
      airportDirectoryPromise = fetch("/data/us-airports.json").then((r) => r.json());
    }
    let cancelled = false;
    setLoading(true);
    airportDirectoryPromise
      .then((data) => { airportDirectoryCache = data; if (!cancelled) { setAirports(data); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled]);
  return { airports, loading };
};

/* Ranks the directory by city / airport-name / IATA / ICAO match, biasing
   toward exact-code and city-prefix matches, then toward major airports —
   deliberately restrained to the top 8 results rather than a long list. */
const searchAirports = (airports, query) => {
  // Strip diacritics so "cancun" matches "Cancún", "merida" matches "Mérida", etc.
  const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const q = norm(query.trim());
  if (!q) return [];
  const scored = [];
  for (const a of airports) {
    const city = norm(a.city);
    const name = norm(a.name);
    const iata = (a.iata || "").toLowerCase();
    const icao = (a.icao || "").toLowerCase();
    let score = -1;
    if (iata && iata === q) score = 100;
    else if (icao && icao === q) score = 95;
    else if (city.startsWith(q)) score = 90;
    else if (iata && iata.startsWith(q)) score = 85;
    else if (name.startsWith(q)) score = 70;
    else if (city.includes(q)) score = 50;
    else if (name.includes(q)) score = 40;
    else if (icao && icao.includes(q)) score = 35;
    if (score >= 0) scored.push({ ...a, score: score + (a.priority === "major" ? 5 : 0) });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 8);
};

/* Searchable airport combobox—ARIA combobox pattern (role="combobox" on
   the input, role="listbox"/"option" on results), full keyboard support,
   an explicit empty state, and results that show city, airport name, and
   code together so a visitor can tell airports apart at a glance. */
const AirportCombobox = ({ onSelect }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [everFocused, setEverFocused] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const { airports, loading } = useAirportDirectory(everFocused);

  // Mexico + Caribbean getaways are always searchable (they're hardcoded, so
  // they work even before the large U.S. directory finishes loading).
  const results = searchAirports([...GETAWAY_DESTINATIONS, ...airports], query);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const commit = (airport) => {
    onSelect(airport);
    setQuery("");
    setOpen(false);
    setActiveIndex(-1);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActiveIndex((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (activeIndex >= 0 && results[activeIndex]) commit(results[activeIndex]); }
    else if (e.key === "Escape") { setOpen(false); setActiveIndex(-1); }
  };

  return (
    <div className="airport-combobox" ref={wrapRef}>
      <label htmlFor="airport-search-input" className="airport-combobox__label access-label-shard">
        Where would you like to go?
      </label>
      <div className="airport-combobox__field">
        <svg className="airport-combobox__icon" width="15" height="15" viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          id="airport-search-input"
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="airport-search-listbox"
          aria-activedescendant={activeIndex >= 0 ? `airport-option-${activeIndex}` : undefined}
          aria-autocomplete="list"
          aria-label="Search airports by city, airport name, or code"
          autoComplete="off"
          placeholder="City, airport, or code (e.g. Denver, Aspen, DEN)"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIndex(-1); }}
          onFocus={() => { setOpen(true); setEverFocused(true); }}
          onKeyDown={onKeyDown}
          className="airport-combobox__input"
        />
      </div>
      {open && query.trim() && (
        <ul id="airport-search-listbox" role="listbox" className="airport-combobox__list" aria-label="Airport search results">
          {loading ? (
            <li className="airport-combobox__status">Loading airport directory…</li>
          ) : results.length === 0 ? (
            <li className="airport-combobox__status">No airports match “{query}.” Try a city or airport code.</li>
          ) : (
            results.map((a, i) => (
              <li
                key={`${a.iata || a.icao}-${a.lat}-${a.lon}`}
                id={`airport-option-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                className={`airport-combobox__option${i === activeIndex ? " is-active" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); commit(a); }}
                onMouseEnter={() => setActiveIndex(i)}
              >
                <span className="airport-combobox__option-code">{a.iata || a.icao}</span>
                <span className="airport-combobox__option-text">
                  <span className="airport-combobox__option-city">{a.city}{a.state ? `, ${a.state}` : ""}</span>
                  <span className="airport-combobox__option-name">{a.name}</span>
                </span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

/* Atmospheric dust motes for the empty upper-right of the section—a
   seeded, deterministic field (not Math.random() per render) so it doesn't
   reshuffle on re-render. Concentrated where the frame is otherwise bare
   black, with a few stragglers elsewhere so it reads as one room's worth
   of drifting air rather than a decal stuck in one corner. */
const ACCESS_DUST = (() => {
  const rand = (() => {
    let a = 0x9e3779b9;
    return () => {
      a |= 0; a = (a + 0x6d2b79f5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  })();
  const lerp = (a, b, t) => a + (b - a) * t;
  const motes = [];
  for (let i = 0; i < 16; i++) {
    motes.push({
      left: lerp(52, 97, rand()),
      top: lerp(2, 46, rand()),
      size: Math.round(lerp(2, 6, rand())),
      dx: Math.round(lerp(-30, 30, rand())),
      dy: Math.round(lerp(-40, -10, rand())),
      dur: Math.round(lerp(20, 38, rand())),
      delay: -Math.round(lerp(0, 30, rand())),
      opMax: lerp(0.35, 0.7, rand()),
    });
  }
  for (let i = 0; i < 6; i++) {
    motes.push({
      left: lerp(4, 48, rand()),
      top: lerp(3, 20, rand()),
      size: Math.round(lerp(2, 4, rand())),
      dx: Math.round(lerp(-24, 24, rand())),
      dy: Math.round(lerp(-30, -8, rand())),
      dur: Math.round(lerp(22, 36, rand())),
      delay: -Math.round(lerp(0, 30, rand())),
      opMax: lerp(0.25, 0.5, rand()),
    });
  }
  return motes;
})();

/* Animates between two already-known values (never computes a new one) —
   satisfies "animate the transition" without implying a live calculation. */
const useCountUp = (target, duration = 650) => {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  useEffect(() => {
    const reduce = typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setValue(target); fromRef.current = target; return; }
    if (fromRef.current === target) return;
    const from = fromRef.current;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
};

/* Dusk / night treatment, replacing the daytime satellite relief photo —
   the country reads as "alive" through its lit cities rather than through
   terrain texture, matching what a real nighttime view of the continent
   actually looks like. The base layer is real photography: NASA's Black
   Marble 2016 nighttime-lights composite (Suomi NPP VIIRS data, public
   domain, science.nasa.gov), cropped offline to this map's exact
   GEO_BOUNDS at true geographic proportions—the same "crop once, let
   preserveAspectRatio='none' do the non-uniform stretch into the 1000×600
   box" approach the old relief photo used, so it registers correctly
   against the state lines/lakes with no extra distortion logic needed.

   The photo asset itself is cropped a few degrees taller than GEO_BOUNDS
   on the north edge—a bit of Canada carries past the state-line map's
   own top border, the same way Mexico already carries past the bottom —
   so both edges read as "the country continues past our borders" rather
   than a hard photo-rectangle cutoff. PHOTO_* below describes exactly how
   much taller and derives the CSS offset/height and the mask-image fade
   stops from that, rather than hand-tuned magic percentages. */
const NIGHT_LIGHTS_IMAGE_URL = "/textures/conus-night-lights.jpg";
const PHOTO_EXTRA_NORTH_DEG = 5;
const PHOTO_EXTRA_EAST_DEG = 6;
const PHOTO_LAT_MAX = GEO_BOUNDS.latMax + PHOTO_EXTRA_NORTH_DEG;
const PHOTO_LAT_MIN = GEO_BOUNDS.latMin;
const PHOTO_LON_MAX = GEO_BOUNDS.lonMax + PHOTO_EXTRA_EAST_DEG;
const PHOTO_LON_MIN = GEO_BOUNDS.lonMin;
const PHOTO_TOTAL_DEG = PHOTO_LAT_MAX - PHOTO_LAT_MIN;
const PHOTO_TOTAL_LON_DEG = PHOTO_LON_MAX - PHOTO_LON_MIN;
const PHOTO_HEIGHT_PCT = (PHOTO_TOTAL_DEG / (GEO_BOUNDS.latMax - GEO_BOUNDS.latMin)) * 100;
const PHOTO_TOP_PCT = 100 - PHOTO_HEIGHT_PCT;
const PHOTO_WIDTH_PCT = (PHOTO_TOTAL_LON_DEG / (GEO_BOUNDS.lonMax - GEO_BOUNDS.lonMin)) * 100;
/* Every fade stop below is pinned to the real GEO_BOUNDS border, not a
   point inside it—the entire country, coastline included, stays at full
   brightness. Only the extra land/ocean beyond that real border (the
   Canada strip to the north, the Atlantic/Nova Scotia strip to the east)
   is where the fade actually happens, so it reads as "the country in full,
   then a runway of fading light" rather than the country itself dimming. */
const photoPctFromTop = (lat) => ((PHOTO_LAT_MAX - lat) / PHOTO_TOTAL_DEG) * 100;
const photoPctFromLeft = (lon) => ((lon - PHOTO_LON_MIN) / PHOTO_TOTAL_LON_DEG) * 100;
const PHOTO_FADE_TOP_SOLID = photoPctFromTop(GEO_BOUNDS.latMax);
const PHOTO_FADE_BOTTOM_START = photoPctFromTop(GEO_BOUNDS.latMin + 0.4);
const PHOTO_FADE_RIGHT_SOLID = photoPctFromLeft(GEO_BOUNDS.lonMax);

/* Southern extension below the map (Mexico/Central America/Cuba)—a
   separate real-photography crop, not a resize of the CONUS photo. Its
   CSS width/aspect-ratio are derived from these bounds at the SAME
   px-per-degree rate .access-mapplane already uses, so the geography
   underneath lines up with what's directly above it rather than being
   silently rescaled by object-fit. SOUTH_EXT_LON_MAX is deliberately
   GEO_BOUNDS' own lonSpan scaled by 100/70—exactly enough width to
   reach 100% of the section when .access-mapwrap itself is 70% of it. */
const SOUTH_EXT_LON_MIN = GEO_BOUNDS.lonMin;
const SOUTH_EXT_LON_MAX = GEO_BOUNDS.lonMin + (GEO_BOUNDS.lonMax - GEO_BOUNDS.lonMin) * (100 / 70);
const SOUTH_EXT_LAT_TOP = GEO_BOUNDS.latMin;
const SOUTH_EXT_LAT_BOTTOM = GEO_BOUNDS.latMin - 3.6; // as much of the wide northern mainland as the existing gap allows
const SOUTH_EXT_WIDTH_PCT = ((SOUTH_EXT_LON_MAX - SOUTH_EXT_LON_MIN) / (GEO_BOUNDS.lonMax - GEO_BOUNDS.lonMin)) * 100;
// Matches .access-mapplane's own non-uniform stretch (its 1000x600 SVG box
// is itself already a distortion of GEO_BOUNDS' true 59°x25.5° span) —
// deliberately NOT the crop's true/undistorted aspect ratio. Using true
// geography here made this extension's own vertical scale (px per degree
// of latitude) different from the map above it, which was the actual cause
// of Cuba reading as compressed toward Miami: same content, wrong amount
// of space between it and the seam. Matching the map's own stretch keeps
// relative distances consistent with everything above it.
const SOUTH_EXT_ASPECT =
  ((SOUTH_EXT_LON_MAX - SOUTH_EXT_LON_MIN) * (MAP_VB.w / (GEO_BOUNDS.lonMax - GEO_BOUNDS.lonMin))) /
  ((SOUTH_EXT_LAT_TOP - SOUTH_EXT_LAT_BOTTOM) * (MAP_VB.h / (GEO_BOUNDS.latMax - GEO_BOUNDS.latMin)));
// Northern extension (Canada)—same method as the southern one, mirrored.
// No extra width needed here: unlike the search panel that sits beside the
// bottom of the map, nothing needs to be reached "behind" at the top, and
// the search title's own protected area sits close enough to the map that
// reaching wider would risk it. Matches .access-mapwrap's own natural
// width exactly.
const NORTH_EXT_LON_MIN = GEO_BOUNDS.lonMin;
const NORTH_EXT_LON_MAX = GEO_BOUNDS.lonMax;
const NORTH_EXT_LAT_BOTTOM = GEO_BOUNDS.latMax;
const NORTH_EXT_LAT_TOP = GEO_BOUNDS.latMax + 3.0; // as far as the title/subtitle gap safely allows
const NORTH_EXT_ASPECT =
  ((NORTH_EXT_LON_MAX - NORTH_EXT_LON_MIN) * (MAP_VB.w / (GEO_BOUNDS.lonMax - GEO_BOUNDS.lonMin))) /
  ((NORTH_EXT_LAT_TOP - NORTH_EXT_LAT_BOTTOM) * (MAP_VB.h / (GEO_BOUNDS.latMax - GEO_BOUNDS.latMin)));
// .access-map__south-photo and .access-map__north-photo both live inside
// .access-mapplane now (for rotation continuity—see the JSX comments),
// which lives inside .access-mapmask. mapmask's own edge-fade mask is
// sized to its own box via mask-size:100% 100%, and CSS masks treat
// anything outside that box as fully transparent—so mapmask's box has
// to actually grow to encompass both extensions, or they just vanish.
// This is that combined height, in MAP_VB-equivalent units, expressed as
// mapmask's own aspect-ratio—and .access-mapplane's own top offset
// within that taller box, since it no longer simply sits at mapmask's own
// top edge now that there's a north extension above it too.
const SOUTH_EXT_HEIGHT_VB = (SOUTH_EXT_LAT_TOP - SOUTH_EXT_LAT_BOTTOM) * (MAP_VB.h / (GEO_BOUNDS.latMax - GEO_BOUNDS.latMin));
const NORTH_EXT_HEIGHT_VB = (NORTH_EXT_LAT_TOP - NORTH_EXT_LAT_BOTTOM) * (MAP_VB.h / (GEO_BOUNDS.latMax - GEO_BOUNDS.latMin));
const MAPMASK_TOTAL_HEIGHT_VB = NORTH_EXT_HEIGHT_VB + MAP_VB.h + SOUTH_EXT_HEIGHT_VB;
const MAPMASK_ASPECT = MAP_VB.w / MAPMASK_TOTAL_HEIGHT_VB;
const MAPPLANE_TOP_PCT = (NORTH_EXT_HEIGHT_VB / MAPMASK_TOTAL_HEIGHT_VB) * 100;

/* A handful of real major-metro coordinates get a soft bloom halo and a
   slow flicker on top of the photo—an accent tying the interactive tool
   (these are also FEATURED_DESTINATIONS/notable hubs) to the ambient
   photography, not an attempt to recreate the whole country's light field
   procedurally now that a real photo is doing that authentically. */
const NIGHT_LIGHT_ACCENTS = [
  { lat: 40.7128, lon: -74.0060 }, // New York
  { lat: 25.7617, lon: -80.1918 }, // Miami
  { lat: 33.7490, lon: -84.3880 }, // Atlanta
  { lat: 41.8781, lon: -87.6298 }, // Chicago
  { lat: 32.7767, lon: -96.7970 }, // Dallas
  { lat: 29.7604, lon: -95.3698 }, // Houston
  { lat: 39.7392, lon: -104.9903 }, // Denver
  { lat: 37.7749, lon: -122.4194 }, // San Francisco
];

const EastTexasAccess = ({ heroAside = null }) => {
  const [selected, setSelected] = useState(() =>
    FEATURED_DESTINATIONS.find((d) => d.code === DEFAULT_AIRPORT_CODE) || FEATURED_DESTINATIONS[0]
  );
  const [preview, setPreview] = useState(null);
  const active = preview || selected;

  // Continuous ambient loops (drift, recon scan) need to pause when the map
  // scrolls out of view and resume when it scrolls back—unlike the site's
  // shared one-shot reveal observer (which only ever adds .is-in-view once,
  // then unobserves), this toggles the class both ways. A direct classList
  // toggle via ref, not React state, so scrolling never triggers a re-render.
  const mapVisRef = useRef(null);
  useEffect(() => {
    const el = mapVisRef.current;
    if (!el) return;
    // Toggle on the mapwrap (drives the map's own ambient pause) AND on the
    // shared .access-stage ancestor, so the results panel—a sibling of the
    // map, not a descendant—can gate its own ambient breath/drift off the
    // same in-view signal and go quiet when the section is off-screen.
    const stage = el.closest(".access-stage");
    const io = new IntersectionObserver(
      ([entry]) => {
        el.classList.toggle("is-in-view", entry.isIntersecting);
        if (stage) stage.classList.toggle("is-in-view", entry.isIntersecting);
      },
      { threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const distanceNm = geodesicNM(EAST_TEXAS_ORIGIN.lat, EAST_TEXAS_ORIGIN.lon, active.lat, active.lon);
  const plan = estimateFlightPlan(distanceNm);
  const animatedNm = useCountUp(Math.round(distanceNm), 650);
  // Tenths-of-an-hour granularity—exactly what formatHours' own
  // toFixed(1) already displays, so tweening loses no precision on the
  // settled value, just interpolates smoothly toward it.
  const animatedHours = useCountUp(Math.round(plan.totalHours * 10), 650) / 10;
  const distanceMi = Math.round(distanceNm * NM_TO_MI);
  const formatHoursRange = ([lo, hi]) => `${lo.toFixed(1)}–${hi.toFixed(1)} hrs`;

  const activeIsFeatured = FEATURED_DESTINATIONS.some(
    (f) => f.code === active.code && f.lat === active.lat && f.lon === active.lon
  );

  const originPos = project(EAST_TEXAS_ORIGIN.lon, EAST_TEXAS_ORIGIN.lat);
  const activePos = project(active.lon, active.lat);
  const originPct = { left: (originPos.x / MAP_VB.w) * 100, top: (originPos.y / MAP_VB.h) * 100 };
  const nearestRingNm = ACCESS_RINGS_NM.reduce((a, b) => (Math.abs(b - distanceNm) < Math.abs(a - distanceNm) ? b : a));

  /* Getaways south/east of the continental-U.S. frame project OUTSIDE the map
     box. Clamp the VISUAL marker + route endpoint to just inside the frame
     edge (the panel's distance/time keep using the true coordinates via
     haversine, so they stay accurate) — the dashed vector then reads as
     pointing off toward the destination rather than a marker flung
     off-screen. */
  const CLAMP_MX = MAP_VB.w * 0.02, CLAMP_MY = MAP_VB.h * 0.022;
  const clampedActivePos = {
    x: Math.min(Math.max(activePos.x, CLAMP_MX), MAP_VB.w - CLAMP_MX),
    y: Math.min(Math.max(activePos.y, CLAMP_MY), MAP_VB.h - CLAMP_MY),
  };
  const isOffMap = Math.abs(clampedActivePos.x - activePos.x) > 0.5 || Math.abs(clampedActivePos.y - activePos.y) > 0.5;

  /* Route curve—bowed toward the projected midpoint of the real great-
     circle path (not an arbitrary decorative arc). For an off-map (clamped)
     destination the great-circle midpoint is off-frame too, so the curve is
     dropped for a clean straight vector to the frame edge. */
  const gcMid = greatCircleMidpoint(EAST_TEXAS_ORIGIN.lat, EAST_TEXAS_ORIGIN.lon, active.lat, active.lon);
  const gcMidPos = project(gcMid.lon, gcMid.lat);
  const routeCtrl = isOffMap
    ? { x: (originPos.x + clampedActivePos.x) / 2, y: (originPos.y + clampedActivePos.y) / 2 }
    : { x: 2 * gcMidPos.x - (originPos.x + activePos.x) / 2, y: 2 * gcMidPos.y - (originPos.y + activePos.y) / 2 };
  const routePath = `M ${originPos.x.toFixed(1)} ${originPos.y.toFixed(1)} Q ${routeCtrl.x.toFixed(1)} ${routeCtrl.y.toFixed(1)} ${clampedActivePos.x.toFixed(1)} ${clampedActivePos.y.toFixed(1)}`;

  /* Route crossfade — fadingRoute briefly keeps the PREVIOUS route path
     rendered (with a CSS fade-out) while the new dashed one fades in, so a
     reselect reads as a crossfade rather than an instant pop.
     prevRoutePathRef captures "what was on screen" via an effect that runs
     after every render, one render behind the value used to detect the
     change. */
  const [fadingRoute, setFadingRoute] = useState(null);
  const prevCodeRef = useRef(active.code);
  const prevRoutePathRef = useRef(routePath);
  useEffect(() => {
    if (prevCodeRef.current === active.code) return;
    setFadingRoute({ code: prevCodeRef.current, path: prevRoutePathRef.current });
    prevCodeRef.current = active.code;
    const t1 = setTimeout(() => setFadingRoute(null), 320);
    return () => { clearTimeout(t1); };
  }, [active.code]);
  useEffect(() => { prevRoutePathRef.current = routePath; }, [routePath]);

  return (
    <section className="access-section" data-testid="home-access-section" aria-label="East Texas flight access explorer">
      {/* Section-wide grain ties map, radar, and surrounding atmosphere into
          one visual material—deliberately fainter than the map's own
          grain layer so the two don't read as an identical repeating tile. */}
      <div className="access-section__grain" aria-hidden />
      {/* Atmospheric dust motes—pure decoration for the otherwise-bare
          black field, each drifting on its own slow, independent cycle so
          the room reads as filled with slow-moving air rather than a
          synchronized pattern. */}
      <div className="access-dust" aria-hidden>
        {ACCESS_DUST.map((m, i) => (
          <span
            key={i}
            className="access-dust__mote"
            style={{
              left: `${m.left}%`, top: `${m.top}%`,
              width: `${m.size}px`, height: `${m.size}px`,
              "--dx": `${m.dx}px`, "--dy": `${m.dy}px`,
              "--dur": `${m.dur}s`, "--delay": `${m.delay}s`,
              "--op-max": m.opMax,
            }}
          />
        ))}
      </div>
      <div className="access-top access-top--withaside">
        <div className="access-head">
          <h2 className="access-title">Everywhere Feels Closer with Steck Aviation.</h2>
          <p className="access-sub">
            See how the Beechjet 400A turns major destinations across the
            country into practical, time-saving connections from East Texas.
          </p>
        </div>
        {/* Collapsed Two Doors module — sits in the empty space beside the
            title on desktop, and stacks in-flow beneath it on mobile. Never
            fixed/sticky. Hidden until the experience has surfaced once. */}
        {heroAside ? <div className="access-aside">{heroAside}</div> : null}
      </div>
      <style>{`
        /* Compound selector (.access-top.access-top--withaside) so this beats the
           component's later base rule (.access-top { flex-direction: column }) at
           higher specificity, regardless of stylesheet source order. */
        .access-aside { margin-top: clamp(20px, 3vh, 30px); display: flex; justify-content: flex-start; }
        /* Phones use the fixed bottom-right tab instead of this in-flow module. */
        @media (max-width: 767px) { .access-aside { display: none; } }
        @media (min-width: 1025px) {
          .access-top.access-top--withaside {
            flex-direction: row; align-items: flex-start; justify-content: space-between;
            gap: clamp(28px, 4vw, 72px);
          }
          .access-top.access-top--withaside .access-head { flex: 0 1 660px; }
          .access-top.access-top--withaside .access-aside {
            margin-top: 0; flex: 0 0 auto; align-self: center; justify-content: flex-end;
          }
        }
      `}</style>

      <div className="access-stage">
        <div className="access-mapwrap" ref={mapVisRef} role="group" aria-label="Interactive map of flight times from East Texas">
          <div className="access-mapmask">
          <div className="access-mapplane">
          {/* Orbital drift—every piece of geographic content (photo,
              borders, rings, route, nodes, north/south extensions) lives
              inside this one wrapper so it all moves together as one rigid
              surface. Never touch .access-mapplane's own transform for
              this—that perspective/rotateX/rotateZ/scale is the hard-won
              3D tilt; this wrapper only adds a tiny translate on top of it. */}
          <div className="access-mapdrift">
            {/* Real nighttime photography—plain HTML img rather than an
                SVG-embedded <image>, so the browser scales it through its
                normal single-pass image pipeline instead of resampling it
                once into SVG viewBox space and again under the mapplane's
                3D transform, which is what was softening it. Unclipped —
                night ocean is already black, so it blends into the section
                background without needing a coastline mask. */}
            <img className="access-map__photo" src={NIGHT_LIGHTS_IMAGE_URL} alt="" aria-hidden loading="lazy"
              style={{ top: `${PHOTO_TOP_PCT}%`, height: `${PHOTO_HEIGHT_PCT}%`, width: `${PHOTO_WIDTH_PCT}%` }} />
            {/* One overlay, two stacked gradients (top/bottom + right),
                covering the exact same (taller AND wider than the box)
                area as the photo itself. Every fade stop is pinned to the
                real GEO_BOUNDS border (PHOTO_FADE_TOP_SOLID/RIGHT_SOLID),
                so the country itself—coastline included—never dims;
                only the extra Canada/Atlantic strip beyond it does. */}
            <div className="access-map__photo-fade" aria-hidden
              style={{ top: `${PHOTO_TOP_PCT}%`, height: `${PHOTO_HEIGHT_PCT}%`, width: `${PHOTO_WIDTH_PCT}%` }} />
            <svg className="access-map__land" viewBox={`0 0 ${MAP_VB.w} ${MAP_VB.h}`} aria-hidden preserveAspectRatio="xMidYMid meet">
              <defs>
                <clipPath id="us-clip"><path d={US_OUTLINE_PATH} /></clipPath>
                {/* Dusk base—warm plum/amber centered near the East Texas
                    origin, cooling to near-black indigo toward the edges,
                    so the country reads as lit from that one point outward
                    rather than evenly floodlit. */}
                <radialGradient id="access-land-fill" cx="46%" cy="63%" r="85%">
                  <stop offset="0%" stopColor="#3a2436" />
                  <stop offset="20%" stopColor="#28203a" />
                  <stop offset="48%" stopColor="#131230" />
                  <stop offset="76%" stopColor="#07071a" />
                  <stop offset="100%" stopColor="#03030a" />
                </radialGradient>
                <filter id="access-terrain-noise">
                  <feTurbulence type="fractalNoise" baseFrequency="0.045" numOctaves="3" seed="7" stitchTiles="stitch" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0.42  0 0 0 0 0.66  0 0 0 0 0.78  0 0 0 0.5 0" />
                </filter>
                <radialGradient id="city-glow-grad" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="rgba(224,196,140,0.55)" />
                  <stop offset="100%" stopColor="rgba(224,196,140,0)" />
                </radialGradient>
                <radialGradient id="lake-water-grad" cx="42%" cy="38%" r="75%">
                  <stop offset="0%" stopColor="#0c2432" />
                  <stop offset="55%" stopColor="#071620" />
                  <stop offset="100%" stopColor="#030a10" />
                </radialGradient>
                {/* Soft bloom for the accent lights—a blurred duplicate
                    behind the crisp core, rather than relying on the flat
                    photo pixels alone for that glow. */}
                <filter id="access-light-bloom" x="-200%" y="-200%" width="500%" height="500%">
                  <feGaussianBlur stdDeviation="1.6" />
                </filter>
              </defs>
              {/* Atmospheric coastline halo—was two passes (a 14px/
                  blur(16px) outer rim plus this tighter one), but that wide
                  outer pass bleeds a long way past the actual coastline. On
                  edges that sit against real black void (open ocean, no
                  mask covering for it the way the right edge has) that
                  bleed reads as a wide, hazy "missing" band over real photo
                  detail rather than a subtle rim light. Confirmed by
                  isolation testing—dropping just that one pass, keeping
                  this tighter glow, resolves it. */}
              <path d={US_OUTLINE_PATH} fill="none" stroke="rgba(150,210,255,0.4)" strokeWidth="5" style={{ filter: "blur(5px)" }} />
              {/* Fill removed—the photo now sits behind this whole SVG and
                  shows through; this path is kept only for its crisp
                  coastline stroke. */}
              <path d={US_OUTLINE_PATH} fill="none" stroke="rgba(155,192,214,0.56)" strokeWidth="1.35" />
              <g clipPath="url(#us-clip)">
                {/* The plum "dusk" color-grade rect that used to live here
                    (fill=access-land-fill, mix-blend:color, opacity 0.4) was
                    removed—it recolored the whole country ~40% toward
                    plum/indigo, which read as a purple "wash" that bleached
                    the real photo's natural deep-blue night look and, more
                    tellingly, did NOT reach the appended Mexico extension
                    (a separate <img> outside this clip), so the country sat
                    visibly more purple than the land just south of it. The
                    richer, truer look is now carried by a tone filter on
                    the photo itself (.access-map__photo) matching the one on
                    .access-map__south-photo, so map and extension read as
                    one continuous photograph. */}

                {/* A few real major-metro coordinates get a bloom halo and
                    slow flicker on top of the photo—see
                    NIGHT_LIGHT_ACCENTS above. */}
                {NIGHT_LIGHT_ACCENTS.map((c, i) => {
                  const pos = project(c.lon, c.lat);
                  return (
                    <g key={`accent${i}`} className="access-night-light is-major">
                      <circle cx={pos.x} cy={pos.y} r="5" fill="rgba(255,221,160,0.8)" filter="url(#access-light-bloom)" />
                      <circle cx={pos.x} cy={pos.y} r="1.4" fill="#fff6e2" />
                    </g>
                  );
                })}

                {/* Great Lakes—dark night water, punched back in as their
                    own layer, on top of the dusk landmass, below the state
                    grid. */}
                {GREAT_LAKES_PROJECTED.map((lake) => (
                  <polygon key={lake.name} points={lake.ring.map((p) => `${p.x},${p.y}`).join(" ")}
                    fill="url(#lake-water-grad)" stroke="rgba(70,140,170,0.35)" strokeWidth="0.4" />
                ))}

                {GRATICULE_LINES.map(([a, b], i) => (
                  <line key={`grat${i}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="rgba(140,200,220,0.08)" strokeWidth="0.6" />
                ))}
                {/* Glowing state-boundary grid—a dark under-stroke first so
                    the bright line stays crisp and legible over both the
                    varied terrain and the lake shorelines without doubling
                    or blowing out into a neon line. */}
                {STATE_LINES.map((ring, i) => (
                  <polyline key={`state-under${i}`} points={ring.map((p) => `${p.x},${p.y}`).join(" ")}
                    fill="none" stroke="rgba(4,10,14,0.55)" strokeWidth="1.3" />
                ))}
                {STATE_LINES.map((ring, i) => (
                  <polyline key={`state${i}`} points={ring.map((p) => `${p.x},${p.y}`).join(" ")}
                    fill="none" stroke="rgba(155,228,248,0.66)" strokeWidth="0.7"
                    style={{ filter: "drop-shadow(0 0 1px rgba(120,225,250,0.35))" }} />
                ))}
                {/* A few restrained traveling highlights, not all borders —
                    "life" stays secondary to destinations. Three, not one,
                    each on its own independent stagger so they read as a
                    quiet network occasionally active at different points,
                    never as a single repeating gesture or a sweep. */}
                {[0, 3, 7].map((idx, i) => (
                  STATE_LINES[idx] && (
                    <polyline key={idx} className={`access-state-pulse access-state-pulse--${i}`}
                      points={STATE_LINES[idx].map((p) => `${p.x},${p.y}`).join(" ")}
                      fill="none" stroke="rgba(103,232,249,0.85)" strokeWidth="1.1" strokeLinecap="round" />
                  )
                ))}

                {CONTEXT_CITIES.map((c) => {
                  const pos = project(c.lon, c.lat);
                  return (
                    <g key={c.city} className="access-context-city">
                      <circle cx={pos.x} cy={pos.y} r="5.5" fill="url(#city-glow-grad)" />
                      <circle cx={pos.x} cy={pos.y} r="0.85" fill="rgba(230,208,164,0.75)" />
                      {c.landmark && (
                        <g transform={`translate(${pos.x - 7},${pos.y - 15})`} style={{ color: "rgba(210,186,138,0.6)" }}>
                          <LandmarkGlyph city={c.city} />
                        </g>
                      )}
                    </g>
                  );
                })}

                {TRANSIENT_BLIPS.map((b, i) => {
                  const pos = project(b.lon, b.lat);
                  return (
                    <g key={b.id} className={`access-transient-blip access-transient-blip--${i}`}
                      style={{ transformOrigin: `${pos.x}px ${pos.y}px` }}>
                      <circle cx={pos.x} cy={pos.y} r="1.4" fill="rgba(103,232,249,0.7)" />
                      <path d={`M${pos.x - 5},${pos.y} h3 M${pos.x + 2},${pos.y} h3 M${pos.x},${pos.y - 5} v3 M${pos.x},${pos.y + 2} v3`}
                        stroke="rgba(103,232,249,0.55)" strokeWidth="0.6" />
                    </g>
                  );
                })}
              </g>
            </svg>
            <svg className="access-map__rings" viewBox={`0 0 ${MAP_VB.w} ${MAP_VB.h}`} aria-hidden preserveAspectRatio="xMidYMid meet">
              {ACCESS_RINGS_NM.map((nm) => {
                const r = nm * PX_PER_NM;
                const emphasized = nm === nearestRingNm;
                const isPlanningRing = nm === PLANNING_RANGE_NM;
                const labelY = originPos.y - r - 8;
                /* Large rings can bow well above the map's own top edge —
                   intentional, so the arc itself is never cut off mid-curve.
                   But once a label would drift up into the empty space
                   above the map (behind the headline), it reads as clutter
                   rather than context, so only the ring line is drawn there;
                   the number stays attached to rings that land back over
                   visible ground. */
                return (
                  <g key={nm}>
                    {/* Dark under-stroke first, same technique already used
                        for the state-boundary grid—a thin, low-opacity
                        ring reads fine over dark rural terrain but nearly
                        vanishes over the brighter, denser city-light
                        clusters further east. This backing keeps it legible
                        regardless of what's underneath, rather than being a
                        stacking/z-index problem. */}
                    <circle cx={originPos.x} cy={originPos.y} r={r} fill="none"
                      stroke="rgba(4,10,14,0.6)" strokeWidth={emphasized ? 3.2 : 2.4}
                      strokeDasharray={isPlanningRing ? "2 6" : "4 7"} className="access-map__ring-line" />
                    <circle className="access-map__ring-line" cx={originPos.x} cy={originPos.y} r={r} fill="none"
                      stroke={emphasized ? "rgba(103,232,249,0.5)" : "rgba(160,180,200,0.14)"}
                      strokeWidth={emphasized ? 1.7 : 1} strokeDasharray={isPlanningRing ? "2 6" : "4 7"} />
                    {labelY > -16 && (
                      <text x={originPos.x} y={labelY} fontSize="12" textAnchor="middle"
                        fill={emphasized ? "rgba(103,232,249,0.72)" : "rgba(160,180,200,0.34)"}
                        fontFamily="'IBM Plex Mono', monospace" letterSpacing="0.5">
                        {isPlanningRing ? `${nm.toLocaleString()} NM · PLANNING RANGE` : `${nm.toLocaleString()} NM`}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>

            {/* The state lines/coastline halo never extend past the real
                GEO_BOUNDS border in the first place—unlike the photo,
                there's no Canada/Atlantic extension of this data to fade
                into—so they're left completely unfaded here. The
                country's outline stays fully visible and fully bright all
                the way to its real edge; only the photo (which does have
                that extra runway) fades. */}
            {/* Faint atmospheric remnant—the only light allowed to soften
                past the coastline, and even this is hard-bounded by the
                section's own overflow:clip so it can never reach content
                below the map. */}
            <div className="access-map__halo" style={{ left: `${originPct.left}%`, top: `${originPct.top}%` }} aria-hidden />

            <svg className="access-map__route" viewBox={`0 0 ${MAP_VB.w} ${MAP_VB.h}`} aria-hidden preserveAspectRatio="xMidYMid meet">
              {/* Previous route, briefly held and fading—overlaps the new
                  route's own draw-in so a reselect reads as a crossfade
                  rather than an instant pop. Already fully drawn (no
                  dasharray trick needed), just a plain opacity fade-out. */}
              {fadingRoute && (
                <path key={`route-fade-${fadingRoute.code}`} className="access-map__route-line--fading"
                  d={fadingRoute.path} />
              )}
              {/* A constant dashed vector to the destination — same dashed
                  vocabulary as the range rings, no traveling "signal packet"
                  (that read as a missile firing at the city). It simply
                  appears and holds for as long as the destination is selected. */}
              <path key={`route-${active.code}`} className="access-map__route-line" d={routePath} />
              {/* Four targeting brackets—snap inward around the
                  destination once the route finishes drawing. Single group
                  transform (not four independent ones) scaling down to its
                  resting size around activePos reads as one clean "lock",
                  not four separate movements. */}
              <g key={`brackets-${active.code}`} className="access-target-brackets"
                style={{ transformBox: "view-box", transformOrigin: `${clampedActivePos.x}px ${clampedActivePos.y}px` }}>
                {[[-1,-1],[1,-1],[-1,1],[1,1]].map(([sx, sy], i) => {
                  const R = 11, A = 5;
                  const cx = clampedActivePos.x + sx * R, cy = clampedActivePos.y + sy * R;
                  const d = `M ${(cx - sx * A).toFixed(1)} ${cy.toFixed(1)} L ${cx.toFixed(1)} ${cy.toFixed(1)} L ${cx.toFixed(1)} ${(cy - sy * A).toFixed(1)}`;
                  return <path key={i} d={d} className="access-target-brackets__mark" />;
                })}
              </g>
            </svg>

            <div className="access-hub" style={{ left: `${originPct.left}%`, top: `${originPct.top}%` }} aria-hidden>
              {/* Origin reticle-tighten—a quick one-shot cinch that plays
                  at the very start of every acquisition, remounted via key
                  so it re-fires on every selection change. */}
              <span key={`reticle-${active.code}`} className="access-hub__reticle" />
              <span className="access-hub__pulse" />
              <span className="access-hub__ring" />
              <span className="access-hub__dot" />
            </div>

            {FEATURED_DESTINATIONS.map((d) => {
              const pos = project(d.lon, d.lat);
              const leftPct = (pos.x / MAP_VB.w) * 100;
              const topPct = (pos.y / MAP_VB.h) * 100;
              const isActive = activeIsFeatured && d.code === active.code;
              return (
                <button
                  key={d.code}
                  type="button"
                  className={`access-node${isActive ? " is-active" : ""}`}
                  style={{ left: `${leftPct}%`, top: `${topPct}%` }}
                  aria-pressed={d.code === selected.code}
                  aria-label={`${d.city}, ${d.state}: ${d.name}`}
                  onMouseEnter={() => setPreview(d)}
                  onMouseLeave={() => setPreview(null)}
                  onFocus={() => setPreview(d)}
                  onBlur={() => setPreview(null)}
                  onClick={() => { setSelected(d); setPreview(null); }}
                >
                  <span className="access-node__hit" />
                  <span className="access-node__dot" />
                  {isActive && <span key={`arrive-${active.code}`} className="access-node__pulse" />}
                  <span className="access-node__code">{d.code}</span>
                </button>
              );
            })}
            {!activeIsFeatured && (
              <div
                className="access-node access-node--search is-active"
                style={{ left: `${(clampedActivePos.x / MAP_VB.w) * 100}%`, top: `${(clampedActivePos.y / MAP_VB.h) * 100}%` }}
                aria-hidden
              >
                <span className="access-node__dot" />
                <span key={`arrive-${active.code}`} className="access-node__pulse" />
                <span className="access-node__code">{active.code}</span>
              </div>
            )}
            {/* Purely additive: attaches at top:100% of .access-mapplane
                itself, exactly wherever the map's own bottom edge already
                falls—never a hardcoded position, can't shift anything
                above it. Deliberately a CHILD of .access-mapplane, not a
                sibling—.access-mapplane carries a 3D transform at the
                desktop breakpoint (perspective + rotateX + rotateZ tilt).
                A sibling sitting outside that transform stays perfectly
                flat while the map above it is rotated a few degrees,
                which put the two surfaces at a slightly different angle
                at the seam—no amount of crop precision fixes a seam
                that's geometrically crooked. As a child, this inherits
                the exact same transform and rides along as one continuous
                rigid surface. Real photography (same NASA Black Marble
                source, a wider/further-south crop), not a stretch of the
                existing image. Widens past .access-mapplane's own 70%-of-
                stage column at the desktop breakpoint only (see media
                query) to reach the full section width, behind the search
                panel, which sits above it in stacking order. */}
            <img className="access-map__south-photo" aria-hidden alt=""
              src="/textures/conus-night-lights-south-ext.jpg" />
            {/* Mirrors .access-map__south-photo exactly: a child of
                .access-mapplane for the same rotation-continuity reason,
                positioned at bottom:100% (its own bottom edge lands
                exactly at mapplane's top—never a hardcoded number) with
                a matching overlap + crossfade at that seam. Its own outer
                fade (top edge, away from the map) is what dissolves
                before reaching the title/subtitle above. */}
            <img className="access-map__north-photo" aria-hidden alt=""
              src="/textures/conus-night-lights-north-ext.jpg" />
            {/* The top-fade above is smooth in brightness (verified pixel
                by pixel), but the region it's fading through still reads
                as an edge: above it is a perfectly flat, textureless
                gradient; below it, rich photographic grain/detail begins.
                The eye segments "flat" from "textured" independently of
                matching brightness—a smooth alpha ramp alone can't hide
                that boundary. This patch adds a little of the same
                turbulence-noise texture already used for
                .access-section__grain, faded in over exactly the same
                zone, so there's no flat-to-textured jump left to see —
                only smooth-and-grainy easing into detailed-and-grainy. */}
            <div className="access-map__north-fade-grain" aria-hidden />
            {/* Whole-surface "breathing"—sits on top of everything else
                in .access-mapdrift, but is a flat, uniform wash with no
                internal gradient of its own (just its overall opacity
                animating), so there's no edge inside it for the 3D tilt to
                compress into a hard line the way every narrow band tried
                here has. Reads as the whole terrain very slowly settling,
                not a moving light or scan. */}
            <div className="access-map__breathe" aria-hidden />
          </div>
          </div>
          </div>
          {/* Unmasked "shine" layer—a duplicate of just the coastline
              halo + state-boundary glow lines (not the photo, fills, city
              lights, or anything else), positioned and transformed exactly
              like .access-mapplane, but living OUTSIDE .access-mapmask so
              it never passes through its mask. Any right-edge (or other)
              fade applied to .access-mapmask dims the photo/fill layer
              underneath without ever touching this glow—the custom mask
              on the base layer can be as aggressive as it needs to be
              around the northeast corner without the coastline losing its
              shine. */}
          <div className="access-mapglow" aria-hidden>
            {/* Mirrors .access-mapdrift exactly (same orbital-drift animation,
                same in-view gating). The masked base layer's borders/rings
                live inside .access-mapdrift and drift a few px; this glow
                layer duplicates those same borders/rings, so if it DOESN'T
                drift in lockstep the two copies separate mid-cycle and every
                line reads as double. This wrapper keeps them perfectly
                superimposed at every moment of the drift. */}
            <div className="access-glowdrift">
            <svg viewBox={`0 0 ${MAP_VB.w} ${MAP_VB.h}`} preserveAspectRatio="xMidYMid meet">
              <defs>
                <clipPath id="us-clip-glow"><path d={US_OUTLINE_PATH} /></clipPath>
              </defs>
              {/* No blurred passes at all here—this layer is always drawn
                  at full strength (never masked), and the original inside
                  .access-mapplane already draws its own wide halo AND
                  mid-glow everywhere this layer overlaps it. Any blurred
                  stroke duplicated on top of that doubles up into a visible
                  haze over real photo detail (confirmed: even just the
                  5px/blur(5px) mid-glow pass alone was enough to wash out
                  the coastline). Just the crisp core line—the only thing
                  that actually needs to survive an aggressive mask. */}
              <path d={US_OUTLINE_PATH} fill="none" stroke="rgba(155,192,214,0.56)" strokeWidth="1.35" />
              <g clipPath="url(#us-clip-glow)">
                {STATE_LINES.map((ring, i) => (
                  <polyline key={`glow-state-under${i}`} points={ring.map((p) => `${p.x},${p.y}`).join(" ")}
                    fill="none" stroke="rgba(4,10,14,0.55)" strokeWidth="1.3" />
                ))}
                {STATE_LINES.map((ring, i) => (
                  <polyline key={`glow-state${i}`} points={ring.map((p) => `${p.x},${p.y}`).join(" ")}
                    fill="none" stroke="rgba(155,228,248,0.66)" strokeWidth="0.7"
                    style={{ filter: "drop-shadow(0 0 1px rgba(120,225,250,0.35))" }} />
                ))}
              </g>
              {/* Range rings, duplicated—these are diagrammatic/
                  informational, not photographic detail, so unlike the
                  terrain they should never fade away just because they
                  cross into .access-mapmask's edge/corner masking. Same
                  className as the originals (.access-map__ring-line) so
                  the dash-drift animation stays in sync between the two —
                  visually identical, just immune to the mask. */}
              {ACCESS_RINGS_NM.map((nm) => {
                const r = nm * PX_PER_NM;
                const emphasized = nm === nearestRingNm;
                const isPlanningRing = nm === PLANNING_RANGE_NM;
                return (
                  <g key={`glow-ring-${nm}`}>
                    <circle cx={originPos.x} cy={originPos.y} r={r} fill="none"
                      stroke="rgba(4,10,14,0.6)" strokeWidth={emphasized ? 3.2 : 2.4}
                      strokeDasharray={isPlanningRing ? "2 6" : "4 7"} className="access-map__ring-line" />
                    <circle className="access-map__ring-line" cx={originPos.x} cy={originPos.y} r={r} fill="none"
                      stroke={emphasized ? "rgba(103,232,249,0.5)" : "rgba(160,180,200,0.14)"}
                      strokeWidth={emphasized ? 1.7 : 1} strokeDasharray={isPlanningRing ? "2 6" : "4 7"} />
                  </g>
                );
              })}
            </svg>
            </div>
          </div>
        </div>

        <div className="access-side">
          <div className="access-controls">
            <AirportCombobox
              onSelect={(a) => {
                setSelected({ city: a.city, code: a.iata || a.icao, name: a.name, state: a.state, lat: a.lat, lon: a.lon });
                setPreview(null);
              }}
            />
            <p className="access-controls__hint">Choose an airport to estimate your connection from East Texas.</p>
            <div className="access-featured" role="listbox" aria-label="Featured destinations">
              {FEATURED_DESTINATIONS.map((d) => (
                <button
                  key={d.code}
                  type="button"
                  role="option"
                  aria-selected={d.code === selected.code}
                  className={`access-featured__chip${activeIsFeatured && d.code === active.code ? " is-active" : ""}`}
                  onMouseEnter={() => setPreview(d)}
                  onMouseLeave={() => setPreview(null)}
                  onFocus={() => setPreview(d)}
                  onBlur={() => setPreview(null)}
                  onClick={() => { setSelected(d); setPreview(null); }}
                >
                  {d.city} <em>{d.code}</em>
                </button>
              ))}
            </div>
          </div>

          <div className="access-panel" aria-live="polite">
            {/* Slow diagonal light-sweep across the glass — gives the card
                visible-at-a-glance life so it doesn't read as a flat panel,
                clipped to the panel by its own overflow:hidden. */}
            <span className="access-panel__sheen" aria-hidden />
            {/* Data-settle scanline—a single cyan line sweeps down the
                panel once each time a destination resolves, keyed to
                active.code so it re-fires on every change. Delayed to land
                just after the number tweens finish, so it reads as the
                readout refreshing in time with the map's targeting lock,
                not as decoration. */}
            <span key={`scan-${active.code}`} className="access-panel__settle" aria-hidden />
            <div className="access-panel__code-row">
              <span className="access-panel__code">{active.code}</span>
              <span className={`access-panel__status${plan.withinRange ? "" : " is-stop"}`}>
                {plan.withinRange ? "Direct access from East Texas" : "Fuel stop may be required"}
              </span>
            </div>
            <h3 className="access-panel__city">{active.city}{active.state ? `, ${active.state}` : ""}</h3>
            <p className="access-panel__airport">{active.name}</p>
            <div className="access-panel__stats">
              <div className="access-panel__stat">
                <span className="access-panel__value">
                  {plan.withinRange ? formatHours(animatedHours) : formatHoursRange(plan.stopRangeHours)}
                </span>
                <span className="access-panel__label">Est. flight time</span>
              </div>
              <div className="access-panel__stat">
                <span className="access-panel__value">{animatedNm.toLocaleString()} NM</span>
                <span className="access-panel__label">Great-circle distance · {distanceMi.toLocaleString()} mi</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="access-disclaimer">
        {/* Plain in-flow text actually paints BEFORE a position:absolute
            sibling at the same stacking level, regardless of DOM order —
            the opposite of what you'd guess. Without this wrapper, the
            fade curtain (a positioned ::before) was painting on top of the
            text itself, not behind it. Making the text its own positioned
            (position:relative, no explicit z-index) element puts it back
            in DOM-order stacking company with the ::before, where being
            later in the DOM correctly wins. */}
        <span style={{ position: "relative" }}>
          Flight times and distances are estimates for general planning purposes
          only and may vary based on routing, winds, weather, air traffic, airport
          conditions, fuel requirements, reserves, and other operational factors.
          Passenger count, baggage, and total aircraft loading may affect
          range, flight time, fuel stops, and nonstop capability. All flights
          remain subject to aircraft performance limitations and the judgment
          of the pilot in command.
        </span>
      </p>

      <style>{`
        /* Two shared motion curves so the whole instrument speaks one tempo
           language. --ease-decisive (fast commit, gentle settle) is the
           dominant curve already used across the sections below the map; the
           map/panel acquisition motion is unified onto it here. --ease-orbital
           is a symmetric sine in-out for every ambient loop (drift, breathe,
           float, panel breath) so nothing at rest ever reads as mechanical. */
        :root {
          --ease-decisive: var(--ease-decisive);
          --ease-orbital: cubic-bezier(.37,0,.63,1);
        }
        .access-section {
          position: relative;
          background: #050607;
          border-top: 1px solid #000;
          /* Bottom padding pulled back in now that the ring is allowed to
             genuinely cross the section boundary (see overflow below): with
             a hard clip we needed a big buffer to fake depth inside the
             section; now the outermost planning ring drifts past the section
             edge for real, into the next section, so the section itself only
             needs modest breathing room under the disclaimer. */
          padding: clamp(48px,6vw,72px) 0 clamp(24px,4vw,52px);
          /* clip horizontally (keeps the wide south crop / west edge from
             spilling sideways) but VISIBLE vertically, so the large range
             rings can bleed down out of this section and into the black of
             the one below for depth, instead of being hard-cut at the edge. */
          overflow: clip visible;
        }
        .access-section::before {
          content: ""; position: absolute; inset: 0; pointer-events: none;
          background: radial-gradient(ellipse 60% 50% at 72% 38%, rgba(78,112,158,0.14) 0%, transparent 70%);
        }
        .access-section__grain {
          position: absolute; inset: 0; z-index: 1; pointer-events: none;
          background-image: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23g)'/%3E%3C/svg%3E");
          background-size: 240px 240px; mix-blend-mode: overlay; opacity: 0.03;
        }
        .access-dust {
          position: absolute; inset: 0; z-index: 1; overflow: hidden; pointer-events: none;
        }
        .access-dust__mote {
          position: absolute; border-radius: 50%;
          background: radial-gradient(circle, rgba(205,222,240,0.9) 0%, rgba(205,222,240,0.25) 45%, rgba(205,222,240,0) 75%);
          filter: blur(0.4px);
          opacity: var(--op-min, 0.12);
          animation: access-dust-drift var(--dur,28s) var(--ease-orbital) infinite;
          animation-delay: var(--delay, 0s);
          will-change: transform, opacity;
        }
        @keyframes access-dust-drift {
          0%   { transform: translate(0,0);                    opacity: var(--op-min, 0.12); }
          50%  { transform: translate(var(--dx,20px), var(--dy,-28px)); opacity: var(--op-max, 0.5); }
          100% { transform: translate(0,0);                    opacity: var(--op-min, 0.12); }
        }
        @media (prefers-reduced-motion: reduce) {
          .access-dust__mote { animation: none; opacity: 0.22; }
        }
        .access-head {
          position: relative; top: -28px; z-index: 4;
          max-width: 620px; margin: 0 0 clamp(20px,3vh,32px);
          padding: 0 clamp(20px,5vw,80px);
          animation: access-head-float 8s var(--ease-orbital) infinite;
        }
        @keyframes access-head-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-7px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .access-head { animation: none; }
        }
        .access-title {
          font-family: 'Fraunces', serif; font-weight: 800;
          font-size: clamp(36px,5.6vw,84px); color: #fff; line-height: 1.12;
          margin: 12px 0 20px; letter-spacing: -0.02em;
        }
        .access-sub {
          color: rgba(255,255,255,0.68); font-size: clamp(16px,1.25vw,18px);
          line-height: 1.75; max-width: 560px; margin: 0;
        }

        .access-top {
          display: flex; flex-direction: column;
        }
        .access-controls {
          position: relative; z-index: 4;
          max-width: 620px; margin: 0 0 clamp(28px,4vh,40px);
          padding: 0 clamp(20px,5vw,80px);
          display: flex; flex-direction: column; gap: 14px;
        }
        .access-controls__hint {
          margin: -6px 0 0; font-size: 13px; color: rgba(255,255,255,0.45);
          font-family: 'IBM Plex Mono', monospace; letter-spacing: 0.02em;
        }

        .airport-combobox { position: relative; }
        .airport-combobox__label {
          position: relative; z-index: 1; display: block;
          font-family: 'Fraunces', serif; font-style: italic; font-weight: 500;
          font-size: clamp(34px,4.6vw,58px); line-height: 1.03; letter-spacing: -0.01em;
          text-transform: none; max-width: 280px;
          /* Always opaque—the reveal is done entirely by COLOR, driven from
             JS on scroll (var(--label-color) ramps near-black -> silver as the
             prompt rises through the viewport). The title thus materializes
             out of the darkness as you scroll rather than fading via opacity.
             A previous opacity animation (view-timeline, then .is-in-view)
             was unreliable for this absolutely-positioned label and left it
             stuck invisible at opacity 0—so opacity is now simply 1 and the
             color owns the whole effect. */
          color: var(--label-color, #0e1013);
          opacity: 1;
          margin-bottom: 18px;
        }
        .airport-combobox__field {
          position: relative; display: flex; align-items: center;
          background: rgba(255,255,255,0.04); border: 1px solid rgba(160,180,200,0.22);
          border-radius: 10px; padding: 0 14px;
          transition: border-color 200ms ease, background 200ms ease;
        }
        .airport-combobox__field:focus-within {
          border-color: rgba(103,232,249,0.55); background: rgba(255,255,255,0.06);
        }
        .airport-combobox__icon { color: rgba(255,255,255,0.4); flex: 0 0 auto; margin-right: 10px; }
        .airport-combobox__input {
          flex: 1 1 auto; background: none; border: none; outline: none;
          color: #fff; font-size: 15px; padding: 13px 0; font-family: inherit;
        }
        .airport-combobox__input::placeholder { color: rgba(255,255,255,0.32); }
        .airport-combobox__list {
          position: absolute; top: calc(100% + 8px); left: 0; right: 0; z-index: 20;
          background: rgba(10,13,16,0.97); backdrop-filter: blur(20px) saturate(140%);
          border: 1px solid rgba(160,180,200,0.2); border-radius: 10px;
          max-height: 320px; overflow-y: auto; padding: 6px; margin: 0;
          list-style: none; box-shadow: 0 30px 70px -20px rgba(0,0,0,0.65);
        }
        .airport-combobox__status {
          padding: 14px 12px; font-size: 13px; color: rgba(255,255,255,0.45);
        }
        .airport-combobox__option {
          display: flex; align-items: center; gap: 12px;
          padding: 10px 12px; border-radius: 7px; cursor: pointer;
        }
        .airport-combobox__option.is-active, .airport-combobox__option:hover {
          background: rgba(103,232,249,0.1);
        }
        .airport-combobox__option-code {
          font-family: 'IBM Plex Mono', monospace; font-size: 12px; letter-spacing: 0.06em;
          color: #7DD8EA; flex: 0 0 auto; min-width: 34px;
        }
        .airport-combobox__option-text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
        .airport-combobox__option-city { font-size: 13.5px; color: #fff; font-weight: 600; }
        .airport-combobox__option-name {
          font-size: 12px; color: rgba(255,255,255,0.5);
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }

        .access-featured { display: flex; flex-wrap: wrap; gap: 8px; }
        .access-featured__chip {
          padding: 7px 13px; border-radius: 999px; cursor: pointer;
          background: rgba(255,255,255,0.03); border: 1px solid rgba(160,180,200,0.2);
          color: rgba(255,255,255,0.62); font-size: 12.5px; white-space: nowrap;
          transition: border-color 200ms ease, background 200ms ease, color 200ms ease;
        }
        .access-featured__chip em {
          font-style: normal; margin-left: 6px; font-family: 'IBM Plex Mono', monospace;
          font-size: 11px; color: #7DD8EA;
        }
        .access-featured__chip:hover, .access-featured__chip:focus-visible {
          border-color: rgba(103,232,249,0.45); color: #fff; outline: none;
        }
        .access-featured__chip.is-active {
          border-color: rgba(103,232,249,0.6); background: rgba(103,232,249,0.1); color: #fff;
        }

        /* ── Mobile / tablet base: simple stacked flow, modest tilt ──
           .access-side is unwrapped via display:contents so its two
           children (controls, panel) become direct flex items of
           .access-stage here, letting them take their natural stacked
           order (search controls above the map, panel below) even
           though the markup groups them together for the desktop
           layout, where they stack as one unit beside the map instead. */
        .access-stage {
          position: relative; z-index: 2;
          max-width: 1800px; margin: 0 auto;
          padding: 0 clamp(20px,5vw,64px);
          display: flex; flex-direction: column;
        }
        .access-side { display: contents; }
        .access-controls { order: 1; }
        .access-mapwrap { order: 2; }
        .access-panel { order: 3; }
        .access-mapwrap {
          position: relative; width: 100%;
          animation: access-map-float 10s var(--ease-orbital) infinite;
          /* Drop-shadow lives here, one level above the 3D-transformed
             .access-mapplane, so the browser composites the perspective
             transform first and shadows the already-final shape—filters
             and 3D transforms on the very same element force an earlier,
             lower-resolution rasterization pass that reads as blur once
             the transform scales it back up. */
          filter: drop-shadow(0 24px 50px rgba(0,0,0,0.55));
        }
        @keyframes access-map-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-9px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .access-mapwrap { animation: none; }
        }
        .access-mapwrap::before {
          content: ""; position: absolute; z-index: 3; pointer-events: none;
          inset: -18% -18% auto auto; width: 62%; height: 62%;
          background: radial-gradient(circle closest-side at 100% 0%, rgba(196,222,255,0.32) 0%, rgba(130,190,240,0.14) 32%, transparent 70%);
          mix-blend-mode: screen;
        }
        /* Absolutely positioned within .access-mapmask now (not simply
           sitting at its top in normal flow)—.access-mapmask is taller
           on both ends now (north extension above, south below), and
           MAPPLANE_TOP_PCT is exactly how far down this untouched 1000x600
           box sits within that taller frame. Its own aspect-ratio, and
           everything computed against GEO_BOUNDS elsewhere, is completely
           unaffected—this only changes where the box sits, not its size. */
        .access-mapplane {
          position: absolute; top: ${MAPPLANE_TOP_PCT}%; left: 0;
          width: 100%; aspect-ratio: ${MAP_VB.w} / ${MAP_VB.h};
          transform: perspective(1100px) rotateX(8deg);
          transform-origin: 50% 15%;
        }
        /* Fills the exact same box as .access-mapplane (its only job is to
           carry every child as one rigid unit) and adds a small, slow
           translate on top of the parent's fixed 3D tilt—an
           imperceptible orbital drift, not a redo of the tilt itself.
           Everything geographic (photo, borders, rings, route, nodes)
           lives inside it, so nothing can drift out of registration with
           anything else. */
        .access-mapdrift {
          position: absolute; inset: 0; width: 100%; height: 100%;
          animation: access-orbital-drift 21s var(--ease-orbital) infinite;
          animation-play-state: paused;
          will-change: transform;
        }
        .access-mapwrap.is-in-view .access-mapdrift { animation-play-state: running; }
        /* Same drift, same timing, same in-view gating as .access-mapdrift so
           the unmasked glow layer moves in perfect lockstep with the masked
           base—otherwise the duplicated borders/rings separate a few px
           mid-cycle and read as doubled lines. */
        .access-glowdrift {
          position: absolute; inset: 0; width: 100%; height: 100%;
          animation: access-orbital-drift 21s var(--ease-orbital) infinite;
          animation-play-state: paused;
          will-change: transform;
        }
        .access-mapwrap.is-in-view .access-glowdrift { animation-play-state: running; }
        @keyframes access-orbital-drift {
          0%, 100% { transform: translate(0, 0); }
          50%      { transform: translate(3px, -2px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .access-mapdrift, .access-glowdrift { animation: none; transform: none; }
        }
        /* Range rings read as "active" via a slow dash-offset drift around
           their own circumference—never a rotation of the ring geometry
           itself, which would misleadingly suggest the ring's radius or
           position is changing. */
        .access-map__ring-line { animation: access-ring-dash-drift 48s linear infinite; }
        @keyframes access-ring-dash-drift { to { stroke-dashoffset: -120; } }
        @media (prefers-reduced-motion: reduce) {
          .access-map__ring-line { animation: none; }
        }
        /* Covers the full empty-to-textured zone above the map (matches
           the north extension's own share of .access-mapmask's height, not
           just .access-map__north-photo's box, since the flat/textureless
           read starts above that box too, where nothing renders at all).
           Same turbulence noise as .access-section__grain, faded in from
           nothing at the very top to fully present by the point real photo
           detail takes over—so the transition is grain-to-grain, never
           flat-to-grain. */
        .access-map__north-fade-grain {
          position: absolute; bottom: 100%; left: 0; width: 100%;
          height: ${((NORTH_EXT_HEIGHT_VB / MAP_VB.h) * 100).toFixed(2)}%;
          background-image: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23g)'/%3E%3C/svg%3E");
          background-size: 240px 240px;
          mix-blend-mode: overlay;
          opacity: 0.1;
          mask-image: linear-gradient(to bottom, transparent 0%, black 55%, black 100%);
          -webkit-mask-image: linear-gradient(to bottom, transparent 0%, black 55%, black 100%);
          pointer-events: none;
        }
        /* Whole-map breathing—deliberately a flat, single-color wash
           covering the entire surface with no internal gradient stops at
           all (only its own overall opacity animates). No internal edge
           means nothing for the 3D tilt to compress into a hard line —
           the failure mode every narrower band here has hit. Reads as the
           whole terrain very slowly and gently intensifying then
           releasing, filling in the sense of life the scan used to carry
           without being a moving light of any kind. */
        .access-map__breathe {
          position: absolute; inset: 0; pointer-events: none;
          background: rgba(140,200,230,1);
          mix-blend-mode: overlay;
          opacity: 0;
          animation: access-map-breathe 9s var(--ease-orbital) infinite;
        }
        @keyframes access-map-breathe {
          0%, 100% { opacity: 0; }
          50%      { opacity: 0.05; }
        }
        @media (prefers-reduced-motion: reduce) {
          .access-map__breathe { animation: none; opacity: 0; }
        }
        /* Same box, same transform as .access-mapplane above—but a
           sibling of .access-mapmask, not a child of it, so none of
           .access-mapmask's mask ever applies here. Carries only the
           coastline/state-line "shine", duplicated from the SVG inside
           .access-mapplane. Sits on top (later in DOM), always fully
           opaque, so any fade on the base layer below can be pushed as
           far as needed without ever dulling this outline. */
        .access-mapglow {
          position: absolute; top: ${MAPPLANE_TOP_PCT}%; left: 0; z-index: 2;
          width: 100%; aspect-ratio: ${MAP_VB.w} / ${MAP_VB.h};
          transform: perspective(1100px) rotateX(8deg);
          transform-origin: 50% 15%;
          pointer-events: none;
        }
        /* overflow: visible is essential here—the large range rings
           (1000 NM, the planning ring) are drawn with circle coordinates
           that deliberately bow well outside this SVG's own 1000x600
           viewBox (by design, so the arc never cuts off mid-curve—see
           the ring rendering comment). SVGs clip to their viewBox by
           default; the ORIGINAL .access-map__rings gets an explicit
           overflow:visible override via its own shared CSS rule, but this
           duplicate svg (bare, no matching class) was silently inheriting
           the default clip instead—cutting off most of every ring
           larger than 500 NM well before it reached the frame edge. */
        .access-mapglow svg { width: 100%; height: 100%; display: block; overflow: visible; }
        /* Narrow edge masking lives on this untransformed wrapper, one level
           above .access-mapplane's 3D perspective/rotate—not on the plane
           itself. A mask's percentage stops are defined in the masked
           element's own local box; putting it on the 3D-tilted plane meant
           the desktop breakpoint's 35deg rotateX visually compressed
           everything near the top edge, so a gradient tuned to look narrow
           and soft became a near-hard cut once tilted. Here, in flat 2D
           space, percentages map predictably to actual screen pixels. */
        .access-mapmask {
          position: relative; width: 100%; aspect-ratio: ${MAPMASK_ASPECT};
          /* Two independent gradients, intersected: the existing right-edge
             fade, plus a new top-edge fade dissolving the north extension
             into the section's own black before the title/subtitle. Both
             live here—on the untransformed wrapper, one level above
             .access-mapplane's 3D perspective/rotateX/rotateZ tilt—never
             on the tilted content itself. A gradient that's smooth in the
             tilted element's own local space gets visually compressed by
             the 35deg rotateX into a near-hard cut once rendered (this is
             exactly why the right-edge fade already lived here instead of
             on the photo)—the previous top fade lived on
             .access-map__north-photo itself and suffered the same
             compression, which is what the hairline actually was. Stops
             tuned to ${MAPPLANE_TOP_PCT.toFixed(2)}% (MAPPLANE_TOP_PCT) —
             fully opaque by the time the real landmass begins, so the
             actual map content is never touched, only the empty band
             above it. */
          /* Top-fade: held at zero through 3.87% (measured via
             getBoundingClientRect—.access-map__north-photo's own
             rendered top edge doesn't start at mapmask's true 0%, it
             starts 3.87% in; the untouched band above it is genuinely
             empty), THEN a smootherstep curve (3t²-2t³, zero slope at both
             ends, steepest in the middle—no single overly-fast segment).
             Deliberately reworked from a short, precise 3.87%-9% ramp (try
             to hide the seam exactly) to a long, honest vignette running
             all the way to 22%—genuinely dipping into the real map's
             own northernmost states, the same way an actual photographic
             vignette darkens real content at its edges. A short ramp keeps
             needing to line up exactly with where the photo's own content
             starts, and every precision attempt at that exact alignment
             has still read as a seam; a vignette this long doesn't need
             to hide anything—it reads as a frame, on purpose. */
          mask-image:
            linear-gradient(to left, transparent 0%, rgba(0,0,0,0.1) 8%, rgba(0,0,0,0.35) 16%, rgba(0,0,0,0.7) 23%, black 30%),
            linear-gradient(to bottom,
              rgba(0,0,0,0) 0%,
              rgba(0,0,0,0.043) 2.75%,
              rgba(0,0,0,0.156) 5.5%,
              rgba(0,0,0,0.316) 8.25%,
              rgba(0,0,0,0.5) 11%,
              rgba(0,0,0,0.684) 13.75%,
              rgba(0,0,0,0.844) 16.5%,
              rgba(0,0,0,0.957) 19.25%,
              black 22%, black 100%),
            /* Corner vignette—same honest-frame logic, echoed as a
               proper rounded corner darkening rather than a razor-edge
               intersection of two straight fades. Reaches further and
               commits harder than the two 1D fades alone so the top-right
               corner—where they'd otherwise overlap and leave a thin
               sliver of undecided brightness—is unambiguously part of
               the frame. */
            radial-gradient(ellipse 72% 88% at 100% 0%, transparent 0%, transparent 14%, black 44%),
            /* LEFT edge—a SMOOTH multi-stop fade, the same character as
               the right-edge fade rather than a hard step. It can't be as
               WIDE as the right (that side fades empty Atlantic; this side
               has California's coast sitting close to the frame—a full
               30% mirror drops SF/LA into the dark), so it fades over ~8.5%
               with a gentle curve: enough of a gradient that the West Coast
               dissolves into the black instead of ending on a hard bright
               line, while LA/San Diego keep their brightness. California's
               coastline glow stays crisp regardless—it lives on the
               separate, never-masked .access-mapglow layer. No bottom-left
               corner vignette: the Baja/Mexico crop is meant to just keep
               running straight down and fade out on its own the way the
               photo naturally does, not be clipped into a rounded frame. */
            linear-gradient(to right, transparent 0%, rgba(0,0,0,0.14) 2.2%, rgba(0,0,0,0.44) 4.5%, rgba(0,0,0,0.74) 6.5%, black 8.5%);
          mask-composite: intersect, intersect, intersect;
          mask-repeat: no-repeat;
          mask-size: 100% 100%;
          /* No -webkit-mask-image duplicate here—Chromium aliases
             -webkit-mask-image to the exact same underlying property as
             mask-image (not a separate fallback), so declaring both meant
             this single-gradient -webkit- version, being last, silently
             overwrote the two-gradient mask-image above and discarded the
             entire top-fade. That's why the fade never visibly changed no
             matter how its stops were retuned—it was never actually
             being applied. Modern evergreen browsers (this app's target)
             support unprefixed mask-image/mask-composite natively. */
        }
        /* Southern extension—real photography picking up exactly where
           .access-mapplane already ends (top:100% of mapplane, never a
           fixed number). A child of mapplane, not mapmask, so it inherits
           the same rotation/perspective as one continuous surface—see
           the JSX comment above it for why that matters. Percentage-based
           fade at its own bottom edge so the crisp zone scales with its
           own height rather than a fixed pixel count. */
        .access-map__south-photo {
          /* A genuine ~12px overlap, not a flush 100% edge—two separately
             rasterized elements meeting at an exact shared line under a 3D
             transform reliably shows a hairline seam from sub-pixel
             rounding, and this crop's own color grade never perfectly
             matches the main photo's regardless of correction. Painted
             after .access-mapplane's other content in DOM order, this
             overlap sits ON TOP of the map's last few rows; the matching
             top-fade below makes that overlap a soft crossfade—the map's
             own true edge shows through, dissolving into this crop —
             rather than a second hard edge on top of the first one. */
          position: absolute; top: calc(100% - 12px); left: 0; z-index: 0;
          /* object-fit:fill + an explicit aspect-ratio (derived from the
             exact same GEO_BOUNDS math as the rest of the map, see
             SOUTH_EXT_* above) instead of object-fit:cover—cover scales
             based on this image's own native pixel dimensions, which had
             nothing to do with the map's real degrees-per-pixel rate and
             was quietly displaying this crop at a different horizontal
             scale than the map above it, so the coastline didn't line up
             with what's directly north of it. fill+aspect-ratio guarantees
             the same scale, with zero distortion, as long as the aspect-
             ratio is geographically correct—which SOUTH_EXT_ASPECT is. */
          width: 100%; max-width: none; aspect-ratio: ${SOUTH_EXT_ASPECT};
          object-fit: fill; display: block;
          opacity: 1; pointer-events: none;
          /* This crop is a separate source export from the main NASA mosaic
             and DIDN'T match on two counts. (1) Its water/empty pixels are
             navy (~rgb(7,8,21))—brighter than the section's own near-black
             (rgb(1,1,12))—so wherever the mask held it opaque it painted a
             full-width blue band that read as a "strip" floating below the
             map (confirmed by a with/without diff: the band showed even in
             the far-left empty space where there is no land at all, so it
             was the photo rectangle's own floor, not geography). (2) Its
             land read cooler/darker than the warm dusk grade the main map
             carries. The two pull opposite ways under a single filter: a
             brightness lift fixes the land but raises the navy floor into a
             band; a hard contrast crush kills the band but also crushes the
             land too dark. The balance below—a modest brightness lift
             (1.2) to bring the land up to the main map's level, paired with
             a moderate contrast (1.3) whose mid-grey pivot still pushes the
             low navy floor down toward the section black—clears the band
             (verified: residual is a sub-perceptual grey ghost only visible
             at 7x amplification, not the old vivid stripe) while keeping the
             land bright. sepia + negative hue-rotate hold it in the map's
             plum register; saturate keeps the city lights from going grey. */
          filter: brightness(1.2) contrast(1.3) saturate(1.2) sepia(0.16) hue-rotate(-8deg);
          /* Percentage-based, not a fixed pixel count—the true-aspect-
             ratio fix above made this element much shorter (50-80px) than
             its first draft, and a fixed-px fade (previously 70px) that
             exceeds the element's own height fades the ENTIRE strip from
             top to bottom instead of just the last stretch of it, which is
             what was turning Baja's tip into a soft blob instead of a
             crisp point. Fade pulled in (opaque only 14%–34%, gone by 78%
             instead of 100%): the real land continuation—Baja + the
             northern-Mexico mainland—all sits in the top third right
             below the US border. The fade was ORIGINALLY pulled in hard (to
             78%) purely to kill a bright navy "band" the crop's ocean floor
             made—but the brightness/contrast crush above now flattens that
             floor to black on its own, so the fade no longer has to do that
             job and can run much longer. Extended to 94% to let the crop
             show as much of Baja + central Mexico as it actually contains,
             filling the space above the disclaimer with real map instead of
             an empty gap, before finally dissolving. */
          mask-image: linear-gradient(to bottom, transparent 0%, black 14%, black 40%, transparent 94%);
          -webkit-mask-image: linear-gradient(to bottom, transparent 0%, black 14%, black 40%, transparent 94%);
        }
        /* Northern extension—mirrors .access-map__south-photo in every
           respect, just upside down: bottom:100% (its own bottom edge
           lands at mapplane's true top) instead of top:100%, and its mask
           gradient flipped so the crossfade-overlap zone sits at the
           bottom (100% end, touching the map) while the real outer fade
           sits at the top (0% end, dissolving before the title/subtitle). */
        .access-map__north-photo {
          position: absolute; bottom: calc(100% - 12px); left: 0; z-index: 0;
          width: 100%; max-width: none; aspect-ratio: ${NORTH_EXT_ASPECT};
          object-fit: fill; display: block;
          opacity: 0.96; pointer-events: none;
          /* Same tone grade as the main photo and the south extension, so
             all three read as one continuous photograph rather than three
             separately-graded crops. */
          filter: brightness(1.2) contrast(1.3) saturate(1.2) sepia(0.16) hue-rotate(-8deg);
          /* No top ramp here anymore—fully opaque from its own top edge.
             .access-mapmask's own top-edge fade (untransformed, above)
             now owns dissolving this into the section background; doing it
             here too, inside the 3D tilt, was the actual source of the
             hairline. Bottom crossfade into the main map unchanged. */
          mask-image: linear-gradient(to bottom, black 0%, black 88%, transparent 100%);
          -webkit-mask-image: linear-gradient(to bottom, black 0%, black 88%, transparent 100%);
        }
        .access-map__photo {
          position: absolute; left: 0;
          /* top/height/width set inline per PHOTO_TOP_PCT/HEIGHT_PCT/
             WIDTH_PCT above—the photo is taller AND wider than this box,
             anchored at its top-left, extending up and to the right.
             max-width/max-height: none overrides Tailwind Preflight's
             img max-width:100% reset, which otherwise silently clamps the
             inline width back down and desyncs the photo from the SVG
             coastline/state-lines it's calibrated to GEO_BOUNDS to match
             exactly—the two are pinned to the same projection, but only
             if this element's own size isn't second-guessed. */
          max-width: none; max-height: none;
          object-fit: fill; display: block; opacity: 0.96;
          /* Same tone filter as .access-map__south-photo. This replaced the
             plum mix-blend:color rect that used to sit over the photo (see
             the note where it was removed, in the land SVG): rather than
             recoloring the country toward purple, it enriches the real
             photo's own night-lights palette—a modest brightness lift,
             contrast to deepen the between-cities dark so it doesn't read
             washed-out, saturate + mild sepia/negative-hue for warmth in
             the city lights. Because the main photo and the Mexico
             extension now carry the identical grade, the appended seam at
             the US border reads as one continuous photograph. */
          filter: brightness(1.2) contrast(1.3) saturate(1.2) sepia(0.16) hue-rotate(-8deg);
        }
        /* A plain painted gradient sitting on top of the photo, never a
           mask-image/clip-path on the photo itself. An element with its
           own mask, living inside .access-mapplane's 3D perspective/rotate
           transform, gets rasterized against its pre-transform rectangular
           bounding box—which no longer lines up with the shape once the
           parent tilts it, and shows up as hard rectangular seams cutting
           across the map instead of a soft edge. A gradient background has
           no such bounding-box mismatch; it composites through the 3D
           transform like any other paint. */
        .access-map__photo-fade {
          position: absolute; left: 0; pointer-events: none;
          /* Same top/height/width as .access-map__photo, set inline, so
             both gradients line up with the actual (taller-and-wider-than-
             the-box) photo edges—including in the top-right corner,
             where the Canada and Atlantic extensions overlap. Both fades
             are pinned to PHOTO_FADE_TOP_SOLID/RIGHT_SOLID—the real
             GEO_BOUNDS border—so the country itself never dims, only the
             extra land/ocean beyond it. */
          background:
            linear-gradient(to right,
              transparent 0%, transparent ${PHOTO_FADE_RIGHT_SOLID}%,
              rgba(5,6,7,0.6) ${(PHOTO_FADE_RIGHT_SOLID + 100) / 2}%, #050607 100%),
            linear-gradient(to bottom,
              #050607 0%, transparent ${PHOTO_FADE_TOP_SOLID}%,
              transparent ${PHOTO_FADE_BOTTOM_START}%, #050607 100%);
        }
        .access-map__land, .access-map__rings, .access-map__route {
          position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible;
        }
        /* The only light permitted to soften past the coastline—small and
           faint by design, and still hard-bounded by the section's own
           overflow:clip so it can never reach the page below the map. */
        .access-map__halo {
          position: absolute; width: 78%; height: 78%;
          transform: translate(-50%,-50%);
          border-radius: 50%;
          background: radial-gradient(circle, rgba(110,214,238,0.05) 0%, rgba(110,214,238,0.018) 55%, transparent 75%);
          filter: blur(4px);
          pointer-events: none;
        }
        .access-state-pulse {
          stroke-dasharray: 9 640; stroke-dashoffset: 0;
          filter: drop-shadow(0 0 3px rgba(103,232,249,0.6));
          animation: access-state-pulse-travel 7s linear infinite;
        }
        .access-state-pulse--1 { animation-duration: 9.5s; animation-delay: -3.2s; }
        .access-state-pulse--2 { animation-duration: 11s; animation-delay: -6.7s; }
        @keyframes access-state-pulse-travel { to { stroke-dashoffset: -649; } }
        .access-context-city { animation: access-city-flicker 6.5s var(--ease-orbital) infinite; }
        .access-context-city:nth-of-type(2n) { animation-duration: 8.5s; animation-delay: -3s; }
        .access-context-city:nth-of-type(3n) { animation-duration: 5.5s; animation-delay: -1.4s; }
        .access-context-city:nth-of-type(5n) { animation-duration: 9.5s; animation-delay: -4.2s; }
        .access-context-city:nth-of-type(7n) { animation-duration: 11.2s; animation-delay: -6.1s; }
        /* Narrow swing (0.88–1.0)—a breathing dim, not a blink. Individual
           dots shouldn't compete for attention against each other. */
        @keyframes access-city-flicker { 0%, 100% { opacity: 0.88; } 50% { opacity: 1; } }
        .access-night-light.is-major { animation: access-city-flicker 7s var(--ease-orbital) infinite; }
        .access-night-light.is-major:nth-of-type(2n) { animation-duration: 9s; animation-delay: -3.5s; }
        .access-night-light.is-major:nth-of-type(3n) { animation-duration: 5.8s; animation-delay: -1.6s; }
        .access-night-light.is-major:nth-of-type(4n) { animation-duration: 10.5s; animation-delay: -5.2s; }
        .access-night-light.is-major:nth-of-type(7n) { animation-duration: 12.4s; animation-delay: -7.3s; }
        /* Sparse, abstract, and mostly invisible—resolves into a dot +
           crosshair for a moment, then fades back out. Long, staggered,
           asynchronous holds so at most one is ever really noticeable. */
        .access-transient-blip { opacity: 0; animation: access-transient-blip-fade 18s var(--ease-orbital) infinite; }
        .access-transient-blip--0 { animation-duration: 17s; animation-delay: -2s; }
        .access-transient-blip--1 { animation-duration: 21s; animation-delay: -9s; }
        .access-transient-blip--2 { animation-duration: 19s; animation-delay: -14s; }
        @keyframes access-transient-blip-fade {
          0%, 82% { opacity: 0; transform: scale(0.7); }
          88%     { opacity: 0.42; transform: scale(1); }
          94%     { opacity: 0.42; transform: scale(1); }
          100%    { opacity: 0; transform: scale(0.7); }
        }
        @media (prefers-reduced-motion: reduce) {
          .access-state-pulse { animation: none; opacity: 0; }
          .access-context-city { animation: none; opacity: 0.8; }
          .access-night-light.is-major { animation: none; }
          .access-transient-blip { animation: none; opacity: 0; }
        }

        /* Constant dashed vector — same dash grammar as the range rings,
           just a touch tighter. No draw-in "shooting" toward the city and no
           traveling pulse; it fades in on select and then holds, steady, for
           as long as that destination stays selected. */
        .access-map__route-line {
          stroke: rgba(103,232,249,0.62); stroke-width: 1.7; fill: none;
          stroke-dasharray: 5 6;
          animation: access-route-fade-in 450ms ease-out both;
        }
        @keyframes access-route-fade-in { from { opacity: 0; } to { opacity: 1; } }
        .access-map__route-line--fading {
          stroke: rgba(103,232,249,0.62); stroke-width: 1.7; fill: none;
          stroke-dasharray: 5 6;
          animation: access-route-fade-out 300ms ease-in forwards;
        }
        @keyframes access-route-fade-out { from { opacity: 1; } to { opacity: 0; } }
        /* Targeting brackets—snap onto the destination shortly after the
           dashed vector fades in. */
        .access-target-brackets { animation: access-brackets-snap 380ms 480ms var(--ease-decisive) both; }
        .access-target-brackets__mark {
          fill: none; stroke: rgba(103,232,249,0.85); stroke-width: 1.1; stroke-linecap: round;
        }
        @keyframes access-brackets-snap {
          0%   { transform: scale(1.7); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .access-map__route-line { animation: none; opacity: 1; }
          .access-map__route-line--fading { animation: none; opacity: 0; }
          .access-target-brackets { animation: none; transform: none; opacity: 1; }
        }

        .access-hub {
          position: absolute; transform: translate(-50%,-50%);
          display: flex; flex-direction: column; align-items: center;
          z-index: 2; pointer-events: none;
        }
        .access-hub__ring {
          position: absolute; top: 50%; left: 50%; width: 22px; height: 22px;
          margin: -11px 0 0 -11px; border-radius: 50%; border: 1px solid rgba(200,168,112,0.6);
        }
        /* Reticle-tighten—a transient accent at the very start of each
           acquisition: starts oversized and invisible, snaps down to a
           tight fit around the origin, then dissolves. Not a permanent
           addition to the origin marker, just a one-shot cue. */
        .access-hub__reticle {
          position: absolute; top: 50%; left: 50%; width: 26px; height: 26px;
          margin: -13px 0 0 -13px;
          border: 1px solid rgba(200,168,112,0.9);
          animation: access-reticle-tighten 500ms var(--ease-decisive) both;
        }
        @keyframes access-reticle-tighten {
          0%   { transform: scale(2.4); opacity: 0; }
          40%  { opacity: 0.9; }
          100% { transform: scale(1); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .access-hub__reticle { animation: none; opacity: 0; }
        }
        .access-hub__dot {
          position: absolute; top: 50%; left: 50%; width: 6px; height: 6px;
          margin: -3px 0 0 -3px; border-radius: 50%; background: rgba(200,168,112,0.95);
          box-shadow: 0 0 12px rgba(200,168,112,0.7);
        }
        .access-hub__pulse {
          position: absolute; top: 50%; left: 50%; width: 10px; height: 10px;
          margin: -5px 0 0 -5px; border-radius: 50%;
          border: 1px solid rgba(200,168,112,0.8);
          animation: access-beacon-pulse 4.6s ease-out infinite;
        }
        /* Restrained on purpose—a ring-only border (never filled), and a
           pulled-in max scale so it reads as a single considered beacon
           rather than a wide bright halo. */
        @keyframes access-beacon-pulse {
          0%   { transform: scale(1); opacity: 0.9; }
          100% { transform: scale(3.6); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .access-hub__pulse { animation: none; opacity: 0; }
        }

        .access-node {
          position: absolute; transform: translate(-50%,-50%);
          display: flex; flex-direction: column; align-items: center; gap: 3px;
          background: none; border: none; padding: 0; cursor: pointer; z-index: 3;
          color: rgba(210,224,236,0.55);
        }
        .access-node--search { pointer-events: none; }
        .access-node__hit { position: absolute; inset: -14px; border-radius: 50%; }
        .access-node__dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: currentColor;
          box-shadow: 0 0 0 1px rgba(255,255,255,0.15);
          transition: transform 300ms ease, background 300ms ease, box-shadow 300ms ease;
        }
        .access-node__code {
          font-family: 'IBM Plex Mono', monospace; font-size: 8px; letter-spacing: 0.09em;
          transition: color 300ms ease, opacity 300ms ease;
          opacity: 0.75;
        }
        .access-node__pulse {
          position: absolute; top: 3px; left: 50%; width: 6px; height: 6px;
          margin-left: -3px; border-radius: 50%;
          border: 1px solid rgba(103,232,249,0.7);
          /* Delayed to land after the targeting brackets snap in (900ms
             route draw + ~380ms bracket snap), so the sequence reads in
             order: draw → brackets lock → acquisition pulse. */
          animation: access-node-arrive 700ms 1050ms var(--ease-decisive) both;
          pointer-events: none;
        }
        @keyframes access-node-arrive {
          0%   { transform: scale(1); opacity: 0.9; }
          100% { transform: scale(5); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .access-node__pulse { animation: none; opacity: 0; }
        }
        .access-node:hover, .access-node:focus-visible { color: #EAF7FB; outline: none; }
        .access-node:hover .access-node__dot, .access-node:focus-visible .access-node__dot { transform: scale(1.4); }
        .access-node:focus-visible .access-node__dot { box-shadow: 0 0 0 3px rgba(103,232,249,0.45); }
        .access-node.is-active { color: #7DE8FA; }
        .access-node.is-active .access-node__dot {
          transform: scale(1.6); background: #A9ECFA;
          box-shadow: 0 0 10px 2px rgba(103,232,249,0.65), 0 0 0 1px rgba(255,255,255,0.25);
        }
        .access-node.is-active .access-node__code { opacity: 1; }

        .access-panel {
          position: relative; z-index: 4; overflow: hidden;
          margin: clamp(20px,3vh,32px) auto 0; max-width: 460px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(160,180,200,0.16);
          padding: clamp(22px,3vw,32px);
          /* Three composed animations:
             1. arrive—one-shot slide-in on transform (always runs).
             2. breath—border/box-shadow pulse on the map's ~9s orbital
                cycle, so the panel is alive on the same heartbeat as the
                terrain (paused off-screen).
             3. drift—a slow parallax on the standalone 'translate' property
                (composes with the arrive transform rather than fighting it),
                counter to the map's own drift and on a longer, non-locking
                period, so panel and map read as one satellite view with a
                touch of depth (paused off-screen).
             animation-play-state is positional: arrive always runs; breath
             and drift start paused and are released by .access-stage.is-in-view. */
          animation:
            access-panel-arrive 850ms cubic-bezier(0.3,1.15,0.55,1) both,
            access-panel-breath 9s var(--ease-orbital) infinite,
            access-panel-drift 24s var(--ease-orbital) infinite;
          animation-play-state: running, paused, paused;
        }
        .access-stage.is-in-view .access-panel {
          animation-play-state: running, running, running;
        }
        /* A sleek slide from the right that overshoots slightly past its
           resting position before settling—reads as the panel hitting a
           soft stop rather than just easing to a halt. */
        @keyframes access-panel-arrive {
          0%   { transform: translateX(70px); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes access-panel-breath {
          0%, 100% {
            border-color: rgba(160,180,200,0.14);
            box-shadow: inset 0 0 0 1px rgba(103,232,249,0);
          }
          50% {
            border-color: rgba(160,180,200,0.30);
            box-shadow: inset 0 0 32px -4px rgba(103,232,249,0.15);
          }
        }
        @keyframes access-panel-drift {
          0%, 100% { translate: 0 0; }
          50%      { translate: -3px 2px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .access-panel { animation: none; translate: none; }
        }
        /* A soft diagonal shine that drifts slowly across the glass — the
           thing that reads as "alive" the instant you look, without the
           card ever feeling busy. Blurred, screen-blended, low opacity;
           clipped by the panel's overflow:hidden. Paused off-screen and
           disabled under reduced motion. */
        .access-panel__sheen {
          position: absolute; top: -20%; bottom: -20%; left: -45%; width: 50%;
          pointer-events: none; z-index: 1;
          background: linear-gradient(104deg,
            transparent 0%,
            rgba(150,205,238,0.05) 42%,
            rgba(175,222,248,0.11) 50%,
            rgba(150,205,238,0.05) 58%,
            transparent 100%);
          filter: blur(7px);
          mix-blend-mode: screen;
          /* Slower pass and a longer quiet hold than before (was 12s, no
             delay) so the shine stays a calm, occasional catch-the-eye
             moment rather than a steady sweep — plus a lead-in delay so it
             doesn't fire the instant the panel settles. */
          animation: access-panel-sheen 20s var(--ease-orbital) 2.5s infinite;
          animation-play-state: paused;
        }
        .access-stage.is-in-view .access-panel__sheen { animation-play-state: running; }
        @keyframes access-panel-sheen {
          0%   { transform: translateX(0); }
          34%  { transform: translateX(360%); }
          100% { transform: translateX(360%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .access-panel__sheen { animation: none; opacity: 0; }
        }
        /* Data-settle scanline—a single cyan line sweeps top→bottom once
           per selection (remounted by key), delayed to land just after the
           number tweens settle, echoing the map's targeting lock. */
        .access-panel__settle {
          position: absolute; left: 0; right: 0; top: 0; height: 2px;
          pointer-events: none; z-index: 2; opacity: 0;
          background: linear-gradient(to right,
            transparent, rgba(103,232,249,0.55), transparent);
          box-shadow: 0 0 10px rgba(103,232,249,0.4);
          animation: access-panel-settle 900ms 700ms var(--ease-decisive) both;
        }
        @keyframes access-panel-settle {
          0%   { opacity: 0; transform: translateY(0); }
          12%  { opacity: 0.9; }
          100% { opacity: 0; transform: translateY(clamp(180px,26vh,320px)); }
        }
        @media (prefers-reduced-motion: reduce) {
          .access-panel__settle { animation: none; opacity: 0; }
        }
        .access-panel__code-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 6px; }
        .access-panel__code { font-family: 'IBM Plex Mono', monospace; font-size: 12px; letter-spacing: 0.14em; color: rgba(103,232,249,0.75); }
        .access-panel__status {
          font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.04em;
          color: rgba(150,225,180,0.85); text-transform: uppercase; text-align: right;
        }
        .access-panel__status.is-stop { color: rgba(235,190,120,0.9); }
        .access-panel__city { font-family: 'Fraunces', serif; font-size: clamp(24px,2.2vw,32px); color: #fff; margin: 0 0 4px; font-weight: 700; }
        .access-panel__airport { font-size: 13px; color: rgba(255,255,255,0.5); margin: 0 0 20px; }
        /* margin-bottom trimmed to 0 (was 20px): the small in-panel
           disclaimer that used to sit below the stats is gone, so this was
           just dead space — now the bottom padding alone closes the card,
           symmetric with the top. */
        .access-panel__stats { display: flex; flex-direction: column; gap: 18px; margin-bottom: 0; }
        .access-panel__value { display: block; font-family: 'Fraunces', serif; font-size: clamp(22px,2vw,28px); color: #fff; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1.05; }
        /* Deliberate 1-2-3 read (aerospace spec-sheet hierarchy): the flight
           time is the hero—the single fact a chartering owner cares about —
           so the first stat's number is set markedly larger; the distance is
           the supporting figure, so its number is a step down in both size
           and brightness. Without this the two numbers sit at the same weight
           and nothing tells the eye what to read first. */
        .access-panel__stat:first-child .access-panel__value {
          font-size: clamp(32px,3vw,44px);
        }
        .access-panel__stat:last-child .access-panel__value {
          font-size: clamp(20px,1.7vw,25px); color: rgba(255,255,255,0.82);
        }
        .access-panel__label {
          display: flex; align-items: center; gap: 8px;
          font-family: 'IBM Plex Mono', monospace; font-size: 10px; letter-spacing: 0.1em;
          color: rgba(255,255,255,0.42); margin-top: 3px;
        }
        /* A short cyan hairline tick before each readout label—the same
           instrument-marking language as the panel's other mono labels, so
           the stats read as gauge readouts rather than captions. */
        .access-panel__label::before {
          content: ""; flex: 0 0 auto; width: 12px; height: 1px;
          background: rgba(103,232,249,0.4);
        }

        .access-disclaimer {
          /* z-index bumped well past 4—the range rings that bleed down
             this far live inside .access-mapwrap, which itself establishes
             its own stacking context at z-index:1 at the desktop
             breakpoint; a z-index set on a *descendant* deep inside that
             context (like .access-mapglow's 2) never escapes it to compete
             with something outside, so this element's own z-index is what
             actually has to win the comparison, not anything nested in
             the map. 4 wasn't clearing that reliably; 20 does. */
          position: relative; z-index: 20;
          /* Deliberately narrow and LEFT-aligned so the legal copy reads as a
             tidy block that stacks taller and flows downward in the open space
             below the map, with its right edge clearing the results panel on the
             right rather than crowding up under the panel's bottom-left corner. */
          max-width: 740px; margin: clamp(32px,4.5vh,56px) 0 0;
          padding: 0 clamp(20px,5vw,80px);
          font-size: 13px; line-height: 1.75; color: rgba(255,255,255,0.42);
        }
        /* The ring-fade curtain that used to live here (a dark ::before
           gradient extending ~90px ABOVE the disclaimer) has been removed.
           Its two original jobs are both gone: the range ring no longer
           bleeds into the next section (that section is now opaque), and
           since the disclaimer got narrowed + pulled up toward the results
           panel, the curtain's 90px upward reach—being only as wide as the
           centered disclaimer—was landing on the LEFT half of the panel
           and darkening it into a discolored rectangle with a hard vertical
           edge (the panel's right half sat outside the curtain's width). The
           ring simply passes faintly behind the disclaimer copy now, which
           stays fully legible, and nothing overlaps the panel. */

        /* ── Desktop: full asymmetric, cinematic-tilt composition ── */
        @media (min-width: 1025px) {
          .access-top {
            padding: 0 clamp(20px,5vw,80px);
            margin-bottom: clamp(8px,1.5vh,20px);
          }
          .access-head {
            max-width: 660px; margin: 0; padding: 0;
          }
          .access-stage {
            min-height: clamp(640px,88vh,900px);
            padding: 0;
          }
          .access-mapwrap {
            /* Reverted to 0%—shifting right to reveal the West Coast
               pushed the whole map (and the northeast corner with it)
               further under the results panel, which was worse than the
               clipping it fixed. Left-edge clipping needs a different
               approach that doesn't move the whole map. */
            position: absolute; top: -7%; left: 0%; width: 70%; z-index: 1;
          }
          /* .access-mapwrap itself is only 70% of the stage here, but the
             southern extension needs to reach the full stage width—this
             is SOUTH_EXT_WIDTH_PCT, ~142.86% of .access-mapwrap's own
             width (the box this percentage resolves against), derived from
             the exact same degrees as the crop itself so width% and
             aspect-ratio stay consistent with each other. */
          .access-map__south-photo {
            width: ${SOUTH_EXT_WIDTH_PCT}%;
          }
          .access-mapplane {
            /* transform-origin moved from 55% 22% to 15% 22% and scale
               pulled back from 1.05 to 0.98—measured empirically
               (getBoundingClientRect) to bring the left edge from -152px
               (real West Coast geography clipped off-stage) to ~0px, while
               keeping the right edge within ~12px of where it was, so the
               northeast doesn't get pushed further under the results panel
               the way a plain left-shift did. Same tilt/perspective, just
               pivoting and sized from a point close to the edge that was
               actually clipping. */
            transform: perspective(1700px) rotateX(35deg) rotateZ(-3deg) scale(0.98);
            transform-origin: 15% 22%;
          }
          .access-mapglow {
            /* transform-origin moved from 55% 22% to 15% 22% and scale
               pulled back from 1.05 to 0.98—measured empirically
               (getBoundingClientRect) to bring the left edge from -152px
               (real West Coast geography clipped off-stage) to ~0px, while
               keeping the right edge within ~12px of where it was, so the
               northeast doesn't get pushed further under the results panel
               the way a plain left-shift did. Same tilt/perspective, just
               pivoting and sized from a point close to the edge that was
               actually clipping. */
            transform: perspective(1700px) rotateX(35deg) rotateZ(-3deg) scale(0.98);
            transform-origin: 15% 22%;
          }
          /* Controls (search + featured) and the calculations panel are
             grouped into one right-side column here, stacked with the
             search sitting directly above the panel rather than floating
             up near the headline—see .access-side below. */
          .access-side {
            display: flex; flex-direction: column;
            gap: clamp(18px,2.4vh,30px);
            position: absolute; z-index: 5;
            right: clamp(16px,3vw,48px); bottom: clamp(20px,4vh,52px);
            width: min(440px, 34vw);
          }
          .access-controls {
            max-width: none; margin: 0; padding: 0; order: initial;
          }
          .access-panel {
            position: static; z-index: auto; order: initial;
            width: 100%; margin: 0;
            padding: clamp(26px,2.4vw,38px);
            /* saturate() removed—it was boosting the color of whatever map
               content / ring showed through the translucent panel, tinting
               the box; plain blur keeps the glass feel without recoloring the
               backdrop. Fill nudged 0.72 -> 0.8 for a touch more solidity.
               (The discolored half-box with the hard vertical edge that
               seemed to come from here was actually the disclaimer fade
               curtain landing on the panel—see the note where that ::before
               was removed. With that gone the panel reads uniform.) */
            background: rgba(8,11,14,0.8); backdrop-filter: blur(16px);
            box-shadow: 0 50px 120px -30px rgba(0,0,0,0.75);
          }
          .access-panel__city { font-size: clamp(28px,2.6vw,38px); }
          .access-panel__value { font-size: clamp(26px,2.4vw,34px); }
          /* Pulled UP into the empty band that the tall results panel leaves
             below the shorter Mexico map extension, so the disclaimer sits
             just under the panel's own baseline instead of floating far
             below everything. Negative, but clamped so it never rises far
             enough to collide with the panel above it. */
          .access-disclaimer { margin-top: clamp(-30px,-2vh,-14px); }
        }
      `}</style>
    </section>
  );
};

const PDFS = [
  {
    title: "Management Services",
    description:
      "How Steck Aviation Management structures owner-aligned management—scope, governance, and the operating model.",
    url: "/documents/aircraft-management-services.pdf",
    label: "Executive Overview",
  },
  {
    title: "Beechjet 400A",
    description:
      "Specifications, performance, and operating profile of the Beechjet 400A.",
    url: "/documents/beechjet-400a.pdf",
    label: "Aircraft Profile",
  },
];

const Home = () => {
  const [reports, setReports] = useState([]);
  const [series, setSeries] = useState([]);
  const bannerVideoRef = useRef(null);
  const [showSkip, setShowSkip] = useState(true);
  // One-way latch used ON MOBILE to retire the Skip control once the visitor
  // leaves the intro (skips, the video ends, or they scroll past it). Desktop
  // keeps its reversible showSkip behaviour; this only gates the mobile CSS.
  const [introDone, setIntroDone] = useState(false);
  // ── Travel-needs assessment (interim step between the video and the map) ──
  // Opens on Skip, on the video's natural end, or on scrolling down into the
  // site; RE-OPENS whenever the visitor scrolls back up to the top. The shared
  // trigger + completion logic lives in useTravelAssessment.
  const {
    open: assessmentOpen,
    showTab: assessmentTabVisible,
    openAssessment,
    closeAssessment,
    onComplete: handleAssessmentComplete,
  } = useTravelAssessment({
    onContinue: (reason, source) => {
      // "Explore your reach" glides into the interactive map; Skip / video-end
      // glide gently into the site; scroll and scroll-to-top opens leave the
      // visitor exactly where they were.
      if (reason === "explore") proceedIntoSite(1400);
      else if (source === "skip" || source === "ended") proceedIntoSite(1100);
    },
  });

  // Custom eased scroll so the hand-off into the site is a slow, cinematic
  // glide—browser "smooth" is too quick and can't be timed. easeInOutCubic.
  const smoothScrollTo = (targetY, duration) => {
    const startY = window.scrollY;
    const dist = targetY - startY;
    if (Math.abs(dist) < 2) return;
    const startT = performance.now();
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const step = (now) => {
      const t = Math.min((now - startT) / duration, 1);
      window.scrollTo(0, startY + dist * ease(t));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  // Glide the visitor past the banner into the site. Duration is explicit so
  // the auto-advance runs slow (~1.75s) while a manual Skip is a touch quicker.
  const proceedIntoSite = (duration = 1100) => {
    const el = document.getElementById("home-after-video");
    if (!el) return;
    const targetY = el.getBoundingClientRect().top + window.scrollY - 88;
    smoothScrollTo(Math.max(targetY, 0), duration);
  };

  // Open the Two Doors experience, optionally jumping to a specific entry
  // (family|business|resume|result) requested from the collapsed module.
  const openTwoDoors = (entry) => {
    if (entry) {
      try { sessionStorage.setItem("steck_2d_entry", entry); } catch (e) { /* ignore */ }
    }
    openAssessment("tab");
  };

  // When the Two Doors "See your reach on the map" CTA is used from another
  // route, it navigates here and leaves this flag; glide to the map once it has
  // actually mounted (poll via rAF — no brittle fixed delay).
  useEffect(() => {
    let flag = false;
    try { flag = sessionStorage.getItem("steck_scroll_to_map") === "1"; } catch (e) { /* ignore */ }
    if (!flag) return undefined;
    try { sessionStorage.removeItem("steck_scroll_to_map"); } catch (e) { /* ignore */ }
    let raf = 0;
    let tries = 0;
    const tryScroll = () => {
      const el = document.getElementById("home-after-video");
      if (el) {
        const targetY = Math.max(el.getBoundingClientRect().top + window.scrollY - 88, 0);
        smoothScrollTo(targetY, 1200);
        return;
      }
      if (tries++ < 90) raf = requestAnimationFrame(tryScroll);
    };
    raf = requestAnimationFrame(tryScroll);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the clip finishes, present the assessment as the interim step — but if
  // it has already surfaced this session (so openAssessment is a no-op), fall
  // through and glide into the site anyway, exactly like the Skip control does.
  // Without this fallback the video would just end into a frozen, unscrolled
  // hero on any repeat visit.
  const handleVideoBannerEnded = () => {
    setIntroDone(true);
    if (assessmentOpen) return;
    if (!openAssessment("ended")) proceedIntoSite(1100);
  };

  // Mobile autoplay hardening. The <video> already carries autoPlay/muted/
  // playsInline, but React applies `muted` as a DOM *property* and never emits
  // the `muted` HTML *attribute* — and iOS Safari checks that attribute when
  // deciding autoplay eligibility. Without it, phones treat the clip as having
  // sound, block autoplay, and show the native play button (desktop ignores
  // this, which is why only mobile was affected). Force the muted state onto
  // the element and kick playback off imperatively. The promise rejection is
  // swallowed for the rare case autoplay is still blocked (e.g. iOS Low Power
  // Mode), where the browser's own play button is the correct fallback.
  useEffect(() => {
    const v = bannerVideoRef.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute("muted", "");
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, []);

  // The "Skip" control is pinned to the bottom of the VIEWPORT (not the video,
  // which is taller than the screen), so a visitor watching the banner sees it
  // without scrolling. It fades out once they've scrolled into the site—or been
  // carried in when the clip ends.
  useEffect(() => {
    const onScroll = () => {
      setShowSkip(window.scrollY < window.innerHeight * 0.55);
      // Mobile: once they've scrolled past the (short) intro, the Skip control
      // is retired for good so it can never float over later sections.
      if (window.scrollY > 48) setIntroDone(true);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    listReports({ featured: true }).then(setReports).catch(() => setReports([]));
    getInsightSeries().then(setSeries).catch(() => setSeries([]));
  }, []);

  // Visibility/animation strategy:
  //   1. Modern Chromium: CSS `animation-timeline: view()` drives the scroll-
  //      coupled reveal. JS does NOT touch the elements—adding .is-in-view
  //      would override the animation (it uses !important).
  //   2. Browsers without animation-timeline: IntersectionObserver adds
  //      .is-in-view on first entry, which triggers the transition-based reveal.
  //   3. Stuck-recovery safety net: a short polling loop (1s × up to 5s) that
  //      ONLY rescues elements actually in the viewport but still at opacity 0
  //     —i.e., the animation silently failed. Never fires preemptively.
  useEffect(() => {
    const SELECTOR =
      ".lens-intro, .lens-card, .ds-intro, .philosophy-eyebrow, " +
      ".philosophy-line, .philosophy-rule, .philosophy-body, .access-label-shard";
    const supportsTimeline = CSS.supports && CSS.supports("animation-timeline: view()");

    // (2) IO fallback for browsers without animation-timeline.
    let io;
    if (!supportsTimeline) {
      const targets = document.querySelectorAll(SELECTOR);
      if (targets.length) {
        io = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting) {
                e.target.classList.add("is-in-view");
                io.unobserve(e.target);
              }
            });
          },
          { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
        );
        targets.forEach((t) => io.observe(t));
      }
    }

    // (3) Stuck-recovery—runs for up to 5 seconds, checks once per second.
    // Only force-applies .is-in-view to elements that are CURRENTLY in the
    // viewport AND computed opacity < 0.05 (i.e. stuck invisible).
    let ticks = 0;
    const recovery = window.setInterval(() => {
      ticks += 1;
      document.querySelectorAll(SELECTOR).forEach((el) => {
        if (el.classList.contains("is-in-view")) return;
        const rect = el.getBoundingClientRect();
        const inViewport =
          rect.top < window.innerHeight * 0.95 && rect.bottom > window.innerHeight * 0.05;
        if (!inViewport) return;
        const opacity = parseFloat(window.getComputedStyle(el).opacity);
        if (opacity < 0.05) {
          el.classList.add("is-in-view");
        }
      });
      if (ticks >= 5) window.clearInterval(recovery);
    }, 1000);

    return () => {
      window.clearInterval(recovery);
      if (io) io.disconnect();
    };
  }, [series]);

  // The airport-search prompt's color is pinned directly to scroll position
  // (not a threshold + timed transition) so it's always exactly as bright as
  // how far past the big headline the visitor has scrolled—a fast flick
  // and a slow scroll both land on the correct shade, not a delayed one.
  useEffect(() => {
    const labelEl = document.querySelector(".airport-combobox__label");
    const sectionEl = document.querySelector(".access-section");
    if (!labelEl || !sectionEl) return;
    const DEEP = [0x0e, 0x10, 0x13]; // near-black, deliberately barely-there at rest
    const SILVER = [0xc2, 0xcc, 0xd6]; // distinctly cool gray-silver, not flat white
    let ticking = false;
    const update = () => {
      ticking = false;
      const rect = labelEl.getBoundingClientRect();
      const vh = window.innerHeight || 800;
      // Tracks the prompt's own position (not the headline's) so it can
      // never "miss its window": stays fully dark while it first arrives
      // low in the viewport—while the headline above it still owns the
      // visitor's attention—then brightens as continued scrolling carries
      // it up toward the top third of the screen, roughly 2.5 scroll-wheel
      // notches' worth of extra hang time before it starts, spread over a
      // longer stretch of scroll with an eased curve so the change reads as
      // a slow bloom rather than a snap.
      const start = vh * 0.62;
      const range = vh * 0.55;
      const raw = Math.min(Math.max((start - rect.top) / range, 0), 1);
      const t = raw * raw * (3 - 2 * raw); // smoothstep: gentle at both ends
      const rgb = DEEP.map((c, i) => Math.round(c + (SILVER[i] - c) * t));
      sectionEl.style.setProperty("--label-color", `rgb(${rgb.join(",")})`);
    };
    const onScroll = () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <Layout noPad>
      <SEO
        title="Steck Aviation Management | Private Aircraft Management, East Texas"
        description="Steck Aviation Management provides private aircraft ownership and management services for owners and corporate flight departments. Based in Mineola, Texas."
        schema={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Steck Aviation Management LLC",
          "description": "Private aircraft management and acquisition services for owners and corporate flight departments.",
          "url": "https://www.steckaviationllc.com",
          "telephone": "+19035399000",
          "email": "contact@steckaviationllc.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "1251 Bromberg Street",
            "addressLocality": "Mineola",
            "addressRegion": "TX",
            "postalCode": "75773",
            "addressCountry": "US"
          },
          "founder": { "@type": "Person", "name": "Brian Steck" },
          "areaServed": "United States",
          "knowsAbout": ["aircraft management", "private aviation", "aircraft acquisition", "Beechjet 400A"],
          "logo": "https://www.steckaviationllc.com/favicon-512.png",
          "image": "https://www.steckaviationllc.com/images/og-steck-aviation.jpg",
          "geo": { "@type": "GeoCoordinates", "latitude": 32.6626, "longitude": -95.4894 }
        }}
      />

      {/* Video + Skip wrapped on solid black, so the gap where the in-flow
          mobile Skip control sits reads black rather than the brand navy. */}
      <div className="home-hero-wrap">
      {/* VIDEO BANNER—plays once; when it finishes it eases the visitor into
          the site. A "Skip" arrow in the lower gap lets anyone who doesn't want
          the intro jump straight in at any time. */}
      <div className="home-hero-banner" style={{ position: "relative", width: "100%", paddingBottom: "56.25%", overflow: "hidden" }}>
        <video
          ref={bannerVideoRef}
          autoPlay
          muted
          playsInline
          onEnded={handleVideoBannerEnded}
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        >
          <source src="/banner-video.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Skip control—pinned to the bottom of the VIEWPORT (fixed), so it's
          visible while the banner plays without scrolling; fades out once the
          visitor is into the site. */}
      <button
        type="button"
        onClick={() => { setIntroDone(true); if (!openAssessment("skip")) proceedIntoSite(1100); }}
        aria-label="Skip the intro and continue to the site"
        className={`banner-skip${showSkip && !assessmentOpen && !introDone ? "" : " is-hidden"}${introDone ? " intro-done" : ""}`}
        data-testid="banner-skip"
      >
        <span className="banner-skip__txt">Skip Intro</span>
        <ChevronDown size={26} strokeWidth={2.4} aria-hidden />
      </button>
      </div>
      <style>{`
        .home-hero-wrap { background: #000; overflow: hidden; }
        .banner-skip {
          position: fixed; left: 50%; bottom: clamp(24px,3.4vh,48px);
          transform: translateX(-50%); z-index: 60;
          display: inline-flex; align-items: center; gap: 10px;
          padding: 13px 26px;
          border: 1px solid rgba(255,255,255,0.30);
          border-radius: 999px;
          background: rgba(6,10,16,0.42);
          -webkit-backdrop-filter: blur(7px); backdrop-filter: blur(7px);
          color: rgba(255,255,255,0.94); cursor: pointer;
          font-family: 'IBM Plex Mono', monospace; font-size: 13px;
          letter-spacing: 0.22em; text-transform: uppercase;
          box-shadow: 0 6px 26px rgba(0,0,0,0.45);
          transition: opacity 450ms ease, background 220ms ease, border-color 220ms ease, color 220ms ease;
        }
        .banner-skip > svg { animation: banner-skip-bounce 1.9s ease-in-out infinite; }
        .banner-skip:hover {
          background: rgba(6,10,16,0.62);
          border-color: rgba(103,232,249,0.55);
          color: #fff;
        }
        .banner-skip:hover > svg { animation-play-state: paused; }
        .banner-skip:focus-visible {
          outline: 2px solid rgba(103,232,249,0.75); outline-offset: 3px;
        }
        .banner-skip.is-hidden { opacity: 0; pointer-events: none; }
        @keyframes banner-skip-bounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(5px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .banner-skip > svg { animation: none; }
        }
        /* Mobile: frame the hero video below the fixed 88px header, and make the
           Skip control small; retire it entirely once the intro is done. */
        @media (max-width: 767px) {
          .home-hero-banner { margin-top: 88px; }
          /* The video isn't full-screen on phones, so don't float the Skip
             control fixed over the map/search at the bottom of the viewport —
             place it in-flow, centered in the gap directly under the video and
             above the "Everywhere Feels Closer" heading. */
          .banner-skip {
            position: static; left: auto; transform: none;
            display: flex; width: -moz-fit-content; width: fit-content;
            margin: clamp(16px,2.6vh,26px) auto clamp(6px,1.6vh,16px);
            padding: 8px 15px; gap: 6px;
            font-size: 10px; letter-spacing: 0.16em;
            overflow: hidden; max-height: 64px;
            transition: opacity 350ms ease, max-height 350ms ease, margin 350ms ease, padding 350ms ease;
          }
          .banner-skip > svg { width: 17px; height: 17px; }
          /* Collapse the in-flow control away entirely once dismissed (scroll /
             skip / video-end / survey), so it leaves no empty gap under the
             video — desktop's fixed control just fades. */
          .banner-skip.is-hidden, .banner-skip.intro-done {
            opacity: 0; pointer-events: none;
            max-height: 0; margin-top: 0; margin-bottom: 0;
            padding-top: 0; padding-bottom: 0; border-width: 0;
          }
        }
      `}</style>

      {/* Two Doors — the unified experience. Surfaces once after the video
          concludes / is skipped / scrolled past; thereafter the collapsed module
          in the map's title section lets the visitor re-open it. */}
      <TwoDoorsExperience
        open={assessmentOpen}
        onClose={closeAssessment}
        onComplete={handleAssessmentComplete}
      />
      {/* Phones: fixed reopen tab in the old "Gauge your needs" spot. Desktop
          uses the in-flow module beside the title instead (hidden on phones). */}
      <TwoDoorsCollapsed mobileTab visible={assessmentTabVisible} onOpen={openTwoDoors} />

      {/* Scroll target for the Skip arrow / auto-advance when the clip ends. */}
      <div id="home-after-video" aria-hidden style={{ scrollMarginTop: 88 }} />
      <EastTexasAccess
        heroAside={<TwoDoorsCollapsed visible={assessmentTabVisible} onOpen={openTwoDoors} />}
      />


      {/* AN EXPERIENCE LIKE NO OTHER */}
      {/* Deep-blue colorway restored—but now as a fully OPAQUE, intentional
          gradient rather than the old translucent veil. The blue people liked
          used to come from the McKinsey navy (#051C2C) page-root bleeding
          THROUGH a translucent top; the trouble was that same translucency
          also let the map's uneven edge-glow and the disclaimer curtain show
          through as blotches. Painting the blue directly (opaque) gives the
          same blue-to-black bloom with none of that: it starts at the map
          section's own near-black (#050607) so the seam stays invisible,
          blooms into a soft deep blue in the empty band below the disclaimer,
          then settles to pure black well before the heading. */}
      <section style={{ background: "linear-gradient(to bottom, #050607 0px, #0b2c46 clamp(80px,9vw,130px), #0a2338 clamp(150px,17vw,240px), #05101c clamp(280px,30vw,420px), #000 clamp(430px,44vw,620px))", padding: "clamp(140px,16vw,240px) clamp(24px,6vw,120px) clamp(56px,7vw,96px)" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>
          <h2 style={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
            fontWeight: 700, fontSize: "clamp(36px,5vw,72px)", lineHeight: 1.05,
            letterSpacing: "-0.04em", color: "#fff",
            margin: "0 0 clamp(36px,5vh,56px)",
          }}>
            An Experience<br />
            <span style={{ color: "rgba(255,255,255,0.30)" }}>Like No Other.</span>
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(32px,5vw,96px)", alignItems: "start" }}>
            <p style={{ fontSize: "clamp(16px,1.3vw,19px)", color: "rgba(255,255,255,0.72)", lineHeight: 1.75, margin: 0, fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif" }}>
              Steck Aviation is a family-owned operation rooted in East Texas, offering something few in the industry can match: true end-to-end aviation expertise. From aircraft sales and management to flight testing, maintenance, and airport development, our family's combined experience spans every corner of aviation.
            </p>
            <div>
              <div style={{ width: 32, height: 1, background: "rgba(200,168,112,0.6)", marginBottom: 28 }} />
              <p style={{ fontSize: "clamp(16px,1.3vw,19px)", color: "rgba(255,255,255,0.46)", lineHeight: 1.75, margin: 0, fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif" }}>
                That depth of knowledge revealed a gap in the market—private air travel that's both premium and accessible. So we built it: a better way for businesses and families to fly private, without the excess.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section style={{ background: "#03080F", borderTop: "1px solid #000", paddingTop: "clamp(56px,7vw,96px)" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "0 clamp(24px,6vw,120px)" }}>
          <h2 style={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
            fontWeight: 700, fontSize: "clamp(48px,7.5vw,112px)", lineHeight: 1.0,
            letterSpacing: "-0.045em", color: "#fff",
            margin: "0 0 clamp(44px,6vh,72px)",
          }}>
            Why<br />
            <span style={{ color: "rgba(255,255,255,0.28)" }}>Choose Us.</span>
          </h2>
        </div>

        {/* Credential rows—hairline separated, no cards */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          {[
            {
              label: "SAFETY",
              claim: "It's not just a priority — it's our foundation.",
              detail: "Every decision we make starts with safety. Specialized mechanics, rigorous maintenance protocols, and a zero-compromise standard keep your aircraft in pristine airworthy condition.",
            },
            {
              label: "AVIONICS",
              claim: "Advanced avionics. Every aircraft.",
              detail: "State-of-the-art avionics and advanced safety technology across our fleet—giving you an added layer of confidence the moment you're airborne.",
            },
            {
              label: "PILOTS",
              claim: "Experience that commands confidence.",
              detail: "Our pilots bring deep, professional aviation backgrounds. Every crew is trained, vetted, and held to an uncompromising standard.",
            },
            {
              label: "TRAINING",
              claim: "Recurrent training. Active proficiency.",
              detail: "Every pilot completes recurrent Flight Safety International training for your aircraft type and stays actively current—maintaining real operational proficiency, not just an annual certificate, and backed by industry-leading liability coverage.",
            },
          ].map(({ label, claim, detail }) => (
            <div key={label} style={{
              maxWidth: 1400, margin: "0 auto",
              padding: "clamp(20px,2.5vh,32px) clamp(24px,6vw,120px)",
              display: "grid",
              gridTemplateColumns: "clamp(96px,11vw,132px) 1fr 1fr",
              gap: "clamp(20px,3.5vw,60px)",
              alignItems: "baseline",
              borderBottom: "1px solid rgba(255,255,255,0.07)",
            }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "clamp(13px,1.15vw,16px)", letterSpacing: "0.11em", color: "rgba(200,168,112,0.85)", fontWeight: 600 }}>
                {label}
              </div>
              <div style={{
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
                fontWeight: 600, fontSize: "clamp(17px,1.6vw,24px)",
                color: "#fff", lineHeight: 1.25, letterSpacing: "-0.025em",
              }}>
                {claim}
              </div>
              <p style={{ fontSize: "clamp(13px,1vw,15px)", color: "rgba(255,255,255,0.46)", lineHeight: 1.80, margin: 0, fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif" }}>
                {detail}
              </p>
            </div>
          ))}
        </div>

        <div style={{ height: "clamp(48px,6vh,72px)" }} />
      </section>

      {/* HERO—McKinsey-coded: serif sentence-case headline + floating cinematic frame behind */}
      <section className="relative min-h-[88vh] pt-[88px] flex items-center hero-section overflow-hidden">
        {/* Full-bleed turbine background—sits at the lowest z layer, below the floating
            video frame. Dark enough by nature that text stays fully legible; gradient
            overlay drives it to near-black at the edges. */}
        <div aria-hidden style={{ position: "absolute", inset: 0, zIndex: 0, overflow: "hidden" }}>
          <img
            src="/images/engine-turbine.png"
            alt=""
            className="engine-breathe"
            style={{
              width: "100%", height: "100%",
              objectFit: "contain",
              objectPosition: "right top",
              opacity: 0.80,
              WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 74%, rgba(0,0,0,0.5) 84%, transparent 94%)",
              maskImage: "linear-gradient(to bottom, black 0%, black 74%, rgba(0,0,0,0.5) 84%, transparent 94%)",
            }}
          />
          <div style={{
            position: "absolute", inset: 0,
            background: [
              "linear-gradient(to right, rgba(5,28,44,1.0) 0%, rgba(5,28,44,1.0) 30%, rgba(5,28,44,0.55) 42%, rgba(5,28,44,0.18) 54%, rgba(5,28,44,0.04) 64%, transparent 74%)",
              "linear-gradient(to bottom, rgba(5,28,44,0.45) 0%, transparent 20%, transparent 58%, rgba(5,28,44,0.20) 70%, rgba(5,28,44,0.50) 82%, rgba(5,28,44,0.88) 93%, rgba(5,28,44,1.0) 100%)",
            ].join(", "),
          }} />
        </div>


        <div className="relative z-10 max-w-[1500px] mx-auto px-6 lg:px-12 w-full grid grid-cols-1 md:grid-cols-12 gap-10 py-24">
          <div className="md:col-span-10">
            <h1 className="font-display-lg text-[var(--white)] text-5xl md:text-7xl lg:text-[5.6rem] max-w-5xl" style={{ fontWeight: 800 }}>
              <span className="hero-cascade hero-cascade--1">Aircraft ownership,</span>
              <br />
              <span className="text-[var(--slate)] hero-cascade hero-cascade--2">managed properly.</span>
            </h1>
            <p className="mt-10 max-w-2xl text-xl text-[var(--ivory-dim)] leading-[1.6] hero-cascade hero-cascade--3">
              Steck Aviation Management coordinates every detail of ownership so you can
              focus on what matters most—your destination. We also publish
              research for owners navigating the decisions that surround it.
            </p>
            <div className="mt-12 flex flex-wrap gap-4 hero-cascade hero-cascade--4">
              <Link to="/about" className="btn-primary" data-testid="home-cta-management">
                Discover our approach <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          <div className="hidden md:block md:col-span-2" />
        </div>


        {/* Cinematic floating frame—full-frame video, all-sides feather, Apple-style float */}
        <style>{`
          /* ===== Hero mount cascade + scroll-coupled exit =====
             On page load the headline, subtitle, CTA and stage indicators
             rise into place in sequence. As the user scrolls past the hero,
             the cinematic frame gently scales up and drifts down while the
             text fades and softens—giving the section a sense of depth
             without obscuring the content behind. */
          .hero-cascade {
            display: inline-block;
            will-change: transform, opacity, filter;
            animation: hero-rise 1000ms var(--ease-decisive) both;
          }
          /* For block-level cascade items (paragraph, button row, stage block) */
          p.hero-cascade,
          div.hero-cascade {
            display: block;
          }

          @keyframes engine-breathe {
            0%, 100% { transform: scale(1.0); }
            50%       { transform: scale(1.018); }
          }
          .engine-breathe {
            animation: engine-breathe 16s ease-in-out infinite;
            transform-origin: right center;
            will-change: transform;
          }
          .hero-nav-card { cursor: pointer; }
          .hero-nav-card__line {
            height: 2px; width: 100%; border-radius: 1px;
            background: rgba(103,232,249,0.22);
            transition: background 380ms ease, box-shadow 380ms ease;
          }
          .hero-nav-card:hover .hero-nav-card__line {
            background: rgba(103,232,249,0.72);
            box-shadow: 0 0 8px 1px rgba(103,232,249,0.30), 0 0 22px 5px rgba(103,232,249,0.09);
          }
          .hero-cascade--1 { animation-delay: 80ms; }
          .hero-cascade--2 { animation-delay: 260ms; }
          .hero-cascade--3 { animation-delay: 480ms; }
          .hero-cascade--4 { animation-delay: 660ms; }
          .hero-cascade--5 { animation-delay: 880ms; }
          @keyframes hero-rise {
            0%   {
              opacity: 0;
              transform: translateY(22px);
              filter: blur(6px);
            }
            60%  {
              filter: blur(0);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
              filter: blur(0);
            }
          }
          @media (prefers-reduced-motion: reduce) {
            .hero-cascade { animation: none; opacity: 1; transform: none; filter: none; }
          }

          /* Scroll-coupled exit—the cinematic frame settles into depth as
             the visitor scrolls down through the hero. Browsers without
             animation-timeline simply skip this (the frame stays static). */
          @supports (animation-timeline: view()) {
            .hero-frame-anchor {
              animation: hero-frame-depart linear both;
              animation-timeline: view();
              animation-range: exit 0% exit 90%;
            }
            @keyframes hero-frame-depart {
              0%   { transform: translateY(0) scale(1);      opacity: 1;   filter: blur(0); }
              100% { transform: translateY(48px) scale(1.04); opacity: 0.55; filter: blur(2px); }
            }
          }
          @media (prefers-reduced-motion: reduce) {
            .hero-frame-anchor { animation: none !important; }
          }

          .hero-frame-wrap {
            position: absolute;
            left: 0;
            right: 0;
            bottom: 25%;
            display: flex;
            justify-content: center;
            z-index: 0;
            pointer-events: none;
          }
          .hero-frame {
            position: relative;
            width: 100%;
            aspect-ratio: 32 / 9;
            overflow: hidden;
            isolation: isolate;
          }
          .hero-frame__video {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center center;
            filter:
              contrast(1.14)
              saturate(0.78)
              brightness(0.86)
              hue-rotate(-2deg);
          }
          /* Per-clip blur removed—single clip on loop */
          .hero-frame__grade {
            position: absolute;
            inset: 0;
            background:
              linear-gradient(180deg,
                rgba(3,16,28,0.35) 0%,
                rgba(3,16,28,0.05) 35%,
                rgba(3,16,28,0.05) 65%,
                rgba(3,16,28,0.35) 100%),
              linear-gradient(135deg,
                rgba(18,52,82,0.18) 0%,
                rgba(8,14,22,0.0) 50%,
                rgba(112,68,28,0.14) 100%);
            pointer-events: none;
          }
          .hero-frame__grain {
            position: absolute;
            inset: -6%;
            background-image: url("data:image/svg+xml;utf8,%3Csvg viewBox='0 0 320 320' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.92' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.55 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
            opacity: 0.22;
            mix-blend-mode: overlay;
            pointer-events: none;
            animation: hero-frame-grain 1.4s steps(4) infinite;
          }
          @keyframes hero-frame-grain {
            0%   { transform: translate(0,0); }
            25%  { transform: translate(-1.4%, 1%); }
            50%  { transform: translate(1%, -1.2%); }
            75%  { transform: translate(-1%, 1.4%); }
            100% { transform: translate(0,0); }
          }
          .hero-frame__scanlines {
            position: absolute;
            inset: 0;
            background-image: repeating-linear-gradient(
              0deg,
              rgba(0,0,0,0.16) 0px,
              rgba(0,0,0,0.16) 1px,
              transparent 1px,
              transparent 3px
            );
            opacity: 0.06;
            mix-blend-mode: multiply;
            pointer-events: none;
          }
          .hero-frame__vignette {
            position: absolute;
            inset: 0;
            background:
              radial-gradient(120% 80% at 50% 50%,
                transparent 0%,
                rgba(3,7,13,0.14) 60%,
                rgba(3,7,13,0.40) 100%);
            pointer-events: none;
          }
          @media (max-width: 768px) {
            .hero-frame-wrap { bottom: 50%; padding: 0 16px; }
          }
          @media (prefers-reduced-motion: reduce) {
            .hero-frame__grain { animation: none; }
          }
        `}</style>
      </section>

      {/* PHILOSOPHY—centered manifesto block, shifted down to sit evenly between the video and the lens tiles */}
      <section className="relative pt-[88px] pb-20 md:pb-28 philosophy-section">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="font-mono-label mb-5 philosophy-eyebrow">Our philosophy</div>
            <h2 className="font-display-lg text-4xl md:text-5xl text-[var(--white)] leading-[1.05]">
              <span className="philosophy-line philosophy-line--1">Ownership is more than an asset.</span>
              <br />
              <span className="text-[var(--slate)] philosophy-line philosophy-line--2">It is a responsibility.</span>
            </h2>
            <div className="philosophy-rule" aria-hidden />
            <p className="mt-8 text-[var(--ivory-dim)] text-lg leading-[1.8] philosophy-body">
              We bring clarity, structure, and precision to every aspect of
              aircraft ownership. We are a management company—not a charter
              operator—and we work exclusively for owners.
            </p>
          </div>
        </div>

        <style>{`
          /* Deep-space atmosphere echo—carries the map section's faint
             grain + soft top-glow down into this section so the page reads
             as one continuous night scene rather than a lit map bolted above
             flat black. Static and very low-opacity (restraint, not
             spectacle); an isolate + z-index pair keeps the text crisply
             above it. */
          .philosophy-section { isolation: isolate; }
          .philosophy-section > div { position: relative; z-index: 1; }
          .philosophy-section::before {
            content: ""; position: absolute; inset: 0; z-index: 0; pointer-events: none;
            background: radial-gradient(ellipse 46% 42% at 50% 4%, rgba(78,112,158,0.055) 0%, transparent 66%);
          }
          .philosophy-section::after {
            content: ""; position: absolute; inset: 0; z-index: 0; pointer-events: none;
            background-image: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23g)'/%3E%3C/svg%3E");
            background-size: 240px 240px; mix-blend-mode: overlay; opacity: 0.022;
          }
          /* ===== Philosophy signature reveal =====
             Distinct from the lens-tile entrance. Each line of the headline
             arrives with a subtle letter-spacing settle (text "tightens
             into focus") instead of a translate+scale. A centered hairline
             rule between heading and body grows OUTWARD from center as the
             section enters view—the unique signature of this section. */
          .philosophy-eyebrow,
          .philosophy-line,
          .philosophy-rule,
          .philosophy-body {
            will-change: transform, opacity, letter-spacing;
          }
          .philosophy-line { display: inline-block; }
          .philosophy-rule {
            margin: 28px auto 0;
            height: 1px;
            width: 80px;
            background: linear-gradient(90deg,
              transparent 0%,
              rgba(255,255,255,0.18) 30%,
              rgba(255,255,255,0.45) 50%,
              rgba(255,255,255,0.18) 70%,
              transparent 100%);
          }

          @supports (animation-timeline: view()) {
            .philosophy-eyebrow {
              opacity: 0;
              transform: translateY(8px);
              animation: philosophy-eyebrow linear both;
              animation-timeline: view();
              animation-range: entry 4% cover 18%;
            }
            .philosophy-line--1 {
              opacity: 0;
              transform: translateY(18px);
              letter-spacing: 0.04em;
              animation: philosophy-settle-1 linear both;
              animation-timeline: view();
              animation-range: entry 8% cover 26%;
            }
            .philosophy-line--2 {
              opacity: 0;
              transform: translateY(18px);
              letter-spacing: 0.04em;
              animation: philosophy-settle-2 linear both;
              animation-timeline: view();
              animation-range: entry 16% cover 36%;
            }
            .philosophy-rule {
              width: 0;
              opacity: 0;
              animation: philosophy-rule-grow linear both;
              animation-timeline: view();
              animation-range: entry 22% cover 42%;
            }
            .philosophy-body {
              opacity: 0;
              transform: translateY(14px);
              animation: philosophy-body-in linear both;
              animation-timeline: view();
              animation-range: entry 28% cover 50%;
            }

            @keyframes philosophy-eyebrow {
              0%   { opacity: 0; transform: translateY(8px); }
              100% { opacity: 1; transform: translateY(0); }
            }
            @keyframes philosophy-settle-1 {
              0%   { opacity: 0; transform: translateY(18px); letter-spacing: 0.04em; }
              100% { opacity: 1; transform: translateY(0);    letter-spacing: 0; }
            }
            @keyframes philosophy-settle-2 {
              0%   { opacity: 0; transform: translateY(18px); letter-spacing: 0.04em; }
              100% { opacity: 1; transform: translateY(0);    letter-spacing: 0; }
            }
            @keyframes philosophy-rule-grow {
              0%   { width: 0;    opacity: 0; }
              100% { width: 80px; opacity: 1; }
            }
            @keyframes philosophy-body-in {
              0%   { opacity: 0; transform: translateY(14px); }
              100% { opacity: 1; transform: translateY(0); }
            }
          }

          /* Safety net (IO + 2.5s timer adds .is-in-view) */
          .philosophy-eyebrow.is-in-view,
          .philosophy-line.is-in-view,
          .philosophy-body.is-in-view {
            opacity: 1 !important;
            transform: translateY(0) !important;
            letter-spacing: 0 !important;
            transition: opacity 800ms var(--ease-decisive),
                        transform 800ms var(--ease-decisive),
                        letter-spacing 800ms var(--ease-decisive);
          }
          .philosophy-rule.is-in-view {
            opacity: 1 !important;
            width: 80px !important;
            transition: opacity 800ms var(--ease-decisive),
                        width 800ms var(--ease-decisive);
          }

          @media (prefers-reduced-motion: reduce) {
            .philosophy-eyebrow,
            .philosophy-line,
            .philosophy-rule,
            .philosophy-body {
              opacity: 1 !important;
              transform: none !important;
              letter-spacing: 0 !important;
              animation: none !important;
            }
            .philosophy-rule { width: 80px !important; }
          }
        `}</style>
      </section>

      {/* INSIGHT SERIES—Seven lenses; title block + tiles enter the frame scroll-coupled (Apple-style) */}
      <section
        className="relative pt-10 md:pt-16 pb-24 md:pb-32 overflow-visible lenses-section"
        data-testid={HOME.insightSeries}
      >
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4 lens-intro">
            <h2 className="font-display text-4xl md:text-5xl text-[var(--white)] leading-tight">
              Seven lenses on ownership.
            </h2>
            <p className="mt-7 text-[var(--slate)] leading-[1.8] max-w-md">
              Our research is organized around the questions owners
              ask—economics, mission, control, regulation, and the
              infrastructure that surrounds them.
            </p>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5 lens-grid">
            {series.map((s, idx) => (
              <Link
                to={`/library?category=${encodeURIComponent(s)}`}
                key={s}
                className="lens-card group"
                data-testid={`series-${s.toLowerCase().replace(/\s+/g, "-")}`}
                style={{ "--card-i": idx }}
              >
                <div className="lens-card__number">0{idx + 1}</div>
                <div className="lens-card__title">{s}</div>
                <div className="lens-card__action">
                  <span>Explore</span>
                  <ArrowRight size={12} className="lens-card__arrow" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        <style>{`
          /* ===== Apple-style scroll-coupled entry =====
             Default state is fully visible—so on any browser that does not
             support animation-timeline (or where the animation never fires
             for any reason), content stays readable. Modern Chromium opts
             into the staged blur+rise via @supports below. */

          .lens-intro,
          .lens-card {
            will-change: transform, opacity, filter;
          }

          @supports (animation-timeline: view()) {
            .lens-intro,
            .lens-card {
              opacity: 0;
              transform: translateY(64px) scale(0.965);
              filter: blur(8px);
              animation: lens-scroll-in linear both;
              animation-timeline: view();
              animation-range: entry 4% cover 28%;
            }
            @keyframes lens-scroll-in {
              0% {
                opacity: 0;
                transform: translateY(64px) scale(0.965);
                filter: blur(8px);
              }
              100% {
                opacity: 1;
                transform: translateY(0) scale(1);
                filter: blur(0);
              }
            }
          }

          /* Reduced-motion users skip the choreography */
          @media (prefers-reduced-motion: reduce) {
            .lens-intro,
            .lens-card {
              opacity: 1 !important;
              transform: none !important;
              filter: none !important;
              animation: none !important;
            }
          }

          /* Fallback class for browsers without animation-timeline (set via JS),
             also used by the 2.5s safety-net timer. !important guards against
             a stuck @supports rule keeping opacity at 0 on a flaky browser. */
          .lens-intro.is-in-view,
          .lens-card.is-in-view {
            opacity: 1 !important;
            transform: translateY(0) scale(1) !important;
            filter: blur(0) !important;
            transition: opacity 900ms var(--ease-decisive),
                        transform 900ms var(--ease-decisive),
                        filter 900ms var(--ease-decisive);
          }

          /* ===== Lens grid: top edge fades into the section above (no hairline divider) ===== */
          .lens-grid {
            position: relative;
            margin-top: -120px;
            padding-top: 120px;
            -webkit-mask-image: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.35) 12%, rgba(0,0,0,0.75) 28%, #000 48%);
                    mask-image: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.35) 12%, rgba(0,0,0,0.75) 28%, #000 48%);
          }
          .lens-grid::before {
            content: "";
            position: absolute;
            inset: -40px -40px 40px -40px;
            background:
              radial-gradient(80% 35% at 50% 18%, rgba(34,81,255,0.10) 0%, rgba(34,81,255,0.03) 35%, transparent 70%),
              radial-gradient(60% 50% at 50% 60%, rgba(8,22,38,0.45) 0%, transparent 70%);
            filter: blur(28px);
            pointer-events: none;
            z-index: 0;
          }
          .lens-grid > * { position: relative; z-index: 1; }

          .lens-card {
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            min-height: 200px;
            padding: 28px 32px 32px;
            background:
              linear-gradient(155deg, rgba(8,22,38,0.95) 0%, rgba(2,8,16,1) 60%, rgba(0,0,0,1) 100%);
            border: 1px solid rgba(255,255,255,0.06);
            box-shadow:
              0 32px 70px -18px rgba(0,0,0,0.75),
              0 8px 24px -6px rgba(0,0,0,0.55),
              inset 0 1px 0 rgba(255,255,255,0.05),
              inset 0 -1px 0 rgba(0,0,0,0.4);
            overflow: hidden;
            isolation: isolate;
          }
          /* Noise texture */
          .lens-card::before {
            content: "";
            position: absolute;
            inset: 0;
            background-image: url("data:image/svg+xml;utf8,%3Csvg viewBox='0 0 240 240' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.6 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
            opacity: 0.05;
            mix-blend-mode: overlay;
            pointer-events: none;
            z-index: 0;
          }
          /* Soft top-light glow that activates on hover */
          .lens-card::after {
            content: "";
            position: absolute;
            inset: 0;
            background:
              radial-gradient(120% 70% at 50% -10%, rgba(34,81,255,0.18) 0%, rgba(34,81,255,0.04) 35%, transparent 65%);
            opacity: 0;
            transition: opacity 500ms ease;
            pointer-events: none;
            z-index: 0;
          }
          .lens-card:hover {
            transform: translateY(-3px);
            border-color: rgba(120,150,255,0.28);
            box-shadow:
              0 40px 80px -20px rgba(0,0,0,0.75),
              0 8px 20px -4px rgba(0,0,0,0.55),
              0 0 0 1px rgba(34,81,255,0.10),
              inset 0 1px 0 rgba(255,255,255,0.10),
              inset 0 -1px 0 rgba(0,0,0,0.4);
          }
          .lens-card:hover::after { opacity: 1; }

          .lens-card > * { position: relative; z-index: 1; }

          .lens-card__number {
            font-family: 'JetBrains Mono', monospace;
            font-size: 11px;
            letter-spacing: 0.14em;
            color: var(--slate-dim);
            margin-bottom: 14px;
            transition: color 400ms ease;
          }
          .lens-card:hover .lens-card__number {
            color: var(--blue-soft);
          }
          .lens-card__title {
            font-family: 'Fraunces', serif;
            font-size: clamp(22px, 1.9vw, 28px);
            line-height: 1.18;
            color: var(--ivory);
            transition: color 400ms ease, transform 500ms var(--ease-decisive);
          }
          .lens-card:hover .lens-card__title {
            color: var(--white);
            transform: translateY(-1px);
          }
          .lens-card__action {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            margin-top: 28px;
            font-size: 12px;
            letter-spacing: 0.04em;
            color: var(--slate);
            transition: color 400ms ease;
          }
          .lens-card:hover .lens-card__action {
            color: var(--blue-soft);
          }
          .lens-card__arrow {
            transition: transform 500ms var(--ease-decisive);
          }
          .lens-card:hover .lens-card__arrow {
            transform: translateX(5px);
          }
        `}</style>
      </section>

      {/* RESEARCH—Featured · deep-space backdrop, tertiary palette ---------- */}
      <section
        id="featured"
        className="relative pt-24 pb-32 deep-space"
        data-testid={HOME.featuredReports}
      >
        <div className="relative z-10 max-w-[1500px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-14 ds-intro">
            <div className="md:col-span-8">
              <div className="font-mono-label mb-5 inline-flex items-center gap-3" style={{ color: "var(--space-dim)" }}>
                <span className="ds-pulse" aria-hidden />
                <span>Research</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl leading-tight" style={{ color: "var(--space-ink)" }}>
                Owner-focused{" "}
                <span className="ds-spark-wrap">
                  analysis
                  <span className="ds-spark ds-spark--a" aria-hidden />
                  <span className="ds-spark ds-spark--b" aria-hidden />
                  <span className="ds-spark ds-spark--c" aria-hidden />
                </span>
                .
              </h2>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/library" className="btn-link" style={{ color: "var(--space-neon)" }}>
                All research <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <ul className="ds-list border-t" style={{ borderColor: "rgba(180,210,255,0.10)" }}>
            {/* Every featured report renders here—no cap—so this list
                stays a strict superset match of the Research library rather
                than silently dropping whichever article sorts last. */}
            {reports.map((r, i) => (
              <li key={r.id} className="ds-row" style={{ borderColor: "rgba(180,210,255,0.07)" }}>
                <Link
                  to={`/reports/${r.slug}`}
                  data-testid={`featured-card-${r.slug}`}
                  className="ds-row__link group block py-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-start -mx-4 px-4"
                >
                  <span className="ds-row__bar" aria-hidden />
                  <div className="md:col-span-2 font-mono-label ds-row__cat" style={{ color: "var(--space-accent)" }}>
                    {r.category}
                  </div>
                  <div className="md:col-span-7">
                    <h3 className="font-display text-2xl md:text-3xl leading-tight ds-row__title" style={{ color: "var(--space-ink)" }}>
                      {r.title}
                    </h3>
                    {r.summary && (
                      <p className="mt-3 leading-relaxed max-w-2xl" style={{ color: "var(--space-body)" }}>
                        {r.summary}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-3 flex md:justify-end items-center">
                    <span className="ds-row__cta text-[14px] font-medium inline-flex items-center gap-2" style={{ color: "var(--space-ink)" }}>
                      Read article <ArrowRight size={14} className="ds-row__cta-arrow" />
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <style>{`
          /* "Deep space intelligence" backdrop—reaches near-black very early
             with a long, soft top feather that erases the join with the navy above.
             A whisper-thin particle field hovers in the transition zone, like
             ambient intelligence dust. */
          .deep-space {
            background:
              linear-gradient(180deg,
                #0B1320 0%,
                #04091300 0%,
                #050B16 4%,
                #01060D 12%,
                #000408 22%,
                #000204 100%);
          }
          /* Long, soft top feather—overlaps the lens section by 280px so the
             two backdrops dissolve into one another, no hairline join. */
          .deep-space::before {
            content: "";
            position: absolute;
            inset: -280px 0 auto 0;
            height: 520px;
            background: linear-gradient(180deg,
              rgba(11,19,32,0) 0%,
              rgba(8,14,24,0.35) 22%,
              rgba(4,9,18,0.80) 50%,
              rgba(1,6,13,0.96) 78%,
              rgba(0,3,7,1) 100%);
            pointer-events: none;
            z-index: 0;
          }
          /* Composed nav-chart fragment—NOT tiled. A single deliberate
             composition spans the transition zone: one range-ring scope
             on the left, a thin dashed great-circle arc sweeping across,
             a small compass mark on the right, and sparse asymmetric
             waypoints between them. Reads as intelligence/nav chart, not
             a repeating overlay. */
          .deep-space::after {
            content: "";
            position: absolute;
            inset: -120px 0 auto 0;
            height: 620px;
            background-image: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 500' preserveAspectRatio='xMidYMid slice'%3E%3Cg stroke='%23B6F2FA' fill='none' stroke-width='0.7' stroke-linecap='round'%3E%3Cg transform='translate(210 240)'%3E%3Ccircle r='14' stroke-opacity='0.65'/%3E%3Ccircle r='28' stroke-opacity='0.32'/%3E%3Ccircle r='46' stroke-opacity='0.16'/%3E%3Ccircle r='68' stroke-opacity='0.07'/%3E%3Cline x1='-20' y1='0' x2='20' y2='0' stroke-opacity='0.55'/%3E%3Cline x1='0' y1='-20' x2='0' y2='20' stroke-opacity='0.55'/%3E%3Cline x1='-46' y1='0' x2='-42' y2='0' stroke-opacity='0.4'/%3E%3Cline x1='42' y1='0' x2='46' y2='0' stroke-opacity='0.4'/%3E%3Cline x1='0' y1='-46' x2='0' y2='-42' stroke-opacity='0.4'/%3E%3Cline x1='0' y1='42' x2='0' y2='46' stroke-opacity='0.4'/%3E%3C/g%3E%3Cpath d='M 280 410 Q 760 60 1420 300' stroke-opacity='0.32' stroke-width='0.8' stroke-dasharray='2 7'/%3E%3Cpath d='M 100 90 Q 520 200 1100 70' stroke-opacity='0.16' stroke-width='0.5' stroke-dasharray='1 9'/%3E%3Cg transform='translate(1310 170)'%3E%3Ccircle r='7' stroke-opacity='0.7'/%3E%3Ccircle r='14' stroke-opacity='0.22'/%3E%3Cline x1='-13' y1='0' x2='-9' y2='0' stroke-opacity='0.55'/%3E%3Cline x1='9' y1='0' x2='13' y2='0' stroke-opacity='0.55'/%3E%3Cline x1='0' y1='-13' x2='0' y2='-9' stroke-opacity='0.55'/%3E%3Cline x1='0' y1='9' x2='0' y2='13' stroke-opacity='0.55'/%3E%3C/g%3E%3Cg stroke-opacity='0.5'%3E%3Cline x1='460' y1='118' x2='468' y2='118'/%3E%3Cline x1='464' y1='114' x2='464' y2='122'/%3E%3Cline x1='710' y1='448' x2='718' y2='448'/%3E%3Cline x1='714' y1='444' x2='714' y2='452'/%3E%3Cline x1='980' y1='198' x2='988' y2='198'/%3E%3Cline x1='984' y1='194' x2='984' y2='202'/%3E%3C/g%3E%3Cg stroke-opacity='0.3'%3E%3Cline x1='620' y1='62' x2='624' y2='62'/%3E%3Cline x1='1180' y1='420' x2='1184' y2='420'/%3E%3Cline x1='860' y1='320' x2='864' y2='320'/%3E%3C/g%3E%3C/g%3E%3Cg fill='%23B6F2FA' stroke='none'%3E%3Ccircle cx='540' cy='268' r='0.95' fill-opacity='0.85'/%3E%3Ccircle cx='1080' cy='384' r='0.7' fill-opacity='0.78'/%3E%3Ccircle cx='870' cy='98' r='0.6' fill-opacity='0.72'/%3E%3Ccircle cx='380' cy='160' r='0.5' fill-opacity='0.65'/%3E%3Ccircle cx='1220' cy='340' r='0.55' fill-opacity='0.7'/%3E%3C/g%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-size: 100% 100%;
            opacity: 0.48;
            mix-blend-mode: screen;
            -webkit-mask-image: linear-gradient(180deg,
              transparent 0%,
              rgba(0,0,0,0.45) 18%,
              rgba(0,0,0,0.95) 42%,
              rgba(0,0,0,0.4) 70%,
              transparent 100%);
                    mask-image: linear-gradient(180deg,
              transparent 0%,
              rgba(0,0,0,0.45) 18%,
              rgba(0,0,0,0.95) 42%,
              rgba(0,0,0,0.4) 70%,
              transparent 100%);
            pointer-events: none;
            z-index: 0;
          }
          /* Tertiary palette tuned for the deeper backdrop */
          .deep-space {
            --space-ink: #E8EEF6;
            --space-body: #8FA3B8;
            --space-dim: #6E839B;
            --space-faint: #3F526A;
            --space-accent: #94B0FF;
            --space-neon: #67E8F9; /* electric cyan—the tertiary pop */
          }

          /* Pulsing neon dot beside the "Research" label */
          .ds-pulse {
            width: 6px;
            height: 6px;
            border-radius: 999px;
            background: var(--space-neon);
            box-shadow:
              0 0 0 0 rgba(103,232,249,0.55),
              0 0 14px 2px rgba(103,232,249,0.45);
            animation: ds-pulse 2.6s ease-out infinite;
          }
          @keyframes ds-pulse {
            0%   { box-shadow: 0 0 0 0 rgba(103,232,249,0.55), 0 0 14px 2px rgba(103,232,249,0.45); }
            70%  { box-shadow: 0 0 0 10px rgba(103,232,249,0), 0 0 14px 2px rgba(103,232,249,0.10); }
            100% { box-shadow: 0 0 0 0 rgba(103,232,249,0), 0 0 14px 2px rgba(103,232,249,0.45); }
          }

          /* Apple-style scroll-coupled entry for the title block.
             Default visible—only animation-timeline-capable browsers opt in. */
          .ds-intro {
            will-change: transform, opacity, filter;
          }
          @supports (animation-timeline: view()) {
            .ds-intro {
              opacity: 0;
              transform: translateY(56px) scale(0.97);
              filter: blur(8px);
              animation: ds-intro-in linear both;
              animation-timeline: view();
              animation-range: entry 4% cover 26%;
            }
            @keyframes ds-intro-in {
              0%   { opacity: 0; transform: translateY(56px) scale(0.97); filter: blur(8px); }
              100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
            }
          }
          .ds-intro.is-in-view {
            opacity: 1 !important;
            transform: translateY(0) scale(1) !important;
            filter: blur(0) !important;
            transition: opacity 900ms var(--ease-decisive),
                        transform 900ms var(--ease-decisive),
                        filter 900ms var(--ease-decisive);
          }
          @media (prefers-reduced-motion: reduce) {
            .ds-intro {
              opacity: 1 !important;
              transform: none !important;
              filter: none !important;
              animation: none !important;
            }
          }

          /* Electric spark—three tiny cyan points that occasionally twitch
             behind the word "intelligence". Long quiet gap between bursts. */
          .ds-spark-wrap {
            position: relative;
            display: inline-block;
            isolation: isolate;
          }
          .ds-spark {
            position: absolute;
            width: 4px;
            height: 4px;
            border-radius: 999px;
            background: #B6F2FA;
            box-shadow:
              0 0 6px rgba(103,232,249,0.95),
              0 0 14px rgba(103,232,249,0.55),
              0 0 28px rgba(103,232,249,0.25);
            opacity: 0;
            pointer-events: none;
            z-index: -1;
            will-change: transform, opacity;
          }
          /* Three sparks fire on a 9s loop with staggered delays so the
             "twitch" feels alive but never busy. */
          .ds-spark--a {
            top: 55%; left: 10%;
            animation: ds-spark-a 9s ease-in-out infinite;
          }
          .ds-spark--b {
            top: 30%; left: 50%;
            animation: ds-spark-b 9s ease-in-out infinite;
            animation-delay: 0.25s;
          }
          .ds-spark--c {
            top: 70%; left: 78%;
            animation: ds-spark-c 9s ease-in-out infinite;
            animation-delay: 0.55s;
          }
          @keyframes ds-spark-a {
            0%, 82%   { opacity: 0; transform: translate(0,0) scale(0.6); }
            83%       { opacity: 1;   transform: translate(0,0) scale(1); }
            84%       { opacity: 0.4; transform: translate(7px,-3px) scale(0.85); }
            85%       { opacity: 0.95;transform: translate(-3px,4px) scale(1.1); }
            86%       { opacity: 0.5; transform: translate(11px,2px) scale(0.9); }
            87%       { opacity: 0.85;transform: translate(-6px,-2px) scale(1); }
            88%       { opacity: 0;   transform: translate(15px,3px) scale(0.6); }
            100%      { opacity: 0; }
          }
          @keyframes ds-spark-b {
            0%, 84%   { opacity: 0; transform: translate(0,0) scale(0.6); }
            85%       { opacity: 0.9; transform: translate(-2px,1px) scale(1.05); }
            86%       { opacity: 0.4; transform: translate(8px,-4px) scale(0.85); }
            87%       { opacity: 0.95;transform: translate(-9px,3px) scale(1.1); }
            88%       { opacity: 0.5; transform: translate(5px,5px) scale(0.9); }
            89%       { opacity: 0;   transform: translate(-12px,-2px) scale(0.55); }
            100%      { opacity: 0; }
          }
          @keyframes ds-spark-c {
            0%, 86%   { opacity: 0; transform: translate(0,0) scale(0.6); }
            87%       { opacity: 0.85;transform: translate(0,-1px) scale(1); }
            88%       { opacity: 0.5; transform: translate(-6px,3px) scale(0.9); }
            89%       { opacity: 0.95;transform: translate(4px,-4px) scale(1.1); }
            90%       { opacity: 0.35;transform: translate(-9px,2px) scale(0.85); }
            91%       { opacity: 0;   transform: translate(8px,5px) scale(0.55); }
            100%      { opacity: 0; }
          }
          @media (prefers-reduced-motion: reduce) {
            .ds-spark { animation: none !important; opacity: 0 !important; }
          }

          /* Article rows—refined hover with electric neon hairline */
          .ds-list > li + li { border-top: 1px solid rgba(180,210,255,0.07); }
          .ds-row__link {
            position: relative;
            transition: background-color 400ms ease;
          }
          .ds-row__bar {
            position: absolute;
            left: -4px;
            top: 16px;
            bottom: 16px;
            width: 2px;
            background: linear-gradient(180deg, transparent, var(--space-neon), transparent);
            box-shadow: 0 0 12px rgba(103,232,249,0.65);
            opacity: 0;
            transform: scaleY(0.4);
            transform-origin: center;
            transition: opacity 400ms ease, transform 500ms var(--ease-decisive);
          }
          .ds-row__link:hover { background-color: rgba(103,232,249,0.025); }
          .ds-row__link:hover .ds-row__bar { opacity: 1; transform: scaleY(1); }
          .ds-row__title { transition: color 400ms ease; }
          .ds-row__link:hover .ds-row__title { color: var(--white); }
          .ds-row__cat { transition: color 400ms ease, letter-spacing 400ms ease; }
          .ds-row__link:hover .ds-row__cat { color: var(--space-neon); letter-spacing: 0.16em; }
          .ds-row__cta { transition: color 400ms ease, gap 400ms ease; }
          .ds-row__link:hover .ds-row__cta { color: var(--space-neon); }
          .ds-row__cta-arrow { transition: transform 400ms var(--ease-decisive); }
          .ds-row__link:hover .ds-row__cta-arrow { transform: translateX(4px); }
        `}</style>
      </section>

      {/* DOCUMENTS—continues the deep-space black -------------- */}
      <section className="relative py-24 deep-space-cont" data-testid={HOME.pdfDownloads}>
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12">
          <div className="font-mono-label mb-5" style={{ color: "#6E839B" }}>Documents</div>
          <h2 className="font-display text-4xl md:text-5xl mb-12" style={{ color: "#E8EEF6" }}>
            Briefing materials.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px" style={{ background: "rgba(180,210,255,0.08)" }}>
            {PDFS.map((pdf) => (
              <a
                key={pdf.title}
                href={pdf.url}
                target="_blank"
                rel="noopener noreferrer"
                className="ds-doc group p-10 transition-colors flex items-start gap-8"
                data-testid={`pdf-${pdf.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="font-mono-label mt-1" style={{ color: "#9AAEC6" }}>{pdf.label}</div>
                <div className="flex-1">
                  <h3 className="font-display text-2xl md:text-3xl transition-colors ds-doc__title" style={{ color: "#E8EEF6" }}>
                    {pdf.title}
                  </h3>
                  <p className="text-sm mt-3 leading-relaxed max-w-md" style={{ color: "#8FA3B8" }}>{pdf.description}</p>
                  <div className="mt-6 inline-flex items-center gap-2 text-[13px] font-medium ds-doc__cta" style={{ color: "#67E8F9" }}>
                    Download PDF <Download size={12} />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

        <style>{`
          .deep-space-cont {
            background: #000204;
          }
          .ds-doc {
            background: #000307;
            position: relative;
            overflow: hidden;
          }
          .ds-doc::before {
            content: "";
            position: absolute;
            inset: 0;
            background: radial-gradient(120% 100% at 0% 0%, rgba(103,232,249,0.06) 0%, transparent 55%);
            opacity: 0;
            transition: opacity 500ms ease;
            pointer-events: none;
          }
          .ds-doc:hover { background: #01070E; }
          .ds-doc:hover::before { opacity: 1; }
          .ds-doc:hover .ds-doc__title { color: #FFFFFF; }
          .ds-doc__cta { transition: gap 400ms ease; }
          .ds-doc:hover .ds-doc__cta { gap: 14px; }
        `}</style>
      </section>

      {/* INQUIRY—closes the page in black, with the faintest deep-navy glow at the very bottom */}
      <section id="inquiry" className="relative py-28 deep-space-close">
        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-start z-10">
          <div className="md:col-span-5">
            <div className="font-mono-label mb-5" style={{ color: "#6E839B" }}>Speak with us</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight" style={{ color: "#E8EEF6" }}>
              A private conversation.
            </h2>
            <p className="mt-6 leading-[1.8]" style={{ color: "#8FA3B8" }}>
              Whether you are comparing ownership versus charter, evaluating
              a management agreement, or rethinking your mission
              profile—send a note and a principal at Steck will respond directly.
            </p>
          </div>
          <div className="md:col-span-7">
            <InquiryForm variant="compact" />
          </div>
        </div>

        <style>{`
          .deep-space-close {
            background: #000204;
          }
          /* Faint hairline divider—barely-there, brushed across the top */
          .deep-space-close::before {
            content: "";
            position: absolute;
            inset: 0 0 auto 0;
            height: 1px;
            background: linear-gradient(90deg,
              transparent 0%,
              rgba(180,210,255,0.10) 25%,
              rgba(103,232,249,0.18) 50%,
              rgba(180,210,255,0.10) 75%,
              transparent 100%);
            pointer-events: none;
          }
          /* Subtle deep-navy glow only at the very bottom—page ends in black */
          .deep-space-close::after {
            content: "";
            position: absolute;
            inset: auto 0 0 0;
            height: 380px;
            background:
              radial-gradient(60% 70% at 50% 100%, rgba(34,81,255,0.08) 0%, rgba(34,81,255,0.02) 45%, transparent 75%);
            pointer-events: none;
            z-index: 0;
          }
        `}</style>
      </section>
    </Layout>
  );
};

export default Home;
